import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import EXIF from 'exif-js';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription } from './components/ui/alert';
import { Separator } from './components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { 
  User, 
  MapPin, 
  Package, 
  Camera, 
  FileSignature, 
  CheckCircle, 
  AlertCircle, 
  Globe,
  Truck,
  Building2,
  Phone,
  Mail,
  Clock,
  Users,
  RefreshCw,
  Cloud,
  Circle,
  Search,
  Plus,
  Download,
  Key,
  Shield,
  Edit,
  Save,
  Trash2,
  X,
  FileText,
  Upload,
  Database,
  History,
  ChevronDown,
  LayoutDashboard
} from 'lucide-react';
import BarcodeScanner from './components/BarcodeScanner';
import DriverMap from './components/DriverMap';
import OrderManagement from './components/OrderManagement';
import './App.css';

// Auto-detect environment and use appropriate backend URL - PRODUCTION DOMAIN HARDCODED
const getBackendURL = () => {
  // Priority 1: Use environment variable if set
  if (process.env.REACT_APP_BACKEND_URL) {
    console.log('🔧 Using env REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  const hostname = window.location.hostname;
  console.log('🔧 Auto-detecting backend for hostname:', hostname);
  
  // Priority 2: Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('🏗️ DEVELOPMENT: Using localhost:8001');
    return 'http://localhost:8001';
  }
  
  // Priority 3: All other environments (production, preview, custom domains)
  // Use same origin - the ingress/routing will handle /api prefix
  console.log('🏗️ PRODUCTION/PREVIEW: Using same origin:', window.location.origin);
  return window.location.origin;
};

const API_BASE_URL = getBackendURL();

// Debug logging
console.log('🔧 Environment:', {
  API_BASE_URL,
  hostname: window.location.hostname,
  origin: window.location.origin
});

function App() {
  // Core state
  const [currentStep, setCurrentStep] = useState('welcome'); // welcome, auth, hotel-select, recipients, review, signature, success, hotel-auth, facility-dashboard, confirm-delivery
  const [language, setLanguage] = useState(() => {
    // Initialize language from localStorage or default to 'en'
    const savedLanguage = localStorage.getItem('sunlight-laundry-language');
    return savedLanguage || 'en';
  });
  const [user, setUser] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [currentDelivery, setCurrentDelivery] = useState(null);
  const [userType, setUserType] = useState(null); // 'driver' or 'hotel_staff'
  
  // Data state
  const [hotels, setHotels] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [deliveryLines, setDeliveryLines] = useState([]);
  const [pickupOrders, setPickupOrders] = useState([]);
  const [selectedOrderForPickup, setSelectedOrderForPickup] = useState(null);
  const [activeTab, setActiveTab] = useState('pickup'); // pickup, message, place-order, gallery
  
  // Facility photo gallery state
  const [facilityDeliveryPhotos, setFacilityDeliveryPhotos] = useState([]);
  const [selectedGalleryPhoto, setSelectedGalleryPhoto] = useState(null);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [expandedGalleryDates, setExpandedGalleryDates] = useState(new Set());
  const [expandedPickupSection, setExpandedPickupSection] = useState(true); // Pickup orders section expanded by default
  
  // CleanCloud integration state removed
  
  // Manager dashboard state
  const [managerDashboardData, setManagerDashboardData] = useState(null);
  const [selectedFacilityDetails, setSelectedFacilityDetails] = useState(null);
  const [managerActiveTab, setManagerActiveTab] = useState('overview'); // overview, orders, tasks, customers, facilities, gallery
  
  // Customer data state
  const [customerImportStatus, setCustomerImportStatus] = useState(null);
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  
  // CSV upload state
  const [csvUploadFile, setCsvUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [updateExistingCustomers, setUpdateExistingCustomers] = useState(false);
  const [showDeleteDatabaseModal, setShowDeleteDatabaseModal] = useState(false);
  const [deleteDatabasePassword, setDeleteDatabasePassword] = useState('');
  const [deleteDatabaseError, setDeleteDatabaseError] = useState('');

  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [selectedFacilityCustomers, setSelectedFacilityCustomers] = useState([]);
  const [showCustomerManagement, setShowCustomerManagement] = useState(false);
  
  // Enhanced driver delivery stops state
  const [driverDeliveryStops, setDriverDeliveryStops] = useState([]);
  const [selectedDeliveryStop, setSelectedDeliveryStop] = useState(null);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [deliveryGpsCoordinates, setDeliveryGpsCoordinates] = useState(null);
  const [taskCompletionPhotos, setTaskCompletionPhotos] = useState([]);
  const [showTaskPhotoModal, setShowTaskPhotoModal] = useState(false);
  const [selectedTaskForCompletion, setSelectedTaskForCompletion] = useState(null);
  const [selectedTaskItem, setSelectedTaskItem] = useState(null);
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [showSubmissionReview, setShowSubmissionReview] = useState(false);
  const [photoGalleryData, setPhotoGalleryData] = useState(null);
  const [submissionStats, setSubmissionStats] = useState(null);
  // Location tracking state - REMOVED FOR PERFORMANCE
  // const [driverLocations, setDriverLocations] = useState([]);
  // const [selectedDriver, setSelectedDriver] = useState(null);
  // const [routeHistory, setRouteHistory] = useState([]);
  // const [gpsEnabled, setGPSEnabled] = useState(true);
  // const [lastAutoRefresh, setLastAutoRefresh] = useState(null);
  // const [autoRefreshActive, setAutoRefreshActive] = useState(true);
  // const [currentLocation, setCurrentLocation] = useState(null);
  // const [locationWatchId, setLocationWatchId] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState(new Set());

  // Persist language setting to localStorage
  useEffect(() => {
    localStorage.setItem('sunlight-laundry-language', language);
  }, [language]);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [pinManagementData, setPinManagementData] = useState(null);
  // PIN Edit Modal State
  const [showPinEditModalState, setShowPinEditModalState] = useState(false);
  
  const setShowPinEditModal = (value) => {
    setShowPinEditModalState(value);
  };
  
  const showPinEditModal = showPinEditModalState;
  const [editingPin, setEditingPin] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        document.querySelectorAll('.dropdown-menu').forEach(menu => {
          menu.classList.add('hidden');
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  // Delete Confirmation Modal State
  const [showDeleteConfirmModalState, setShowDeleteConfirmModalState] = useState(false);
  
  const setShowDeleteConfirmModal = (value) => {
    setShowDeleteConfirmModalState(value);
  };
  
  const showDeleteConfirmModal = showDeleteConfirmModalState;
  const [userToDelete, setUserToDelete] = useState(null);
  const [newUserData, setNewUserData] = useState({ name: '', pin: '', phone: '', role: 'driver' });
  const [showTaskEditModal, setShowTaskEditModal] = useState(false);
  const [showTaskDeleteModal, setShowTaskDeleteModal] = useState(false);
  const [showOrderDeleteModal, setShowOrderDeleteModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  // Removed allOrders state - now using OrderManagement component with pagination
  const [showOrderEditModal, setShowOrderEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderManagementRefreshKey, setOrderManagementRefreshKey] = useState(0);
  const [showDriverTaskEditModal, setShowDriverTaskEditModal] = useState(false);
  const [showPendingOrdersModal, setShowPendingOrdersModal] = useState(false);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [showDriverTaskDeleteModal, setShowDriverTaskDeleteModal] = useState(false);
  const [editingDriverTask, setEditingDriverTask] = useState(null);
  const [driverTaskToDelete, setDriverTaskToDelete] = useState(null);
  const [showAddFacilityModal, setShowAddFacilityModal] = useState(false);
  const [showDeleteFacilityModal, setShowDeleteFacilityModal] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState(null);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [newFacilityData, setNewFacilityData] = useState({ 
    name: '', 
    address: '', 
    pin: '', 
    phone: '', 
    contact_person: '' 
  });
  
  // Facility search state
  const [facilitySearchQuery, setFacilitySearchQuery] = useState('');
  const [filteredPickupOrders, setFilteredPickupOrders] = useState([]);
  const [pin, setPin] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [bagCounts, setBagCounts] = useState({ regular: 0, bulk: 0, specialty: 0 });
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [pickupImages, setPickupImages] = useState([]);
  const [receiverName, setReceiverName] = useState('');
  const [receiverTitle, setReceiverTitle] = useState('');
  
  // New order form state
  const [newOrderForm, setNewOrderForm] = useState({
    firstName: '',
    lastName: '',
    roomNumber: '',
    bedNumber: '',
    orderType: 'clothes', // clothes or linen
    specialInstructions: ''
  });
  
  // Message form state
  const [messageForm, setMessageForm] = useState({
    subject: '',
    message: '',
    priority: 'normal' // normal, urgent
  });
  
  // Task management state
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [managerTasks, setManagerTasks] = useState([]);
  const [driverCreatedTasks, setDriverCreatedTasks] = useState([]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskCreation, setShowTaskCreation] = useState(false);
  const [showTemplateCreation, setShowTemplateCreation] = useState(false);
  const [showCustomTaskCreation, setShowCustomTaskCreation] = useState(false);
  const [showDriverSelection, setShowDriverSelection] = useState(false);
  const [selectedTemplateForAssignment, setSelectedTemplateForAssignment] = useState(null);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  // Utility function to create local date string without timezone issues
  const getLocalDateString = (date = new Date()) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Utility function to format date string for display
  const formatDateForDisplay = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return localDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Utility function to format completion timestamp
  const formatCompletionTime = (completedAt) => {
    if (!completedAt) return null;
    try {
      const date = new Date(completedAt);
      return date.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting completion time:', error);
      return null;
    }
  };

  const [selectedAssignmentDate, setSelectedAssignmentDate] = useState(() => getLocalDateString()); // Default to today in local timezone
  const [newTemplate, setNewTemplate] = useState({
    title: '',
    description: '',
    day_of_week: 'monday',
    locations: [{
      hotel_id: '',
      hotel_name: '',
      task_type: 'both', // pickup, dropoff, both
      scheduled_time: '', // optional HH:MM format
      notes: '',
      checklist_items: [{ text: '', requiresPhoto: false }] // Each location now has its own checklist with photo requirements
    }],
    assignable_to_roles: ['driver'],
    priority: 'normal',
    estimated_duration: 30,
    required_photos: 1
  });
  const [newCustomTask, setNewCustomTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    room_number: '',
    target_date: new Date().toISOString().split('T')[0],
    priority: 'normal',
    task_type: 'delivery',
    checklist_items: [{ text: '', requiresPhoto: false }],
    required_photos: 1,
    hotel_id: '' // Optional hotel assignment
  });
  const [availableStaff, setAvailableStaff] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  
  // PWA state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);

  // Facility pickup success state
  const [showPickupSuccessModal, setShowPickupSuccessModal] = useState(false);
  const [pickupSuccessDetails, setPickupSuccessDetails] = useState(null);

  // Signature canvas
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState('');

  // Add New Customer modal states
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    facility_id: '',
    notes: ''
  });

  // Translations
  const t = {
    en: {
      welcome: "Welcome to Sunlight Laundry",
      laundryStaff: "Laundry Staff",
      facilityStaff: "Facility Staff",
      language: "Language",
      laundryAuth: "Laundry Staff Authentication",
      facilityAuth: "Facility Staff Authentication",
      enterPin: "Enter PIN",
      authenticate: "Authenticate",
      selectHotel: "Select Hotel",
      addRecipients: "Add Recipients & Bags",
      selectRecipient: "Select Recipient",
      bagCounts: "Bag Counts",
      regular: "Regular",
      bulk: "Bulk",
      specialty: "Specialty",
      notes: "Notes",
      addPhotos: "Add Photos",
      addRecipient: "Add Recipient",
      reviewDelivery: "Review Delivery",
      totalBags: "Total Bags",
      signature: "Signature",
      receiverName: "Receiver Name",
      receiverTitle: "Receiver Title",
      signHere: "Sign here to confirm delivery",
      clearSignature: "Clear",
      completeDelivery: "Complete Delivery",
      deliveryComplete: "Delivery Complete!",
      startNew: "Start New Delivery",
      hotelDashboard: "Facility Dashboard",
      pickup: "Pick Up",
      message: "Message",
      placeOrder: "Place Order",
      readyForPickup: "Ready for Participant Pick Up",
      confirmPickup: "Confirm Pickup",
      guestPickup: "Participant Pickup",
      guestName: "Participant Name",
      firstName: "First Name",
      lastName: "Last Name",
      roomNumber: "Room Number",
      bedNumber: "Bed Number",
      orderType: "Order Type",
      clothes: "Clothes",
      linen: "Linen",
      location: "Location",
      items: "Items",
      orderDate: "Order Date",
      readyDate: "Ready Date",
      specialInstructions: "Special Instructions",
      attachImage: "Attach Images",
      confirmHandout: "Confirm Handout",
      sendMessage: "Send Message",
      messageSubject: "Subject",
      messageContent: "Message",
      priority: "Priority",
      normal: "Normal",
      urgent: "Urgent",
      submitOrder: "Submit Order",
      messageSent: "Message Sent Successfully",
      orderPlaced: "Order Placed Successfully",
      orderConfirmation: "Order Confirmation",
      orderCreated: "Order Created Successfully!",
      orderNumber: "Order Number",
      participantDetails: "Participant Details",
      orderDetails: "Order Details",
      thankYou: "Thank you for placing your order",
      closeConfirmation: "Close",
      back: "Back",
      next: "Next",
      loading: "Loading...",
      error: "Error",
      success: "Success",
      // CleanCloud integration
      syncCleancloud: "Sync CleanCloud",
      syncHotels: "Sync Hotels",
      cleancloudOrders: "CleanCloud Orders",
      cleancloudHotels: "CleanCloud Hotels",
      lastSync: "Last Sync",
      syncStatus: "Sync Status",
      fromCleancloud: "From CleanCloud",
      syncSuccessful: "CleanCloud sync completed successfully!",
      syncFailed: "CleanCloud sync failed. Please try again.",
      hotelSyncSuccessful: "CleanCloud hotels synced successfully!",
      hotelSyncFailed: "CleanCloud hotel sync failed. Please try again.",
      neverSynced: "Never synced",
      // Manager dashboard
      manager: "Manager",
      managerDashboard: "Operations Dashboard",
      systemOverview: "System Overview",
      facilityStats: "Facility Statistics",
      recentActivity: "Recent Activity",
      pendingOrders: "Pending Orders",
      completedPickups: "Completed Pickups",
      deliveriesMade: "Deliveries Made",
      viewDetails: "View Details",
      totalFacilities: "Total Facilities",
      allFacilities: "All Facilities",
      driverDashboard: "Driver Dashboard",
      operationsDashboard: "Operations Dashboard",
      sunlightHospitalityLaundry: "Sunlight Hospitality Laundry",
      welcomeBack: "Welcome back",
      facilityStaff: "Facility Staff",
      communityFacilities: "Community Facilities",
      selectForTraditionalDelivery: "Select for traditional delivery",
      customerSearch: "Customer Search",
      typeCustomerName: "Type customer name...",
      search: "Search",
      myTasks: "My Tasks",
      locations: "locations",
      totalOrdersGrouped: "total orders grouped by location",
      noDeliveryStops: "No delivery stops yet",
      searchCustomersToCreate: "Search customers to create stops",
      refreshTasks: "Refresh Tasks",
      dropOff: "Drop Off",
      managerTask: "Manager Task",
      location: "Location",
      unknownLocation: "Unknown Location", 
      amount: "Amount",
      orders: "orders",
      pending: "pending",
      individualOrders: "Individual Orders:",
      room: "Room",
      delivery: "delivery",
      ready: "Ready",
      delivered: "Delivered",
      taskChecklist: "Task Checklist:",
      photo: "photo",
      photos: "photos",
      progress: "Progress",
      completed: "completed",
      // Manager forms
      createCustomTask: "Create Custom Task",
      createTaskDescription: "Create a custom task without using a template",
      taskTitle: "Task Title",
      taskType: "Task Type",
      description: "Description",
      assignToStaff: "Assign to Staff",
      priority: "Priority",
      assignToHotel: "Assign to Hotel/Facility",
      optional: "(Optional)",
      photosRequired: "Photos Required",
      placeholderTaskTitle: "e.g., Emergency Pickup - Room 301",
      placeholderDescription: "Describe what needs to be done",
      placeholderChooseStaff: "Choose staff member...",
      placeholderSelectHotel: "Select hotel/facility (optional)...",
      hotelAssociationNote: "If selected, this task will be associated with the specific hotel/facility",
      // Task types
      delivery: "Delivery",
      pickup: "Pickup",
      qualityCheck: "Quality Check",
      maintenance: "Maintenance",
      emergency: "Emergency",
      specialRequest: "Special Request",
      // Priority levels
      normal: "Normal",
      high: "High",
      urgent: "Urgent",
      // Photos options
      noPhotosRequired: "No Photos Required",
      onePhotoRequired: "1 Photo Required",
      twoPhotosRequired: "2 Photos Required", 
      threePhotosRequired: "3 Photos Required",
      // Checklist and form elements
      checklistItems: "Checklist Items",
      taskSteps: "Task Steps (Checklist)",
      taskStepsDescription: "Add specific steps that the driver must complete",
      addStep: "Add Step",
      // Template form
      templateName: "Template Name",
      templateDescription: "Template Description", 
      dayOfWeek: "Day of Week",
      monday: "Monday",
      tuesday: "Tuesday", 
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      selectHotel: "Select Hotel",
      loadingHotels: "Loading hotels...",
      taskChecklistForHotel: "Task Checklist for this Hotel",
      addTaskForHotel: "Add Task for this Hotel",
      noChecklistItems: "No checklist items yet.",
      clickAddStep: "Click \"Add Step\" to create checklist items.",
      createTaskTemplate: "Create Task Template",
      createTaskTemplateDescription: "Create a new task template for weekly scheduling",
      hotelLocations: "Hotel Locations & Schedule",
      hotel: "Hotel",
      taskType: "Task Type",
      both: "Both",
      scheduledTime: "Scheduled Time (Optional)",
      notes: "Notes (Optional)",
      specialInstructions: "Special instructions...",
      remove: "Remove",
      completedAt: "Completed at:",
      proofImages: "Proof Images",
      requirePhoto: "Require Photo?",
      yes: "Yes",
      no: "No",
      photoRequired: "Photo required"
    },
    es: {
      welcome: "Bienvenido a Sunlight Laundry",
      laundryStaff: "Personal de Lavandería",
      facilityStaff: "Personal del Hotel",
      language: "Idioma",
      laundryAuth: "Autenticación del Personal de Lavandería",
      facilityAuth: "Autenticación del Personal del Hotel",
      enterPin: "Ingrese PIN",
      authenticate: "Autenticar",
      selectHotel: "Seleccionar Hotel",
      addRecipients: "Agregar Destinatarios y Bolsas",
      selectRecipient: "Seleccionar Destinatario",
      bagCounts: "Cantidad de Bolsas",
      regular: "Regular",
      bulk: "Volumen",
      specialty: "Especialidad",
      notes: "Notas",
      addPhotos: "Agregar Fotos",
      addRecipient: "Agregar Destinatario",
      reviewDelivery: "Revisar Entrega",
      totalBags: "Total de Bolsas",
      signature: "Firma",
      receiverName: "Nombre del Receptor",
      receiverTitle: "Título del Receptor",
      signHere: "Firme aquí para confirmar la entrega",
      clearSignature: "Limpiar",
      completeDelivery: "Completar Entrega",
      deliveryComplete: "¡Entrega Completada!",
      startNew: "Iniciar Nueva Entrega",
      hotelDashboard: "Panel de Instalación",
      pickup: "Recoger",
      message: "Mensaje",
      placeOrder: "Hacer Pedido",
      readyForPickup: "Listo para Recogida de Participantes",
      confirmPickup: "Confirmar Recogida",
      guestPickup: "Recogida del Participante",
      guestName: "Nombre del Participante",
      firstName: "Nombre",
      lastName: "Apellido",
      roomNumber: "Número de Habitación",
      bedNumber: "Número de Cama",
      orderType: "Tipo de Pedido",
      clothes: "Ropa",
      linen: "Ropa de Cama",
      location: "Ubicación",
      items: "Artículos",
      orderDate: "Fecha del Pedido",
      readyDate: "Fecha de Preparación",
      specialInstructions: "Instrucciones Especiales",
      attachImage: "Adjuntar Imágenes",
      confirmHandout: "Confirmar Entrega",
      sendMessage: "Enviar Mensaje",
      messageSubject: "Asunto",
      messageContent: "Mensaje",
      priority: "Prioridad",
      normal: "Normal",
      urgent: "Urgente",
      submitOrder: "Enviar Pedido",
      messageSent: "Mensaje Enviado Exitosamente",
      orderPlaced: "Pedido Realizado Exitosamente",
      orderConfirmation: "Confirmación de Pedido",
      orderCreated: "¡Pedido Creado Exitosamente!",
      orderNumber: "Número de Pedido",
      participantDetails: "Detalles del Participante",
      orderDetails: "Detalles del Pedido",
      thankYou: "Gracias por realizar su pedido",
      closeConfirmation: "Cerrar",
      back: "Atrás",
      next: "Siguiente",
      loading: "Cargando...",
      error: "Error",
      success: "Éxito",
      // CleanCloud integration
      syncCleancloud: "Sincronizar CleanCloud",
      syncHotels: "Sincronizar Hoteles",
      cleancloudOrders: "Pedidos CleanCloud",
      cleancloudHotels: "Hoteles CleanCloud",
      lastSync: "Última Sincronización",
      syncStatus: "Estado de Sincronización",
      fromCleancloud: "De CleanCloud",
      syncSuccessful: "¡Sincronización de CleanCloud completada exitosamente!",
      syncFailed: "La sincronización de CleanCloud falló. Intente de nuevo.",
      hotelSyncSuccessful: "¡Hoteles de CleanCloud sincronizados exitosamente!",
      hotelSyncFailed: "La sincronización de hoteles de CleanCloud falló. Intente de nuevo.",
      neverSynced: "Nunca sincronizado",
      // Manager dashboard
      manager: "Gerente",
      managerDashboard: "Panel de Operaciones",
      systemOverview: "Resumen del Sistema",
      facilityStats: "Estadísticas de Instalación",
      recentActivity: "Actividad Reciente",
      pendingOrders: "Pedidos Pendientes",
      completedPickups: "Recogidas Completadas",
      deliveriesMade: "Entregas Realizadas",
      viewDetails: "Ver Detalles",
      totalFacilities: "Total de Instalaciones",
      allFacilities: "Todas las Instalaciones",
      driverDashboard: "Panel del Conductor",
      operationsDashboard: "Panel de Operaciones", 
      sunlightHospitalityLaundry: "Lavandería Sunlight Hospitality",
      welcomeBack: "Bienvenido de nuevo",
      facilityStaff: "Personal del Hotel",
      communityFacilities: "Instalaciones Comunitarias",
      selectForTraditionalDelivery: "Seleccionar para entrega tradicional",
      customerSearch: "Búsqueda de Cliente",
      typeCustomerName: "Escriba el nombre del cliente...",
      search: "Buscar",
      myTasks: "Mis Tareas",
      locations: "ubicaciones",
      totalOrdersGrouped: "total de órdenes agrupadas por ubicación",
      noDeliveryStops: "Aún no hay paradas de entrega",
      searchCustomersToCreate: "Buscar clientes para crear paradas",
      refreshTasks: "Actualizar Tareas",
      dropOff: "Entrega",
      managerTask: "Tarea del Gerente",
      location: "Ubicación",
      unknownLocation: "Ubicación Desconocida",
      amount: "Cantidad",
      orders: "órdenes", 
      pending: "pendiente",
      individualOrders: "Órdenes Individuales:",
      room: "Habitación",
      delivery: "entrega",
      ready: "Listo",
      delivered: "Entregado",
      taskChecklist: "Lista de Tareas:",
      photo: "foto",
      photos: "fotos", 
      progress: "Progreso",
      completed: "completado",
      // Manager forms
      createCustomTask: "Crear Tarea Personalizada",
      createTaskDescription: "Crear una tarea personalizada sin usar una plantilla",
      taskTitle: "Título de la Tarea",
      taskType: "Tipo de Tarea",
      description: "Descripción",
      assignToStaff: "Asignar al Personal",
      priority: "Prioridad",
      assignToHotel: "Asignar a Hotel/Instalación",
      optional: "(Opcional)",
      photosRequired: "Fotos Requeridas",
      placeholderTaskTitle: "ej., Recogida de Emergencia - Habitación 301",
      placeholderDescription: "Describe lo que necesita ser hecho",
      placeholderChooseStaff: "Elegir miembro del personal...",
      placeholderSelectHotel: "Seleccionar hotel/instalación (opcional)...",
      hotelAssociationNote: "Si se selecciona, esta tarea será asociada con el hotel/instalación específica",
      // Task types
      delivery: "Entrega",
      pickup: "Recogida",
      qualityCheck: "Control de Calidad",
      maintenance: "Mantenimiento",
      emergency: "Emergencia",
      specialRequest: "Solicitud Especial",
      // Priority levels
      normal: "Normal",
      high: "Alta",
      urgent: "Urgente",
      // Photos options
      noPhotosRequired: "No se Requieren Fotos",
      onePhotoRequired: "1 Foto Requerida",
      twoPhotosRequired: "2 Fotos Requeridas",
      threePhotosRequired: "3 Fotos Requeridas",
      // Checklist and form elements
      checklistItems: "Lista de Verificación",
      taskSteps: "Pasos de la Tarea (Lista de Verificación)",
      taskStepsDescription: "Agregar pasos específicos que el conductor debe completar",
      addStep: "Agregar Paso",
      // Template form
      templateName: "Nombre de Plantilla",
      templateDescription: "Descripción de Plantilla",
      dayOfWeek: "Día de la Semana",
      monday: "Lunes",
      tuesday: "Martes",
      wednesday: "Miércoles", 
      thursday: "Jueves",
      friday: "Viernes",
      selectHotel: "Seleccionar Hotel",
      loadingHotels: "Cargando hoteles...",
      taskChecklistForHotel: "Lista de Tareas para este Hotel",
      addTaskForHotel: "Agregar Tarea para este Hotel",
      noChecklistItems: "Aún no hay elementos de lista de verificación.",
      clickAddStep: "Haga clic en \"Agregar Paso\" para crear elementos de la lista de verificación.",
      createTaskTemplate: "Crear Plantilla de Tarea",
      createTaskTemplateDescription: "Crear una nueva plantilla de tarea para programación semanal",
      hotelLocations: "Ubicaciones de Hotel y Horario",
      hotel: "Hotel",
      taskType: "Tipo de Tarea",
      both: "Ambos",
      scheduledTime: "Hora Programada (Opcional)",
      notes: "Notas (Opcional)", 
      specialInstructions: "Instrucciones especiales...",
      remove: "Eliminar",
      completedAt: "Completado en:",
      proofImages: "Imágenes de Prueba",
      requirePhoto: "¿Requiere Foto?",
      yes: "Sí",
      no: "No",
      photoRequired: "Foto requerida"
    }
  };

  const tr = (key) => t[language][key] || key;

  // API calls with retry logic for resilience
  const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 25000,  // 25s timeout (under server's 30s limit)
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Retry configuration
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1000;
  const RETRYABLE_STATUS_CODES = [408, 500, 502, 503, 504, 520, 521, 522, 523, 524];

  // Add response interceptor for automatic retry
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      
      // Initialize retry count
      if (!config._retryCount) {
        config._retryCount = 0;
      }

      // Check if we should retry
      const shouldRetry = (
        config._retryCount < MAX_RETRIES &&
        (
          !error.response ||  // Network error
          RETRYABLE_STATUS_CODES.includes(error.response?.status)
        )
      );

      if (shouldRetry) {
        config._retryCount += 1;
        
        // Calculate delay with exponential backoff
        const delay = RETRY_DELAY_MS * Math.pow(2, config._retryCount - 1);
        
        console.log(
          `⚠️ Request failed (${error.response?.status || 'network error'}), ` +
          `retrying in ${delay}ms (attempt ${config._retryCount}/${MAX_RETRIES})...`
        );
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry the request
        return api(config);
      }

      // If we've exhausted retries or it's a non-retryable error
      if (config._retryCount >= MAX_RETRIES) {
        console.error(`❌ Request failed after ${MAX_RETRIES} retries`);
      }
      
      return Promise.reject(error);
    }
  );

  // Debug function for browser console
  React.useEffect(() => {
    window.debugAddRecipient = () => {
      addRecipientToDelivery();
    };
  }, [selectedRecipient, bagCounts, currentDelivery]);

  const authenticate = async (isHotelStaff = false, isManager = false) => {
    setLoading(true);
    setError('');
    
    try {
      if (isManager) {
        // Use manager authentication endpoint
        const response = await api.post('/api/auth/manager', { pin });
        
        if (response.data.success) {
          setUser(response.data.user);
          setUserType('manager');
          
          // Go to manager dashboard immediately
          setCurrentStep('manager-dashboard');
          setPin('');
          
          // Load dashboard data in background (non-blocking)
          loadManagerDashboard();
          loadAvailableDrivers();
          loadHotels();
        } else {
          setError(response.data.message);
        }
      } else if (isHotelStaff) {
        // Use hotel authentication endpoint
        const response = await api.post('/api/auth/hotel', { pin });
        
        if (response.data.success) {
          // Set hotel directly from authentication response
          const hotel = response.data.hotel;
          setSelectedHotel(hotel);
          setUserType('hotel_staff');
          
          // Go directly to facility dashboard (don't wait for data)
          setCurrentStep('facility-dashboard');
          setPin('');
          
          // Load data in background (non-blocking)
          loadPickupOrders(hotel.id);
          loadFacilityDeliveryPhotos(hotel.id);
        } else {
          setError(response.data.message);
        }
      } else {
        // Laundry Staff authentication - try manager PIN first, then driver PIN
        // This allows the Laundry Staff button to access both dashboards
        
        // First, try manager authentication
        try {
          const managerResponse = await api.post('/api/auth/manager', { pin });
          
          if (managerResponse.data.success) {
            setUser(managerResponse.data.user);
            setUserType('manager');
            setCurrentStep('manager-dashboard');
            setPin('');
            
            // Load dashboard data in background
            loadManagerDashboard();
            loadAvailableDrivers();
            loadHotels();
            return; // Exit early - manager auth successful
          }
        } catch (managerErr) {
          // Manager auth failed, continue to try driver auth
          console.log('Not a manager PIN, trying driver auth...');
        }
        
        // If not a manager, try driver authentication
        const response = await api.post('/api/auth', { pin });
        
        if (response.data.success) {
          setUser(response.data.user);
          
          if (response.data.user.role === 'driver') {
            setUserType('driver');
            setCurrentStep('hotel-select');
            
            // Load data in background (non-blocking)
            loadHotels();
            loadCustomerImportStatus();
            loadDriverDeliveryStops();
          } else {
            setError('Only drivers can start deliveries');
          }
        } else {
          setError(response.data.message);
        }
      }
    } catch (err) {
      if (isManager) {
        setError('Manager authentication failed');
      } else if (isHotelStaff) {
        setError('Hotel authentication failed');
      } else {
        setError('Invalid PIN. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadHotels = async () => {
    try {
      console.log('🏨 Loading hotels...');
      const response = await api.get('/api/hotels');
      console.log('🏨 Hotels API response:', response.data);
      setHotels(response.data.hotels || []);
      console.log('🏨 Hotels set to state:', response.data.hotels);
    } catch (err) {
      console.error('🏨 Failed to load hotels:', err);
      setError('Failed to load hotels');
    }
  };

  const loadRecipients = async (hotelId) => {
    try {
      const response = await api.get(`/api/hotels/${hotelId}/recipients`);
      setRecipients(response.data.recipients);
    } catch (err) {
      setError('Failed to load recipients');
    }
  };

  const loadPickupOrders = async (hotelId) => {
    try {
      const response = await api.get(`/api/pickup-orders?hotel_id=${hotelId}`);
      const orders = response.data.orders || [];
      setPickupOrders(orders);
      setFilteredPickupOrders(orders); // Initialize filtered orders
    } catch (err) {
      setError('Failed to load pickup orders');
      console.warn('⚠️ Keeping existing pickup orders due to API failure');
      // Don't clear existing data - preserve what users have
    }
  };

  // Load facility delivery photos for gallery
  const loadFacilityDeliveryPhotos = async (facilityId) => {
    try {
      setGalleryLoading(true);
      const response = await api.get(`/api/facility/${facilityId}/delivery-photos?limit=100`);
      if (response.data.success) {
        setFacilityDeliveryPhotos(response.data.photos || []);
      }
    } catch (err) {
      console.error('Failed to load delivery photos:', err);
      setFacilityDeliveryPhotos([]);
    } finally {
      setGalleryLoading(false);
    }
  };

  // Filter pickup orders based on search query
  const filterPickupOrders = (query) => {
    if (!query.trim()) {
      setFilteredPickupOrders(pickupOrders);
      return;
    }

    const filtered = pickupOrders.filter(order => {
      const searchText = query.toLowerCase();
      return (
        order.guest_name?.toLowerCase().includes(searchText) ||
        order.room_number?.toLowerCase().includes(searchText) ||
        order.customer_name?.toLowerCase().includes(searchText) ||
        order.items_description?.toLowerCase().includes(searchText) ||
        order.special_instructions?.toLowerCase().includes(searchText)
      );
    });

    setFilteredPickupOrders(filtered);
  };

  const handleFacilitySearch = (value) => {
    setFacilitySearchQuery(value);
    filterPickupOrders(value);
  };

  const confirmGuestPickup = async (pickupOrderId, signature, guestName, staffName) => {
    setLoading(true);
    try {
      const response = await api.post('/api/pickup-orders/confirm-pickup', {
        pickup_order_id: pickupOrderId,
        signature: signature,
        picked_up_by: guestName,
        staff_member: staffName,
        images: pickupImages
      });

      if (response.data.success) {
        // Store success details for modal
        setPickupSuccessDetails({
          customerName: guestName,
          staffName: staffName,
          facilityName: selectedHotel?.name,
          orderDetails: selectedOrderForPickup,
          timestamp: new Date().toLocaleString()
        });
        
        // Show success modal
        setShowPickupSuccessModal(true);
        
        // Refresh pickup orders
        if (selectedHotel) {
          await loadPickupOrders(selectedHotel.id);
        }
        setCurrentStep('facility-dashboard');
        setSelectedOrderForPickup(null);
        setSignatureData('');
        setReceiverName('');
        setReceiverTitle('');
        setPickupImages([]);
      } else {
        setError('Failed to confirm pickup: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      setError('Failed to confirm pickup');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageForm.subject || !messageForm.message) {
      setError('Please fill in subject and message');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/messages', {
        hotel_id: selectedHotel.id,
        sender_id: user.id,
        subject: messageForm.subject,
        message: messageForm.message,
        priority: messageForm.priority
      });

      if (response.data.success) {
        setError(''); // Clear any errors
        setMessageForm({ subject: '', message: '', priority: 'normal' });
        // Show success message temporarily
        setTimeout(() => setError(''), 3000);
        setError(tr('messageSent'));
      }
    } catch (err) {
      setError('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const placeNewOrder = async () => {
    if (!newOrderForm.firstName || !newOrderForm.lastName || !newOrderForm.roomNumber) {
      setError('Please fill in first name, last name, and room number');
      return;
    }

    setLoading(true);
    try {
      // First, try to consolidate with existing tasks if user is a driver
      if (userType === 'driver') {
        const consolidationResponse = await api.post('/api/tasks/consolidate-by-location', {
          hotel_id: selectedHotel.id,
          guest_name: `${newOrderForm.firstName} ${newOrderForm.lastName}`,
          room_number: newOrderForm.roomNumber,
          items_description: `${newOrderForm.orderType}: ${newOrderForm.specialInstructions || 'No special instructions'}`,
          driver_id: user.id,
          target_date: getLocalDateString() // Use today's date for consolidation
        });

        if (consolidationResponse.data.success) {
          setError(''); // Clear any errors
          // Set success message based on consolidation result
          const message = consolidationResponse.data.action === 'consolidated' 
            ? `Delivery stop added to existing task: ${consolidationResponse.data.message}`
            : `New consolidated task created: ${consolidationResponse.data.message}`;
          
          setSuccess(message);
          setTimeout(() => setSuccess(''), 5000);
          
          // Reset form
          setNewOrderForm({
            firstName: '',
            lastName: '',
            roomNumber: '',
            bedNumber: '',
            orderType: 'laundry',
            specialInstructions: ''
          });
          
          // Refresh driver tasks to show updated consolidation
          if (userType === 'driver') {
            await loadDriverDeliveryStops();
          }
          
          return;
        }
      }
      
      // Fallback to original order creation for non-drivers or if consolidation fails
      const response = await api.post('/api/orders', {
        hotel_id: selectedHotel.id,
        first_name: newOrderForm.firstName,
        last_name: newOrderForm.lastName,
        room_number: newOrderForm.roomNumber,
        bed_number: newOrderForm.bedNumber,
        order_type: newOrderForm.orderType,
        special_instructions: newOrderForm.specialInstructions,
        requested_by: user?.id || 'facility_staff'
      });

      if (response.data.success) {
        setError(''); // Clear any errors
        // Set confirmed order details for the confirmation dialog
        setConfirmedOrder({
          orderId: response.data.order_id,
          firstName: newOrderForm.firstName,
          lastName: newOrderForm.lastName,
          roomNumber: newOrderForm.roomNumber,
          bedNumber: newOrderForm.bedNumber,
          orderType: newOrderForm.orderType,
          specialInstructions: newOrderForm.specialInstructions,
          hotelName: selectedHotel.name,
          staffName: user?.name || selectedHotel?.name || 'Facility Staff',
          createdAt: new Date().toLocaleString()
        });
        
        // Show confirmation dialog
        setShowOrderConfirmation(true);
        
        // Reset form
        setNewOrderForm({
          firstName: '',
          lastName: '',
          roomNumber: '',
          bedNumber: '',
          orderType: 'clothes',
          specialInstructions: ''
        });
      }
    } catch (err) {
      setError('Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const startDelivery = async (hotel) => {
    setLoading(true);
    try {
      const response = await api.post('/api/deliveries', {
        hotel_id: hotel.id,
        driver_id: user.id,
        delivery_note: ''
      });
      
      if (response.data.success) {
        setCurrentDelivery({ id: response.data.delivery_id });
        setSelectedHotel(hotel);
        await loadRecipients(hotel.id);
        setCurrentStep('recipients');
      }
    } catch (err) {
      setError('Failed to start delivery');
    } finally {
      setLoading(false);
    }
  };

  const addRecipientToDelivery = async () => {
    console.log('addRecipientToDelivery function called');
    console.log('selectedRecipient:', selectedRecipient);
    console.log('bagCounts:', bagCounts);
    console.log('currentDelivery:', currentDelivery);
    
    if (!selectedRecipient) {
      setError('Please select a recipient');
      console.log('Error: No recipient selected');
      return;
    }
    
    const totalBags = bagCounts.regular + bagCounts.bulk + bagCounts.specialty;
    if (totalBags === 0) {
      setError('Please specify at least one bag');
      console.log('Error: No bags specified');
      return;
    }

    if (!currentDelivery || !currentDelivery.id) {
      setError('No active delivery found');
      console.log('Error: No current delivery');
      return;
    }

    setLoading(true);
    setError(''); // Clear any previous errors
    console.log('Making API call to add recipient...');
    
    try {
      const response = await api.post('/api/deliveries/add-recipient', {
        delivery_id: currentDelivery.id,
        recipient_id: selectedRecipient.id,
        bag_counts: bagCounts,
        bag_ids: [],
        photos: photos,
        notes: notes
      });

      console.log('API response:', response.data);

      if (response.data.success) {
        const newLine = {
          id: Date.now().toString(),
          recipient_name: selectedRecipient.full_name,
          room_number: selectedRecipient.room_number,
          department: selectedRecipient.department,
          bag_counts: { ...bagCounts },
          notes: notes,
          photos: [...photos]
        };
        
        console.log('Adding new delivery line:', newLine);
        setDeliveryLines([...deliveryLines, newLine]);
        
        // Reset form
        setSelectedRecipient(null);
        setBagCounts({ regular: 0, bulk: 0, specialty: 0 });
        setNotes('');
        setPhotos([]);
        setError('');
        
        console.log('Recipient added successfully!');
      } else {
        setError(response.data.message || 'Failed to add recipient');
        console.log('API error:', response.data.message);
      }
    } catch (err) {
      console.error('Error adding recipient:', err);
      setError('Failed to add recipient: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePickupImageUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPickupImages(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePickupImage = (index) => {
    setPickupImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleTaskPhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setTaskCompletionPhotos(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeTaskPhoto = (index) => {
    setTaskCompletionPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removePhoto = (index) => {
    setDeliveryPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const completeTaskWithPhotos = async () => {
    if (selectedTaskForCompletion && selectedTaskItem) {
      await completeChecklistItem(selectedTaskForCompletion.id, selectedTaskItem.id, taskCompletionPhotos);
      setShowTaskPhotoModal(false);
      setTaskCompletionPhotos([]);
      setSelectedTaskForCompletion(null);
      setSelectedTaskItem(null);
    }
  };

  const handleTaskItemClick = (task, item, requiredPhotos = 0) => {
    console.log('handleTaskItemClick called:', {
      taskId: task.id,
      itemId: item.id,
      itemText: item.text,
      requiredPhotos,
      itemCompleted: item.completed,
      taskData: task
    });
    
    if (item.completed) {
      console.log('Item already completed, skipping');
      return;
    }
    
    if (requiredPhotos > 0) {
      console.log('Opening photo modal for', requiredPhotos, 'photos');
      setSelectedTaskForCompletion(task);
      setSelectedTaskItem(item);
      setShowTaskPhotoModal(true);
    } else {
      console.log('No photos required, completing directly');
      completeChecklistItem(task.id, item.id, []);
    }
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDeliveryPhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    
    // Helper function to convert EXIF GPS coordinates to decimal degrees
    const convertDMSToDD = (degrees, minutes, seconds, direction) => {
      let dd = degrees + minutes / 60 + seconds / 3600;
      if (direction === 'S' || direction === 'W') {
        dd = dd * -1;
      }
      return dd;
    };
    
    files.forEach(file => {
      // Extract EXIF GPS data from image
      EXIF.getData(file, function() {
        const lat = EXIF.getTag(this, 'GPSLatitude');
        const latRef = EXIF.getTag(this, 'GPSLatitudeRef');
        const lon = EXIF.getTag(this, 'GPSLongitude');
        const lonRef = EXIF.getTag(this, 'GPSLongitudeRef');
        
        if (lat && lon && latRef && lonRef) {
          const latitude = convertDMSToDD(lat[0], lat[1], lat[2], latRef);
          const longitude = convertDMSToDD(lon[0], lon[1], lon[2], lonRef);
          
          console.log('GPS extracted from image EXIF:', latitude, longitude);
          
          setDeliveryGpsCoordinates({
            latitude: latitude,
            longitude: longitude,
            source: 'exif',
            timestamp: new Date().toISOString()
          });
        } else {
          console.log('No GPS data in image EXIF, trying device location...');
          // Fallback to device GPS if no EXIF data
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                setDeliveryGpsCoordinates({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  accuracy: position.coords.accuracy,
                  source: 'device',
                  timestamp: new Date().toISOString()
                });
              },
              (error) => {
                console.log('GPS not available:', error.message);
              },
              { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
            );
          }
        }
      });
      
      // Read the file as base64 for preview/upload
      const reader = new FileReader();
      reader.onload = (e) => {
        setDeliveryPhotos(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Signature canvas functions
  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.beginPath();
    ctx.moveTo(
      (e.clientX || e.touches[0].clientX) - rect.left,
      (e.clientY || e.touches[0].clientY) - rect.top
    );
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1f2937';
    
    ctx.lineTo(
      (e.clientX || e.touches[0].clientX) - rect.left,
      (e.clientY || e.touches[0].clientY) - rect.top
    );
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = signatureCanvasRef.current;
      setSignatureData(canvas.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData('');
  };

  const completeDelivery = async () => {
    if (!signatureData || !receiverName) {
      setError('Signature and receiver name are required');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/deliveries/complete', {
        delivery_id: currentDelivery.id,
        signature: signatureData,
        receiver_name: receiverName,
        receiver_title: receiverTitle
      });

      if (response.data.success) {
        setCurrentStep('success');
      }
    } catch (err) {
      setError('Failed to complete delivery');
    } finally {
      setLoading(false);
    }
  };

  // Print order receipt function - optimized for thermal printers (80mm)
  const printOrderReceipt = () => {
    if (!confirmedOrder) return;
    
    const printWindow = window.open('', '_blank');
    const receiptContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt #${confirmedOrder.orderId.slice(-8).toUpperCase()}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Courier New', Courier, monospace; 
            padding: 5px;
            width: 80mm;
            max-width: 80mm;
            margin: 0 auto;
            font-size: 12px;
            line-height: 1.3;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .receipt-header { 
            text-align: center; 
            border-bottom: 1px dashed #000; 
            padding-bottom: 8px; 
            margin-bottom: 8px; 
          }
          .receipt-header h1 { 
            font-size: 14px; 
            font-weight: bold; 
            margin-bottom: 2px; 
          }
          .receipt-header p { 
            font-size: 10px; 
          }
          .order-number { 
            text-align: center; 
            font-size: 20px; 
            font-weight: bold; 
            padding: 8px 0; 
            border-bottom: 1px dashed #000;
            margin-bottom: 8px;
            letter-spacing: 2px;
          }
          .section { 
            margin-bottom: 8px; 
            padding-bottom: 6px;
            border-bottom: 1px dashed #ccc;
          }
          .section-title { 
            font-weight: bold; 
            font-size: 11px;
            margin-bottom: 4px; 
            text-transform: uppercase;
          }
          .detail-row { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 2px;
            font-size: 11px;
          }
          .detail-label { }
          .detail-value { font-weight: bold; text-align: right; max-width: 50%; }
          .instructions { 
            padding: 6px; 
            margin-top: 6px;
            border: 1px dashed #000;
            font-size: 10px;
          }
          .instructions-title { font-weight: bold; margin-bottom: 3px; font-size: 11px; }
          .footer { 
            text-align: center; 
            margin-top: 10px; 
            padding-top: 8px;
            border-top: 1px dashed #000;
            font-size: 10px;
          }
          .footer p { margin-bottom: 3px; }
          .close-btn {
            display: block;
            width: 100%;
            padding: 12px;
            margin-top: 15px;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: bold;
            cursor: pointer;
          }
          .close-btn:hover {
            background: #2563eb;
          }
          .cut-line {
            text-align: center;
            margin-top: 15px;
            border-top: 1px dashed #000;
            padding-top: 5px;
            font-size: 9px;
            color: #666;
          }
          @media print {
            body { 
              width: 80mm;
              max-width: 80mm;
              padding: 0;
              margin: 0;
            }
            @page { 
              size: 80mm auto;
              margin: 2mm;
            }
            .close-btn { display: none !important; }
            .no-print { display: none !important; }
          }
          @media screen {
            body {
              background: #f0f0f0;
              padding: 20px;
            }
            .receipt-container {
              background: white;
              padding: 10px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="receipt-header">
            <h1>SUNLIGHT HOSPITALITY</h1>
            <p>Laundry Services</p>
            <p>${confirmedOrder.hotelName}</p>
          </div>
          
          <div class="order-number">
            #${confirmedOrder.orderId.slice(-8).toUpperCase()}
          </div>
          
          <div class="section">
            <div class="section-title">Participant</div>
            <div class="detail-row">
              <span class="detail-label">Name:</span>
              <span class="detail-value">${confirmedOrder.firstName} ${confirmedOrder.lastName}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Room:</span>
              <span class="detail-value">${confirmedOrder.roomNumber || 'N/A'}</span>
            </div>
            ${confirmedOrder.bedNumber ? `
            <div class="detail-row">
              <span class="detail-label">Bed:</span>
              <span class="detail-value">${confirmedOrder.bedNumber}</span>
            </div>
            ` : ''}
          </div>
          
          <div class="section">
            <div class="section-title">Order Info</div>
            <div class="detail-row">
              <span class="detail-label">Type:</span>
              <span class="detail-value">${confirmedOrder.orderType === 'clothes' ? 'Clothes' : 'Linen'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Staff:</span>
              <span class="detail-value">${confirmedOrder.staffName}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Date:</span>
              <span class="detail-value">${new Date().toLocaleDateString()}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Time:</span>
              <span class="detail-value">${new Date().toLocaleTimeString()}</span>
            </div>
          </div>
          
          ${confirmedOrder.specialInstructions ? `
          <div class="instructions">
            <div class="instructions-title">Notes:</div>
            <p>${confirmedOrder.specialInstructions}</p>
          </div>
          ` : ''}
          
          <div class="footer">
            <p><strong>Thank you!</strong></p>
            <p>Keep this receipt for pickup</p>
          </div>
          
          <div class="cut-line">- - - - - - - - - - - - - - -</div>
        </div>
        
        <button class="close-btn no-print" onclick="window.close()">
          ✕ Close Receipt
        </button>
        
        <script>
          window.onload = function() {
            window.print();
          };
          window.onafterprint = function() {
            window.close();
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(receiptContent);
    printWindow.document.close();
  };

  const resetApp = () => {
    setCurrentStep('welcome');
    setUser(null);
    setUserType(null);
    setSelectedHotel(null);
    setCurrentDelivery(null);
    setDeliveryLines([]);
    setPickupOrders([]);
    setSelectedOrderForPickup(null);
    setActiveTab('pickup');
    setPin('');
    setError('');
    setSignatureData('');
    setReceiverName('');
    setReceiverTitle('');
    setPhotos([]);
    setPickupImages([]);
    setShowOrderConfirmation(false);
    setConfirmedOrder(null);
    setNewOrderForm({
      firstName: '',
      lastName: '',
      roomNumber: '',
      bedNumber: '',
      orderType: 'clothes',
      specialInstructions: ''
    });
    setMessageForm({
      subject: '',
      message: '',
      priority: 'normal'
    });
  };

  // CleanCloud functions removed

  // Task Management Functions
  const loadTaskTemplates = async (hotelId = null) => {
    try {
      // For managers, load all templates. For facility staff, load templates for specific hotel
      const endpoint = user?.role === 'manager' || !hotelId ? 
        '/api/task-templates/all' : 
        `/api/task-templates/${hotelId}`;
        
      console.log('📋 Loading task templates from:', endpoint);
      const response = await api.get(endpoint);
      if (response.data.success) {
        console.log('📋 Loaded templates:', response.data.templates?.length || 0);
        setTaskTemplates(response.data.templates || []);
      }
    } catch (err) {
      console.error('Failed to load task templates:', err);
      console.warn('⚠️ Keeping existing task templates due to API failure');
      // Don't clear existing templates - preserve what managers have
    }
  };

  const loadManagerTasks = async () => {
    try {
      console.log('Loading manager tasks, user state:', user);
      
      // Don't make API call if user ID is undefined
      if (!user?.id) {
        console.log('User ID not available, skipping manager tasks load');
        console.log('Current user state:', user);
        setManagerTasks([]);
        return;
      }
      
      console.log('Making API call to:', `/api/manager/${user.id}/tasks`);
      const response = await api.get(`/api/manager/${user.id}/tasks`);
      if (response.data.success) {
        console.log('Manager tasks loaded successfully:', response.data.tasks?.length || 0);
        setManagerTasks(response.data.tasks || []);
      } else {
        console.log('Manager tasks API returned success=false:', response.data);
        setManagerTasks([]);
      }
    } catch (err) {
      console.error('Failed to load manager tasks:', err);
      console.warn('⚠️ Keeping existing manager tasks due to API failure');
      // Don't clear existing tasks - preserve what managers have
    }
  };

  const loadAvailableStaff = async () => {
    try {
      const response = await api.get('/api/users');
      if (response.data.success) {
        const staff = response.data.users.filter(user => 
          user.role === 'driver' || user.role === 'staff' || user.role === 'housekeeper'
        );
        setAvailableStaff(staff);
      }
    } catch (err) {
      console.error('Failed to load available staff:', err);
      setAvailableStaff([
        { id: "6a16086a-fb28-4873-9fab-869de2912a0c", name: "John Driver", role: "driver" }
      ]);
    }
  };

  const loadAllDriverTasks = async () => {
    try {
      console.log('Loading all driver tasks...');
      const response = await api.get('/api/manager/all-driver-tasks');
      console.log('Driver tasks API response:', response.data);
      
      if (response.data.success) {
        console.log('Driver tasks loaded successfully:', response.data.tasks?.length || 0);
        setDriverCreatedTasks(response.data.tasks || []);
      } else {
        console.log('Driver tasks API returned success=false:', response.data);
        setDriverCreatedTasks([]);
      }
    } catch (err) {
      console.error('Failed to load driver tasks:', err);
      console.error('Error details:', err.response?.data || err.message);
      console.warn('⚠️ Keeping existing driver tasks due to API failure');
      // Don't clear existing tasks - preserve what drivers have
    }
  };

  const loadPipelineStats = async () => {
    try {
      const response = await api.get('/api/manager/delivery-pipeline-stats');
      if (response.data.success) {
        setPipelineStats(response.data.pipeline_stats);
      }
    } catch (err) {
      console.error('Failed to load pipeline stats:', err);
      setPipelineStats(null);
    }
  };

  const createCustomTask = async () => {
    try {
      setLoading(true);
      
      if (!newCustomTask.title || !newCustomTask.assigned_to) {
        setError('Please fill in title and assign to staff member');
        return;
      }
      
      // Handle both string and object formats for checklist items
      // Convert objects back to strings for backend compatibility
      const cleanedChecklistItems = newCustomTask.checklist_items
        .filter(item => {
          if (typeof item === 'string') return item.trim() !== '';
          if (typeof item === 'object') return item.text && item.text.trim() !== '';
          return false;
        })
        .map(item => {
          if (typeof item === 'string') {
            return item;
          }
          // For objects, encode photo requirement in the text
          const text = item.text;
          const requiresPhoto = item.requiresPhoto;
          // Add a special marker for photo-required items that backend can detect
          return requiresPhoto ? `${text} [PHOTO_REQUIRED]` : text;
        });
      
      const taskData = {
        ...newCustomTask,
        checklist_items: cleanedChecklistItems,
        created_by: user?.id,
        hotel_id: newCustomTask.hotel_id || managerDashboardData?.facilities?.[0]?.id,
        target_date: new Date().toISOString().split('T')[0],
        room_number: "Manager Created"
      };
      
      const response = await api.post('/api/tasks/custom', taskData);
      if (response.data.success) {
        setSuccess('One-time task created successfully!');
        setShowCustomTaskCreation(false);
        setNewCustomTask({
          title: '',
          description: '',
          assigned_to: '',
          room_number: '',
          target_date: new Date().toISOString().split('T')[0],
          priority: 'normal',
          task_type: 'delivery',
          checklist_items: [{ text: '', requiresPhoto: false }],
          required_photos: 1,
          hotel_id: '' // Reset hotel assignment
        });
        await loadManagerTasks();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Failed to create custom task:', err);
      setError('Failed to create custom task');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const completeChecklistItem = async (taskId, itemId, proofPhotos = []) => {
    try {
      setLoading(true);
      const response = await api.post(`/api/tasks/${taskId}/checklist/${itemId}/complete`, {
        completed_by: user?.id,
        proof_photos: proofPhotos,
        location: selectedHotel?.name || tr('unknownLocation')
      });
      
      if (response.data.success) {
        setSuccess('Checklist item completed!');
        // Reload tasks to reflect changes
        if (userType === 'driver' || userType === 'staff') {
          await loadDriverDeliveryStops();
        } else if (userType === 'manager') {
          await loadManagerTasks();
        }
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Failed to complete checklist item:', err);
      setError('Failed to complete checklist item');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const submitTaskForApproval = async (taskId) => {
    try {
      setLoading(true);
      const response = await api.post(`/api/tasks/${taskId}/submit`, {
        staff_id: user?.id,
        photos: []
      });
      
      if (response.data.success) {
        setSuccess('Task submitted for manager approval!');
        await loadDriverDeliveryStops();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Failed to submit task:', err);
      setError('Failed to submit task');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const createTaskFromTemplate = async (templateId, assignmentData) => {
    try {
      setLoading(true);
      const taskData = {
        template_id: templateId,
        ...assignmentData,
        created_by: user?.id
      };
      
      const response = await api.post('/api/tasks/from-template', taskData);
      if (response.data.success) {
        setSuccess('Task assigned successfully!');
        await loadManagerTasks();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Failed to create task from template:', err);
      setError('Failed to assign task');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const createTaskTemplate = async () => {
    try {
      setLoading(true);
      
      // Transform location-specific checklists into a global checklist for backend compatibility
      const allChecklistItems = [];
      newTemplate.locations.forEach((location, index) => {
        const hotelName = hotels.find(h => h.id === location.hotel_id)?.name || 'Unknown Hotel';
        if (location.checklist_items && location.checklist_items.length > 0) {
          location.checklist_items.forEach((item, itemIndex) => {
            if (item.text && item.text.trim()) {
              // Backend expects just strings, not objects
              allChecklistItems.push(`${hotelName}: ${item.text.trim()}`);
            }
          });
        }
      });
      
      // Prepare template data for backend (without location-specific checklists)
      const backendLocations = newTemplate.locations.map(loc => ({
        hotel_id: loc.hotel_id,
        hotel_name: loc.hotel_name,
        task_type: loc.task_type,
        scheduled_time: loc.scheduled_time,
        notes: loc.notes
        // Remove checklist_items as backend doesn't support location-specific checklists
      }));
      
      const templateData = {
        ...newTemplate,
        locations: backendLocations,
        checklist_items: allChecklistItems, // Combined checklist items with hotel prefixes
        created_by: user?.id,
        hotel_id: selectedHotel?.id || user?.facility_id
      };
      
      console.log('📝 Saving template with data:', templateData);
      console.log('📝 Combined checklist items:', allChecklistItems);
      
      // Check if we're editing an existing template
      const isEditing = newTemplate.id;
      const url = isEditing ? `/api/task-templates/${newTemplate.id}` : '/api/task-templates';
      const method = isEditing ? 'put' : 'post';
      
      const response = await api[method](url, templateData);
      if (response.data.success) {
        setSuccess(isEditing ? 'Template updated successfully!' : 'Template created successfully!');
        // Reset form
        setNewTemplate({
          title: '',
          description: '',
          day_of_week: 'monday',
          locations: [{
            hotel_id: '',
            hotel_name: '',
            task_type: 'both',
            scheduled_time: '',
            notes: '',
            checklist_items: [{ text: '', requiresPhoto: false }]
          }],
          assignable_to_roles: ['driver'],
          priority: 'normal',
          estimated_duration: 30,
          required_photos: 1
        });
        setShowTemplateCreation(false);
        // Reload templates
        await loadTaskTemplates(selectedHotel?.id || user?.facility_id);
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Failed to save template:', err);
      setError('Failed to save template');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Location-specific checklist management functions
  const addLocationChecklistItem = (locationIndex) => {
    setNewTemplate(prev => {
      const updatedLocations = [...prev.locations];
      updatedLocations[locationIndex] = {
        ...updatedLocations[locationIndex],
        checklist_items: [...updatedLocations[locationIndex].checklist_items, { text: '', requiresPhoto: false }]
      };
      return {
        ...prev,
        locations: updatedLocations
      };
    });
  };

  const removeLocationChecklistItem = (locationIndex, itemIndex) => {
    setNewTemplate(prev => {
      const updatedLocations = [...prev.locations];
      updatedLocations[locationIndex] = {
        ...updatedLocations[locationIndex],
        checklist_items: updatedLocations[locationIndex].checklist_items.filter((_, i) => i !== itemIndex)
      };
      return {
        ...prev,
        locations: updatedLocations
      };
    });
  };

  const updateLocationChecklistItem = (locationIndex, itemIndex, field, value) => {
    setNewTemplate(prev => {
      const updatedLocations = [...prev.locations];
      updatedLocations[locationIndex] = {
        ...updatedLocations[locationIndex],
        checklist_items: updatedLocations[locationIndex].checklist_items.map((item, i) => 
          i === itemIndex ? { ...item, [field]: value } : item
        )
      };
      return {
        ...prev,
        locations: updatedLocations
      };
    });
  };

  // Location management functions for day-based templates
  const addTemplateLocation = () => {
    setNewTemplate(prev => ({
      ...prev,
      locations: [...prev.locations, {
        hotel_id: '',
        hotel_name: '',
        task_type: 'both',
        scheduled_time: '',
        notes: '',
        checklist_items: [{ text: '', requiresPhoto: false }] // Each new location gets its own checklist
      }]
    }));
  };

  const removeTemplateLocation = (index) => {
    setNewTemplate(prev => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index)
    }));
  };

  const updateTemplateLocation = (index, field, value) => {
    setNewTemplate(prev => ({
      ...prev,
      locations: prev.locations.map((location, i) => 
        i === index ? { ...location, [field]: value } : location
      )
    }));
  };

  // Manager dashboard functions
  // Optimized dashboard loading - load only essential data initially
  const loadManagerDashboard = async () => {
    try {
      console.log('Loading manager dashboard...');
      const response = await api.get('/api/manager/dashboard');
      
      if (response.data.success) {
        setManagerDashboardData(response.data);
        console.log('Manager dashboard loaded successfully');
        
        // Load task templates for manager
        await loadTaskTemplates();
        console.log('Task templates loaded for manager');
      }
    } catch (err) {
      console.error('Failed to load manager dashboard:', err);
      // Don't set error to avoid blocking UI, just log it
    }
  };

  const loadFacilityDetails = async (facilityId) => {
    try {
      const response = await api.get(`/api/manager/facility/${facilityId}/details`);
      setSelectedFacilityDetails(response.data);
    } catch (err) {
      console.error('Failed to load facility details:', err);
      setError('Failed to load facility details');
    }
  };

  const loadAvailableDrivers = async () => {
    try {
      const response = await api.get('/api/manager/users');
      if (response.data.success) {
        // Filter for drivers only
        const drivers = response.data.users.filter(user => user.role === 'driver');
        setAvailableDrivers(drivers);
      }
    } catch (err) {
      console.error('Failed to load drivers:', err);
      setError('Failed to load drivers');
    }
  };

  const assignTemplateToDriver = async (driverId) => {
    try {
      if (!selectedTemplateForAssignment) return;
      
      setLoading(true);
      
      // Handle bulk assignment (all templates for a day)
      if (selectedTemplateForAssignment.id === 'bulk') {
        const { templates, day } = selectedTemplateForAssignment;
        
        // Assign all templates for this day
        for (const template of templates) {
          await createTaskFromTemplate(template.id, {
            assigned_to: driverId,
            target_date: selectedAssignmentDate
          });
        }
        
        const [targetYear, targetMonth, targetDay] = selectedAssignmentDate.split('-');
        const localDate = new Date(parseInt(targetYear), parseInt(targetMonth) - 1, parseInt(targetDay));
        const formattedDate = localDate.toLocaleDateString();
        setSuccess(`All ${day} templates assigned to driver for ${formattedDate}!`);
      } else {
        // Handle single template assignment
        await createTaskFromTemplate(selectedTemplateForAssignment.id, {
          assigned_to: driverId,
          target_date: selectedAssignmentDate
        });
        
        const [targetYear, targetMonth, targetDay] = selectedAssignmentDate.split('-');
        const localDate = new Date(parseInt(targetYear), parseInt(targetMonth) - 1, parseInt(targetDay));
        const formattedDate = localDate.toLocaleDateString();
        setSuccess(`Template assigned to driver for ${formattedDate}!`);
      }
      
      setShowDriverSelection(false);
      setSelectedTemplateForAssignment(null);
      setSelectedAssignmentDate(getLocalDateString()); // Reset to today
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to assign template:', err);
      setError('Failed to assign template');
      setTimeout(() => setError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Customer search with proper debounce using useRef
  const searchTimeoutRef = useRef(null);
  const lastSearchQueryRef = useRef('');
  
  // Memoized search function with AbortController to cancel in-flight requests
  const abortControllerRef = useRef(null);
  
  const searchCustomersDebounced = useCallback(async (query) => {
    const trimmedQuery = query.trim();
    
    // Skip if same query or too short
    if (trimmedQuery === lastSearchQueryRef.current || trimmedQuery.length < 2) {
      if (trimmedQuery.length < 2) {
        setCustomerSearchResults([]);
      }
      return;
    }
    
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    lastSearchQueryRef.current = trimmedQuery;
    
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/customers/search?query=${encodeURIComponent(trimmedQuery)}`, {
        signal: abortControllerRef.current.signal
      });
      if (response.data.success) {
        setCustomerSearchResults(response.data.customers || []);
      } else {
        setCustomerSearchResults([]);
      }
    } catch (err) {
      // Ignore aborted requests
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Failed to search customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const handleLiveSearch = useCallback((value) => {
    setCustomerSearchQuery(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Cancel any in-flight request immediately
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const trimmedValue = value.trim();
    if (trimmedValue.length >= 2) {
      // Set new timeout for debounced search (800ms for better debounce)
      searchTimeoutRef.current = setTimeout(() => {
        searchCustomersDebounced(trimmedValue);
      }, 800);
    } else {
      setCustomerSearchResults([]);
      lastSearchQueryRef.current = '';
    }
  }, [searchCustomersDebounced]);

  const createCustomerDeliveryStop = async (customer) => {
    try {
      setLoading(true);
      
      // Create a delivery order for this customer
      const response = await api.post(`/api/customers/${customer.id}/orders`, {
        room_number: customer.room_number || 'N/A',
        order_type: 'laundry',
        items_description: 'Delivery stop created from customer search',
        special_instructions: `Delivery stop created by ${user?.name || 'Driver'} for customer: ${customer.name}`,
        priority: 'normal',
        driver_id: user?.id,
        delivery_status: 'pending_pickup'
      });
      
      if (response.data.success) {
        setSuccess(`Delivery stop created for ${customer.name}! Order will appear at ${customer.facility_name || 'assigned facility'} for pickup.`);
        setTimeout(() => setSuccess(''), 5000);
        
        // Reset search bar and refresh data
        setCustomerSearchQuery(''); // Clear the search input
        setCustomerSearchResults([]); // Clear search results
        await loadDriverDeliveryStops();
      }
    } catch (err) {
      console.error('Failed to create customer delivery stop:', err);
      setError('Failed to create delivery stop: ' + (err.response?.data?.detail || err.message));
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Create new customer function
  const createCustomer = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validation
      if (!newCustomerData.name.trim()) {
        setError('Customer name is required');
        return;
      }
      
      const response = await api.post('/api/customers', {
        name: newCustomerData.name.trim(),
        phone: newCustomerData.phone.trim(),
        email: newCustomerData.email.trim(),
        address: newCustomerData.address.trim(),
        facility_id: newCustomerData.facility_id || null,
        notes: newCustomerData.notes.trim()
      });
      
      if (response.data.success) {
        setSuccess(`Customer "${newCustomerData.name}" added successfully!`);
        
        // Reset form and close modal
        setNewCustomerData({
          name: '',
          phone: '',
          email: '',
          address: '',
          facility_id: '',
          notes: ''
        });
        setShowAddCustomerModal(false);
        
        // Optionally search for the new customer to show it in results
        if (newCustomerData.name.length >= 2) {
          setTimeout(() => {
            searchCustomers(newCustomerData.name);
          }, 500);
        }
        
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Failed to create customer:', err);
      setError('Failed to create customer: ' + (err.response?.data?.detail || err.message));
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get hotel name from hotel_id
  const getHotelName = (hotelId) => {
    if (!hotelId) return tr('unknownLocation');
    const hotel = hotels.find(h => h.id === hotelId);
    return hotel ? hotel.name : tr('unknownLocation');
  };

  // Load driver's delivery stops AND assigned tasks (including consolidated tasks)
  const loadDriverDeliveryStops = async () => {
    try {
      if (!user?.id) {
        console.log('User not available yet, skipping delivery stops load');
        return;
      }
      
      console.log('Loading delivery stops and assigned tasks for driver:', user.id);
      
      // Load delivery stops (customer orders) - these are traditional individual orders
      const deliveryStopsResponse = await api.get(`/api/driver/${user.id}/delivery-stops`);
      let deliveryStops = [];
      if (deliveryStopsResponse.data.success) {
        deliveryStops = deliveryStopsResponse.data.delivery_stops || [];
      }
      
      // Load assigned tasks from manager (including consolidated tasks)
      const assignedTasksResponse = await api.get(`/api/staff/${user.id}/tasks`);
      let assignedTasks = [];
      if (assignedTasksResponse.data.success) {
        assignedTasks = assignedTasksResponse.data.tasks || [];
        
        // Process assigned tasks - handle both regular and consolidated tasks
        assignedTasks = assignedTasks.map(task => {
          // Check if this is a consolidated task with driver delivery stops
          if (task.driver_delivery_stops && task.driver_delivery_stops.length > 0) {
            // This is a consolidated task - create unified display
            return {
              ...task,
              customer_name: task.title,
              facility_name: getHotelName(task.hotel_id),
              delivery_status: task.status === 'completed' ? 'delivered' : 'pending_pickup',
              order_type: task.task_type,
              guest_name: task.title,
              hotel_id: task.hotel_id,
              room_number: task.room_number || 'Multiple Rooms',
              is_manager_task: true,
              is_consolidated_task: true,
              checklist_items: task.checklist_items || [],
              driver_delivery_stops: task.driver_delivery_stops || [],
              consolidation_info: {
                manager_requirements: task.checklist_items || [],
                driver_added_stops: task.driver_delivery_stops || [],
                total_stops: (task.driver_delivery_stops || []).length
              }
            };
          } else {
            // Regular manager task
            return {
              ...task,
              customer_name: task.title,
              facility_name: getHotelName(task.hotel_id),
              delivery_status: task.status === 'completed' ? 'delivered' : 'pending_pickup',
              order_type: task.task_type,
              guest_name: task.title,
              hotel_id: task.hotel_id,
              room_number: task.room_number || 'N/A',
              is_manager_task: true,
              is_consolidated_task: false,
              checklist_items: task.checklist_items || []
            };
          }
        });
      }
      
      // Combine delivery stops and assigned tasks
      const allTasks = [...deliveryStops, ...assignedTasks];
      setDriverDeliveryStops(allTasks);
      
      console.log('Loaded tasks:', {
        deliveryStops: deliveryStops.length,
        assignedTasks: assignedTasks.length,
        consolidatedTasks: assignedTasks.filter(t => t.is_consolidated_task).length,
        total: allTasks.length
      });
    } catch (err) {
      console.error('Failed to load driver tasks:', err);
      // Don't clear existing data on failure - keep what we have
      console.warn('⚠️ Keeping existing delivery stops due to API failure');
    }
  };

  // Group delivery stops by facility for task organization
  const groupDeliveryStopsByFacility = useCallback((deliveryStops) => {
    const grouped = {};
    
    // Filter out fully completed tasks (all orders delivered)
    const activeStops = deliveryStops.filter(stop => 
      stop.delivery_status !== 'delivered' && 
      !(stop.status === 'completed' && stop.is_manager_task)
    );
    
    activeStops.forEach(stop => {
      const facilityKey = stop.hotel_id || stop.facility_id || 'unknown';
      const facilityName = stop.facility_name || 'Unknown Facility';
      
      if (!grouped[facilityKey]) {
        grouped[facilityKey] = {
          taskId: facilityKey,
          taskName: tr('dropOff'),
          location: facilityName,
          facility_id: facilityKey,
          orders: [],
          totalOrders: 0,
          pendingCount: 0,
          deliveredCount: 0,
          hasManagerTasks: false
        };
      }
      
      grouped[facilityKey].orders.push(stop);
      grouped[facilityKey].totalOrders++;
      
      // Track if this group has manager-assigned tasks
      if (stop.is_manager_task) {
        grouped[facilityKey].hasManagerTasks = true;
      }
      
      if (stop.delivery_status === 'pending_pickup' || stop.delivery_status === 'ready') {
        grouped[facilityKey].pendingCount++;
      } else if (stop.delivery_status === 'delivered') {
        grouped[facilityKey].deliveredCount++;
      }
    });
    
    // Filter out groups where all orders are completed
    return Object.values(grouped).filter(group => group.pendingCount > 0 || group.orders.some(order => 
      order.is_manager_task && order.status !== 'completed'
    ));
  }, [tr]);

  // Memoize grouped delivery stops to prevent recalculation on every render
  const groupedDeliveryStops = useMemo(() => {
    return groupDeliveryStopsByFacility(driverDeliveryStops);
  }, [driverDeliveryStops, groupDeliveryStopsByFacility]);

  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Customer Data Management Functions
  const loadCustomerImportStatus = async () => {
    try {
      const response = await api.get('/api/customers/import-status');
      if (response.data.success) {
        setCustomerImportStatus(response.data.statistics);
      }
    } catch (err) {
      console.error('Failed to load customer import status:', err);
    }
  };

  const importCustomerDatabase = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/customers/import-database');
      if (response.data.success) {
        setSuccess('Customer database import initiated successfully!');
        // Reload import status after a delay
        setTimeout(() => {
          loadCustomerImportStatus();
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to import customer database:', err);
      setError('Failed to import customer database');
    } finally {
      setLoading(false);
    }
  };

  // CSV Upload Functions
  const uploadCustomerCSV = async () => {
    if (!csvUploadFile) {
      setError('Please select a CSV file to upload');
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('csv_file', csvUploadFile);
      formData.append('update_existing', updateExistingCustomers);

      const response = await api.post('/api/customers/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (response.data.success) {
        setSuccess(`CSV upload initiated: ${csvUploadFile.name}`);
        setCsvUploadFile(null);
        setShowUploadModal(false);
        setUploadProgress(0);
        
        // Reload import status and upload history
        setTimeout(() => {
          loadCustomerImportStatus();
          loadUploadHistory();
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to upload CSV:', err);
      setError('Failed to upload CSV file');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const loadUploadHistory = async () => {
    try {
      const response = await api.get('/api/customers/upload-history');
      if (response.data.success) {
        setUploadHistory(response.data.uploads);
      }
    } catch (err) {
      console.error('Failed to load upload history:', err);
    }
  };

  const cleanupDuplicateCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/customers/cleanup-duplicates');
      
      if (response.data.success) {
        setSuccess(`Cleanup completed! Removed ${response.data.duplicates_removed} duplicate customers, kept ${response.data.unique_customers_kept} unique customers.`);
        
        // Reload customer statistics
        setTimeout(() => {
          loadCustomerImportStatus();
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to cleanup duplicates:', err);
      setError('Failed to cleanup duplicate customers');
    } finally {
      setLoading(false);
    }
  };

  // Delete entire database (password protected)
  const deleteEntireDatabase = async () => {
    const correctPassword = 'Laundrydeletion!';
    
    if (deleteDatabasePassword !== correctPassword) {
      setDeleteDatabaseError('Incorrect password. Please try again.');
      return;
    }
    
    try {
      setLoading(true);
      setDeleteDatabaseError('');
      
      const response = await api.post('/api/database/delete-all', {
        password: deleteDatabasePassword
      });
      
      if (response.data.success) {
        setSuccess(`Successfully deleted all data from the database. Total records deleted: ${response.data.total_deleted}`);
        setShowDeleteDatabaseModal(false);
        setDeleteDatabasePassword('');
        
        // Reload everything
        setTimeout(() => {
          loadCustomerImportStatus();
          window.location.reload();
        }, 1500);
      } else {
        setDeleteDatabaseError(response.data.message || 'Failed to delete database');
      }
    } catch (err) {
      console.error('Failed to delete database:', err);
      setDeleteDatabaseError('Failed to delete database. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setCsvUploadFile(file);
        setError('');
      } else {
        setError('Please select a valid CSV file');
        setCsvUploadFile(null);
      }
    }
  };

  // Direct search function (for button clicks)
  const searchCustomers = useCallback(async (query) => {
    const trimmedQuery = query?.trim();
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setCustomerSearchResults([]);
      return;
    }
    
    // Use the debounced function
    await searchCustomersDebounced(trimmedQuery);
  }, [searchCustomersDebounced]);

  const createCustomerOrder = async (customerId, orderData) => {
    try {
      setLoading(true);
      const response = await api.post(`/api/customers/${customerId}/orders`, {
        ...orderData,
        created_by: user?.id,
        created_by_name: user?.name || 'Manager'
      });
      
      if (response.data.success) {
        const customerName = orderData.customer_name || 'customer';
        setSuccess(`Order created successfully for ${customerName}!`);
        setTimeout(() => setSuccess(''), 5000);
        
        // Reset search bar and clear results
        setCustomerSearchQuery('');
        setCustomerSearchResults([]);
        
        // Refresh dashboard data
        await loadManagerDashboard();
      } else {
        setError(response.data.message || 'Failed to create order');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      console.error('Failed to create customer order:', err);
      setError('Failed to create order: ' + (err.response?.data?.detail || err.message));
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomersByFacility = async (facilityId) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/customers/by-facility/${facilityId}`);
      if (response.data.success) {
        setSelectedFacilityCustomers(response.data.customers);
      }
    } catch (err) {
      console.error('Failed to load facility customers:', err);
      setError('Failed to load facility customers');
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh function
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const loadPhotoGalleryData = async () => {
    try {
      const response = await api.get('/api/tasks/photo-gallery');
      if (response.data.success) {
        setPhotoGalleryData(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load photo gallery data:', err);
      // Set empty data structure to prevent undefined errors
      setPhotoGalleryData({
        orders: [],
        totalPhotos: 0,
        totalOrders: 0
      });
    }
  };

  const loadSubmissionStats = async () => {
    try {
      const response = await api.get('/api/tasks/submission-stats');
      if (response.data.success) {
        setSubmissionStats(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load submission stats:', err);
      // Reset stats to zero for fresh start
      setSubmissionStats({
        ordersReadyForInspection: 0,
        ordersCompletedToday: 0,
        ordersInProgress: 0,
        totalOrders: 0,
        ordersWithPhotos: 0,
        totalDeliveryPhotos: 0,
        clientSignatures: 0
      });
    }
  };

  // GPS tracking functions - REMOVED FOR PERFORMANCE
  /*
  const loadDriverLocations = async () => {
    // Function removed to improve app performance
  };

  const loadRouteHistory = async (driverId) => {
    // Function removed to improve app performance
  };

  const updateDriverLocation = async (position) => {
    // Function removed to improve app performance  
  };

  const startLocationTracking = async () => {
    // Function removed to improve app performance
  };

  const initializeLocationTracking = () => {
    // Function removed to improve app performance
  };

  const stopLocationTracking = () => {
    // Function removed to improve app performance
  };
  */

  // PIN Management Functions with debouncing and retry logic
  const [isLoadingPinData, setIsLoadingPinData] = useState(false);
  const loadPinDataRef = useRef(null);

  const loadPinManagementData = async (retryCount = 0) => {
    // Debouncing: Cancel previous call if still pending
    if (loadPinDataRef.current) {
      clearTimeout(loadPinDataRef.current);
    }

    // Prevent multiple simultaneous calls
    if (isLoadingPinData) {
      console.log('🔍 PIN data load already in progress, skipping...');
      return;
    }

    setIsLoadingPinData(true);
    
    try {
      console.log('🔍 Loading PIN management data... (attempt:', retryCount + 1, ')');
      const response = await api.get('/api/manager/pins');
      console.log('🔍 PIN management response:', response.data);
      
      if (response.data.success) {
        console.log('🔍 Setting PIN management data:', response.data.pins);
        console.log('🔍 Managers count:', response.data.pins.managers?.length || 0);
        console.log('🔍 Drivers count:', response.data.pins.drivers?.length || 0);
        console.log('🔍 Facilities count:', response.data.pins.facilities?.length || 0);
        setPinManagementData(response.data.pins);
        console.log('🔍 PIN management data set successfully');
        setError(''); // Clear any previous errors
      } else {
        console.error('🔍 PIN management API returned success: false');
        setError('Failed to load PIN management data');
      }
    } catch (err) {
      console.error('Failed to load PIN management data:', err);
      
      // Retry logic with exponential backoff for 503 errors
      if (err.response?.status === 503 && retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s delays
        console.log(`🔄 Retrying PIN data load in ${delay}ms... (attempt ${retryCount + 2}/4)`);
        
        loadPinDataRef.current = setTimeout(() => {
          loadPinManagementData(retryCount + 1);
        }, delay);
      } else {
        setError('Failed to load PIN management data. Please try again.');
      }
    } finally {
      setIsLoadingPinData(false);
    }
  };

  const updatePin = async (pinType, id, newPin) => {
    try {
      const endpoint = `/api/manager/pins/${pinType}/${id}`;
      const response = await api.put(endpoint, { pin: newPin });
      
      if (response.data.success) {
        setSuccess(`${pinType.charAt(0).toUpperCase() + pinType.slice(1)} PIN updated successfully`);
        await loadPinManagementData(); // Reload data
        setShowPinEditModal(false);
        setEditingPin(null);
      } else {
        setError(response.data.message || 'Failed to update PIN');
      }
    } catch (err) {
      console.error('Failed to update PIN:', err);
      setError('Failed to update PIN');
    }
  };

  const openPinEditModal = (pinType, pinData) => {
    console.log('🔍 openPinEditModal called with:', pinType, pinData);
    console.log('🔍 Current showPinEditModal state:', showPinEditModal);
    setEditingPin({
      type: pinType,
      id: pinData.id,
      name: pinData.name,
      currentPin: pinData.pin,
      newPin: ''
    });
    console.log('🔍 Setting showPinEditModal to true');
    setShowPinEditModal(true);
    console.log('🔍 showPinEditModal should now be true');
    
    // Force a state check after a short delay
    setTimeout(() => {
      console.log('🔍 Delayed check - showPinEditModal:', showPinEditModal);
    }, 100);
  };

  const createUser = async () => {
    try {
      if (!newUserData.name || !newUserData.pin) {
        setError('Name and PIN are required');
        return;
      }

      const response = await api.post('/api/manager/users', newUserData);
      
      if (response.data.success) {
        setSuccess(`${newUserData.role.charAt(0).toUpperCase() + newUserData.role.slice(1)} created successfully`);
        await loadPinManagementData(); // Reload data
        setShowAddUserModal(false);
        setNewUserData({ name: '', pin: '', phone: '', role: 'driver' });
      } else {
        setError(response.data.message || 'Failed to create user');
      }
    } catch (err) {
      console.error('Failed to create user:', err);
      setError('Failed to create user');
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await api.delete(`/api/manager/users/${userId}`);
      
      if (response.data.success) {
        setSuccess('User deleted successfully');
        await loadPinManagementData(); // Reload data
        setShowDeleteConfirmModal(false);
        setUserToDelete(null);
      } else {
        setError(response.data.message || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError('Failed to delete user');
    }
  };

  const openDeleteConfirmModal = (user) => {
    console.log('🔵 openDeleteConfirmModal called with user:', user);
    console.log('🔵 Current showDeleteConfirmModal state:', showDeleteConfirmModal);
    setUserToDelete(user);
    console.log('🔵 Setting showDeleteConfirmModal to true');
    setShowDeleteConfirmModal(true);
    console.log('🔵 showDeleteConfirmModal should now be true');
    
    // Force a state check after a short delay  
    setTimeout(() => {
      console.log('🔵 Delayed check - showDeleteConfirmModal:', showDeleteConfirmModal);
    }, 100);
  };

  // Optimized order loading - removed heavy loadAllOrders function
  // Now using OrderManagement component with pagination

  const openTaskEditModal = (task) => {
    // Convert checklist_items to subtasks format for the editor
    let editableSubtasks = [];
    
    // First, add main checklist items as subtasks
    if (task.checklist_items && Array.isArray(task.checklist_items)) {
      editableSubtasks = task.checklist_items.map(item => ({
        text: typeof item === 'string' ? item : (item.text || item),
        completed: typeof item === 'object' ? (item.completed || false) : false,
        required_photos: typeof item === 'object' ? (item.required_photos || 0) : 0
      }));
    }
    
    // Then, add subtasks if they exist (flatten them for editing)
    if (task.subtasks && Array.isArray(task.subtasks)) {
      task.subtasks.forEach(subtask => {
        if (subtask.checklist_items && Array.isArray(subtask.checklist_items)) {
          const subtaskItems = subtask.checklist_items.map(item => ({
            text: `${subtask.title}: ${typeof item === 'string' ? item : (item.text || item)}`,
            completed: typeof item === 'object' ? (item.completed || false) : false,
            required_photos: typeof item === 'object' ? (item.required_photos || 0) : 0
          }));
          editableSubtasks.push(...subtaskItems);
        }
      });
    }
    
    setEditingTask({
      ...task,
      target_date: task.target_date ? task.target_date.split('T')[0] : '',
      subtasks: editableSubtasks
    });
    setShowTaskEditModal(true);
  };

  const updateTask = async () => {
    try {
      if (!editingTask.title || !editingTask.description) {
        setError('Title and description are required');
        return;
      }

      // Prepare the task data with properly formatted subtasks
      const taskUpdateData = {
        ...editingTask,
        // Convert subtasks back to checklist_items format
        checklist_items: editingTask.subtasks ? editingTask.subtasks.map(subtask => ({
          text: subtask.text,
          completed: subtask.completed || false,
          required_photos: subtask.required_photos || 0
        })) : []
      };

      const response = await api.put(`/api/manager/tasks/${editingTask.id}`, taskUpdateData);
      
      if (response.data.success) {
        setSuccess('Task updated successfully');
        await loadManagerTasks(); // Reload tasks
        await loadAllDriverTasks(); // Reload driver tasks
        setShowTaskEditModal(false);
        setEditingTask(null);
      } else {
        setError(response.data.message || 'Failed to update task');
      }
    } catch (err) {
      console.error('Failed to update task:', err);
      setError('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const response = await api.delete(`/api/manager/tasks/${taskId}`);
      
      if (response.data.success) {
        setSuccess('Task deleted successfully');
        await loadManagerTasks(); // Reload tasks
        await loadAllDriverTasks(); // Reload driver tasks
        setShowTaskDeleteModal(false);
        setTaskToDelete(null);
      } else {
        setError(response.data.message || 'Failed to delete task');
      }
    } catch (err) {
      console.error('Failed to delete task:', err);
      setError('Failed to delete task');
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      const response = await api.delete(`/api/manager/orders/${orderId}`);
      
      if (response.data.success) {
        setSuccess('Order deleted successfully');
        // Trigger OrderManagement component refresh
        setOrderManagementRefreshKey(prev => prev + 1);
        await loadPendingOrders(); // Reload pending orders for modal
        await loadManagerDashboard(); // Refresh dashboard statistics
        setShowOrderDeleteModal(false);
        setOrderToDelete(null);
      } else {
        setError(response.data.message || 'Failed to delete order');
      }
    } catch (err) {
      console.error('Failed to delete order:', err);
      setError('Failed to delete order');
    }
  };

  const openTaskDeleteModal = (task) => {
    setTaskToDelete(task);
    setShowTaskDeleteModal(true);
  };

  const openOrderDeleteModal = (order) => {
    setOrderToDelete(order);
    setShowOrderDeleteModal(true);
  };

  const openOrderEditModal = (order) => {
    setEditingOrder(order);
    setShowOrderEditModal(true);
  };

  const updateOrder = async () => {
    try {
      setLoading(true);

      const updateData = {
        customer_name: editingOrder.customer_name || editingOrder.guest_name,
        room_number: editingOrder.room_number,
        items_description: editingOrder.items_description,
        special_instructions: editingOrder.special_instructions,
        priority: editingOrder.priority,
        bag_count: editingOrder.bag_count,
        order_type: editingOrder.order_type,
        updated_by: user?.id
      };

      const response = await api.put(`/api/manager/orders/${editingOrder.id}`, updateData);
      
      if (response.data.success) {
        setSuccess('Order updated successfully');
        setShowOrderEditModal(false);
        setEditingOrder(null);
        await loadPendingOrders(); // Reload pending orders
        await loadManagerDashboard(); // Refresh dashboard statistics
      } else {
        setError(response.data.message || 'Failed to update order');
      }
    } catch (err) {
      console.error('Failed to update order:', err);
      setError('Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  // Driver Task Management Functions
  const openDriverTaskEditModal = (driverTask) => {
    setEditingDriverTask({...driverTask});
    setShowDriverTaskEditModal(true);
  };

  const updateDriverTask = async () => {
    try {
      if (!editingDriverTask.customer_name && !editingDriverTask.guest_name) {
        setError('Customer name is required');
        return;
      }

      const response = await api.put(`/api/manager/driver-task/${editingDriverTask.id}`, editingDriverTask);
      
      if (response.data.success) {
        setSuccess('Driver task updated successfully');
        await loadAllDriverTasks(); // Reload driver tasks
        setShowDriverTaskEditModal(false);
        setEditingDriverTask(null);
      } else {
        setError(response.data.message || 'Failed to update driver task');
      }
    } catch (err) {
      console.error('Failed to update driver task:', err);
      setError('Failed to update driver task');
    }
  };

  const deleteDriverTask = async (taskId) => {
    try {
      const deletionData = {
        deleted_by: user?.id,
        reason: "Deleted by manager from dashboard"
      };
      
      const response = await api.delete(`/api/manager/driver-task/${taskId}`, { 
        data: deletionData 
      });
      
      if (response.data.success) {
        setSuccess('Driver task deleted successfully');
        await loadAllDriverTasks(); // Reload driver tasks
        setShowDriverTaskDeleteModal(false);
        setDriverTaskToDelete(null);
      } else {
        setError(response.data.message || 'Failed to delete driver task');
      }
    } catch (err) {
      console.error('Failed to delete driver task:', err);
      console.error('Error response:', err.response?.data);
      setError('Failed to delete driver task');
    }
  };

  const openDriverTaskDeleteModal = (driverTask) => {
    setDriverTaskToDelete(driverTask);
    setShowDriverTaskDeleteModal(true);
  };

  // Optimized pending orders loading with pagination
  const loadPendingOrders = async () => {
    try {
      // Load only pending orders using pagination and filtering
      const response = await api.get('/api/manager/orders?limit=100&status_filter=ready_for_pickup');
      
      if (response.data.success && response.data.orders) {
        // Additional client-side filtering for pending orders
        const pending = response.data.orders.filter(order => {
          const status = order.delivery_status || order.order_status || order.status || 'pending';
          return status !== 'delivered' && 
                 status !== 'completed' && 
                 status !== 'picked_up_by_customer' && 
                 status !== 'cancelled';
        });
        
        setPendingOrders(pending);
      } else {
        setPendingOrders([]);
      }
    } catch (err) {
      console.error('Failed to load pending orders:', err);
      setError('Failed to load pending orders');
      console.warn('⚠️ Keeping existing pending orders due to API failure');
      // Don't clear existing orders - preserve what staff have
    }
  };

  const openPendingOrdersModal = async () => {
    setShowPendingOrdersModal(true);
    await loadPendingOrders();
  };

  // Facility Management Functions
  const createFacility = async () => {
    try {
      if (!newFacilityData.name || !newFacilityData.pin) {
        setError('Facility name and PIN are required');
        return;
      }

      const response = await api.post('/api/manager/facilities', newFacilityData);
      
      if (response.data.success) {
        setSuccess('Facility created successfully');
        await loadManagerDashboard(); // Reload dashboard data
        setShowAddFacilityModal(false);
        setNewFacilityData({ name: '', address: '', pin: '', phone: '', contact_person: '' });
      } else {
        setError(response.data.message || 'Failed to create facility');
      }
    } catch (err) {
      console.error('Failed to create facility:', err);
      setError('Failed to create facility');
    }
  };

  const deleteFacility = async (facilityId) => {
    try {
      const response = await api.delete(`/api/manager/facilities/${facilityId}`);
      
      if (response.data.success) {
        setSuccess('Facility deleted successfully');
        await loadManagerDashboard(); // Reload dashboard data
        setShowDeleteFacilityModal(false);
        setFacilityToDelete(null);
      } else {
        setError(response.data.message || 'Failed to delete facility');
      }
    } catch (err) {
      console.error('Failed to delete facility:', err);
      setError('Failed to delete facility');
    }
  };

  const openDeleteFacilityModal = (facility) => {
    setFacilityToDelete(facility);
    setShowDeleteFacilityModal(true);
  };

  const refreshManagerDashboard = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadManagerTasks(),
        loadPipelineStats(),
        loadPhotoGalleryData(),
        loadSubmissionStats()
        // loadDriverLocations() - REMOVED FOR PERFORMANCE
      ]);
    } catch (err) {
      console.error('Failed to refresh dashboard:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-refresh tasks and GPS data every 3 minutes - REMOVED FOR PERFORMANCE
  /*
  useEffect(() => {
    if (currentStep === 'manager-dashboard' && managerDashboardData && autoRefreshActive) {
      const interval = setInterval(async () => {
        console.log('Auto-refreshing tasks and GPS data...');
        setLastAutoRefresh(new Date());
        
        try {
          // Refresh tasks and GPS data specifically
          await Promise.all([
            loadManagerTasks(),
            loadAllDriverTasks(),
            // loadDriverLocations(), - REMOVED FOR PERFORMANCE
            loadPipelineStats(),
            loadSubmissionStats()
          ]);
          console.log('Auto-refresh: Tasks and data updated successfully');
        } catch (refreshError) {
          console.error('Auto-refresh error:', refreshError);
          
          // If it's a CORS or network error, temporarily disable auto-refresh
          if (refreshError.message?.includes('CORS') || 
              refreshError.message?.includes('Network Error') || 
              refreshError.code === 'ERR_NETWORK') {
            console.warn('🔄 CORS/Network error detected - temporarily disabling auto-refresh for 5 minutes');
            setAutoRefreshActive(false);
            
            // Re-enable auto-refresh after 5 minutes
            setTimeout(() => {
              console.log('🔄 Re-enabling auto-refresh after CORS cooldown');
              setAutoRefreshActive(true);
            }, 5 * 60 * 1000);
          }
        }
      }, 3 * 60 * 1000); // 3 minutes

      return () => clearInterval(interval);
    }
  }, [currentStep, managerDashboardData, autoRefreshActive]);
  */

  // Auto-refresh driver tasks and locations every 3 minutes - REMOVED FOR PERFORMANCE
  /*
  useEffect(() => {
    if (currentStep === 'laundry-staff-dashboard' && driverDeliveryStops && autoRefreshActive) {
      const interval = setInterval(async () => {
        console.log('Auto-refreshing driver tasks...');
        setLastAutoRefresh(new Date());
        
        try {
          await loadDriverDeliveryStops();
          console.log('Auto-refresh: Driver tasks updated successfully');
        } catch (refreshError) {
          console.error('Driver auto-refresh error:', refreshError);
          
          // If it's a CORS or network error, temporarily disable auto-refresh
          if (refreshError.message?.includes('CORS') || 
              refreshError.message?.includes('Network Error') || 
              refreshError.code === 'ERR_NETWORK') {
            console.warn('🔄 CORS/Network error detected - temporarily disabling auto-refresh for 5 minutes');
            setAutoRefreshActive(false);
            
            // Re-enable auto-refresh after 5 minutes
            setTimeout(() => {
              console.log('🔄 Re-enabling auto-refresh after CORS cooldown');
              setAutoRefreshActive(true);
            }, 5 * 60 * 1000);
          }
        }
      }, 3 * 60 * 1000); // 3 minutes

      return () => clearInterval(interval);
    }
  }, [currentStep, driverDeliveryStops, autoRefreshActive]);
  */

  // Auto-refresh facility pickup orders every 3 minutes (silent) - REMOVED FOR PERFORMANCE
  /*
  useEffect(() => {
    if (currentStep === 'facility-dashboard' && selectedHotel?.id && autoRefreshActive) {
      const interval = setInterval(async () => {
        console.log('Auto-refreshing facility pickup orders...');
        
        try {
          await loadPickupOrders(selectedHotel.id);
          console.log('Auto-refresh: Facility pickup orders updated successfully');
        } catch (refreshError) {
          console.error('Facility auto-refresh error:', refreshError);
          
          // If it's a CORS or network error, temporarily disable auto-refresh
          if (refreshError.message?.includes('CORS') || 
              refreshError.message?.includes('Network Error') || 
              refreshError.code === 'ERR_NETWORK') {
            console.warn('🔄 CORS/Network error detected - temporarily disabling auto-refresh for 5 minutes');
            setAutoRefreshActive(false);
            
            // Re-enable auto-refresh after 5 minutes
            setTimeout(() => {
              console.log('🔄 Re-enabling auto-refresh after CORS cooldown');
              setAutoRefreshActive(true);
            }, 5 * 60 * 1000);
          }
        }
      }, 3 * 60 * 1000); // 3 minutes

      return () => clearInterval(interval);
    }
  }, [currentStep, selectedHotel?.id, autoRefreshActive]);
  */

  // GPS Location tracking for drivers - REMOVED FOR PERFORMANCE
  /*
  useEffect(() => {
    if (currentStep === 'hotel-select' && userType === 'driver' && user?.id) {
      console.log('Driver dashboard loaded - initiating GPS tracking...');
      
      // Start location tracking when driver is on dashboard
      startLocationTracking();
      
      // Send location updates every 1 minute (60 seconds) for real-time manager tracking
      const locationInterval = setInterval(() => {
        const gpsPermission = localStorage.getItem('sunlight-gps-permission');
        
        if (gpsPermission === 'granted' && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            updateDriverLocation,
            (error) => {
              console.error('Error updating location during interval:', error);
              // Don't stop tracking on temporary errors
            },
            { 
              enableHighAccuracy: true, 
              timeout: 10000, 
              maximumAge: 60000 // 1 minute cache
            }
          );
        } else if (gpsPermission === 'denied') {
          console.log('GPS permission denied, skipping location update');
        }
      }, 60000); // 1 minute (60,000 milliseconds) for manager real-time tracking

      // Set up background location tracking using Page Visibility API
      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.log('App went to background - maintaining GPS tracking...');
        } else {
          console.log('App returned to foreground - GPS tracking continues...');
          // Send immediate location update when returning to foreground
          if (localStorage.getItem('sunlight-gps-permission') === 'granted' && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              updateDriverLocation,
              (error) => console.error('Error updating location on foreground return:', error),
              { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
            );
          }
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        console.log('Cleaning up GPS tracking...');
        stopLocationTracking();
        clearInterval(locationInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else if (userType === 'driver' && currentStep !== 'hotel-select') {
      // Stop tracking when driver leaves dashboard
      stopLocationTracking();
    }
  }, [currentStep, userType, user?.id]);
  */

  // Global GPS persistence - REMOVED FOR PERFORMANCE
  /*
  useEffect(() => {
    if (userType === 'driver' && user?.id) {
      const gpsPermission = localStorage.getItem('sunlight-gps-permission');
      
      if (gpsPermission === 'granted') {
        console.log('Maintaining global GPS tracking for driver throughout app usage...');
        
        // Set up global location interval for continuous tracking
        const globalLocationInterval = setInterval(() => {
          if (navigator.geolocation && localStorage.getItem('sunlight-gps-permission') === 'granted') {
            navigator.geolocation.getCurrentPosition(
              updateDriverLocation,
              (error) => {
                console.error('Global GPS update error:', error);
                // Continue tracking despite errors
              },
              { 
                enableHighAccuracy: true, 
                timeout: 15000, 
                maximumAge: 90000 
              }
            );
          }
        }, 60000); // Global 1-minute updates
        
        // Handle app focus/blur for background tracking
        const handleFocus = () => {
          console.log('App gained focus - sending immediate GPS update...');
          if (navigator.geolocation && localStorage.getItem('sunlight-gps-permission') === 'granted') {
            navigator.geolocation.getCurrentPosition(
              updateDriverLocation,
              (error) => console.error('Focus GPS update error:', error),
              { enableHighAccuracy: true, timeout: 5000, maximumAge: 10000 }
            );
          }
        };

        const handleBlur = () => {
          console.log('App lost focus - GPS tracking continues in background...');
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        
        return () => {
          clearInterval(globalLocationInterval);
          window.removeEventListener('focus', handleFocus);
          window.removeEventListener('blur', handleBlur);
        };
      }
    }
  }, [userType, user?.id]);
  */

  // Auto-refresh tasks every 1 minute for manager dashboard (with initial load)
  useEffect(() => {
    if (currentStep === 'manager-dashboard' && user?.id) {
      console.log('Loading tasks for manager dashboard...');
      
      // Load tasks immediately when dashboard opens
      const loadInitialTasks = async () => {
        try {
          await loadManagerTasks();
          await loadAllDriverTasks();
        } catch (error) {
          console.error('Error loading initial tasks:', error);
        }
      };
      
      // Load tasks immediately
      loadInitialTasks();
      
      // Then set up auto-refresh every 5 minutes (increased from 1 min to reduce load)
      const taskRefreshInterval = setInterval(async () => {
        try {
          await loadManagerTasks();
          await loadAllDriverTasks();
        } catch (refreshError) {
          console.error('Task auto-refresh error:', refreshError);
          
          // If it's a CORS or network error, temporarily disable auto-refresh
          if (refreshError.message?.includes('CORS') || 
              refreshError.message?.includes('Network Error') || 
              refreshError.code === 'ERR_NETWORK') {
            clearInterval(taskRefreshInterval);
          }
        }
      }, 300000); // 5 minutes

      return () => {
        console.log('Stopping task auto-refresh...');
        clearInterval(taskRefreshInterval);
      };
    }
  }, [currentStep, user?.id]);

  // Auto-refresh tasks for driver dashboard (with initial load)
  useEffect(() => {
    if (currentStep === 'hotel-select' && userType === 'driver' && user?.id) {
      // Load tasks immediately when dashboard opens
      const loadInitialDriverTasks = async () => {
        try {
          await loadDriverDeliveryStops();
        } catch (error) {
          console.error('Error loading initial driver tasks:', error);
        }
      };
      
      // Load tasks immediately
      loadInitialDriverTasks();
      
      // Reduce auto-refresh frequency to prevent overload (every 5 minutes)
      const driverTaskRefreshInterval = setInterval(async () => {
        try {
          await loadDriverDeliveryStops();
        } catch (refreshError) {
          console.error('Driver task auto-refresh error:', refreshError);
        }
      }, 300000); // 5 minutes

      return () => {
        clearInterval(driverTaskRefreshInterval);
      };
    }
  }, [currentStep, userType, user?.id]);

  // Auto-refresh PIN management and order management systems - REMOVED FOR PERFORMANCE
  /*
  useEffect(() => {
    // Function removed to improve app performance
  }, [currentStep, user?.id]);
  */

  // Barcode scanning state and functions
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [barcodeScanContext, setBarcodeScanContext] = useState(null); // 'driver' or 'facility'
  const [scannedBarcodes, setScannedBarcodes] = useState([]);

  const openBarcodeScanner = (context, title = "Scan Barcode", subtitle = "Point camera at barcode or QR code") => {
    setBarcodeScanContext({
      type: context,
      title,
      subtitle
    });
    setShowBarcodeScanner(true);
  };

  const closeBarcodeScanner = () => {
    setShowBarcodeScanner(false);
    setBarcodeScanContext(null);
  };

  const handleBarcodeScanned = async (barcode) => {
    try {
      console.log(`Barcode scanned (${barcodeScanContext?.type}):`, barcode);
      
      if (barcodeScanContext?.type === 'driver') {
        // Driver scanning for delivery tracking
        const response = await api.post('/api/barcodes/driver-scan', {
          barcode: barcode,
          driver_name: user?.name || 'Unknown Driver',
          hotel_id: selectedHotel?.id,
          order_id: null // Will be linked by barcode matching later
        });
        
        if (response.data.success) {
          setScannedBarcodes(prev => [...prev, {
            barcode,
            type: 'driver',
            timestamp: new Date().toISOString(),
            driver: user?.name || 'Unknown Driver'
          }]);
          setSuccess('Barcode scanned successfully for delivery tracking!');
        }
      } else if (barcodeScanContext?.type === 'driver-cleancloud') {
        // Driver scanning CleanCloud orders to add them to system
        const response = await api.post('/api/orders/scan-cleancloud-order', {
          barcode_data: barcode,
          scanned_by: user?.name || 'Unknown Driver',
          scanner_type: 'driver',
          hotel_id: selectedHotel?.id
        });
        
        if (response.data.success) {
          setSuccess(`CleanCloud order added! Guest: ${response.data.order.guest_name}`);
          // Refresh any order lists if needed
          if (currentStep === 'facilityDashboard') {
            loadPickupOrders();
          }
        } else {
          setError(response.data.message || 'Failed to add CleanCloud order');
        }
      } else if (barcodeScanContext?.type === 'facility') {
        // Facility scanning for pickup confirmation
        const response = await api.post('/api/barcodes/facility-scan', {
          barcode: barcode,
          facility_staff: 'Facility Staff', // Could be made dynamic
          guest_name: 'Guest', // Could be extracted from order
          hotel_id: selectedHotel?.id,
          order_id: null // Will be linked by barcode matching
        });
        
        if (response.data.success) {
          setScannedBarcodes(prev => [...prev, {
            barcode,
            type: 'facility',
            timestamp: new Date().toISOString(),
            facility: selectedHotel?.name
          }]);
          setSuccess('Barcode scanned successfully! Order marked as picked up.');
          
          // Refresh the pickup orders to reflect the confirmation
          if (currentStep === 'facilityDashboard') {
            loadPickupOrders();
          }
        }
      } else if (barcodeScanContext?.type === 'facility-cleancloud') {
        // Facility scanning CleanCloud orders to add them to system
        const response = await api.post('/api/orders/scan-cleancloud-order', {
          barcode_data: barcode,
          scanned_by: 'Facility Staff',
          scanner_type: 'facility',
          hotel_id: selectedHotel?.id
        });
        
        if (response.data.success) {
          setSuccess(`CleanCloud order added! Guest: ${response.data.order.guest_name}`);
          // Refresh pickup orders to show new order
          if (currentStep === 'facilityDashboard') {
            loadPickupOrders();
          }
        } else {
          setError(response.data.message || 'Failed to add CleanCloud order');
        }
      }
      
      closeBarcodeScanner();
    } catch (err) {
      console.error('Error processing barcode scan:', err);
      setError(`Failed to process barcode scan: ${err.response?.data?.detail || err.message}`);
    }
  };

  const renderFacilityDetails = () => (
    <div className="facility-details">
      <div className="details-header">
        <div className="header-info">
          <Building2 className="w-8 h-8 text-green-600" />
          <div>
            <h1 className="text-3xl font-bold">{selectedFacilityDetails.facility.name}</h1>
            <p className="text-gray-600">{selectedFacilityDetails.facility.address}</p>
            <Badge variant="outline" className="mt-2">
              PIN: {selectedFacilityDetails.facility.pin}
            </Badge>
          </div>
        </div>
        
        <div className="header-actions">
          <Button
            onClick={() => setSelectedFacilityDetails(null)}
            variant="outline"
            size="sm"
          >
            {tr('back')}
          </Button>
        </div>
      </div>

      <div className="facility-content">
        {/* Statistics Overview */}
        <div className="stats-grid">
          <Card className="stat-card">
            <CardContent className="stat-content">
              <Package className="w-6 h-6 text-orange-500" />
              <div>
                <h3 className="text-2xl font-bold">{selectedFacilityDetails.statistics.pending_count}</h3>
                <p className="text-sm text-gray-600">{tr('pendingOrders')}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card">
            <CardContent className="stat-content">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <div>
                <h3 className="text-2xl font-bold">{selectedFacilityDetails.statistics.confirmations_count}</h3>
                <p className="text-sm text-gray-600">{tr('completedPickups')}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stat-card">
            <CardContent className="stat-content">
              <Truck className="w-6 h-6 text-purple-500" />
              <div>
                <h3 className="text-2xl font-bold">{selectedFacilityDetails.statistics.deliveries_count}</h3>
                <p className="text-sm text-gray-600">{tr('deliveriesMade')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Orders */}
        <Card className="pending-orders">
          <CardHeader>
            <CardTitle>{tr('pendingOrders')} ({(selectedFacilityDetails.pending_orders || []).length})</CardTitle>
          </CardHeader>
          <CardContent>
            {(selectedFacilityDetails.pending_orders || []).length === 0 ? (
              <div className="no-orders">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p className="text-gray-500">No pending orders</p>
              </div>
            ) : (
              <div className="orders-list">
                {(selectedFacilityDetails.pending_orders || []).map((order) => (
                  <Card key={order.id} className="order-item">
                    <CardContent className="order-content">
                      <div className="order-header">
                        <div className="guest-info">
                          <h4 className="order-guest">{order.guest_name}</h4>
                          {/* CleanCloud badge removed */}
                        </div>
                        <Badge variant="secondary">Room {order.room_number}</Badge>
                      </div>
                      
                      <div className="order-details">
                        <div className="detail-item">
                          <MapPin className="w-4 h-4 text-red-500" />
                          <span>{order.storage_location}</span>
                        </div>
                        <div className="detail-item">
                          <Clock className="w-4 h-4 text-blue-500" />
                          <span>Ready: {new Date(order.ready_date).toLocaleString()}</span>
                        </div>
                        {order.items_description && (
                          <div className="detail-item">
                            <Package className="w-4 h-4 text-purple-500" />
                            <span>{order.items_description}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <div className="activity-grid">
          <Card className="activity-card">
            <CardHeader>
              <CardTitle>Recent Pickup Confirmations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="activity-list">
                {(selectedFacilityDetails.recent_confirmations || []).length === 0 ? (
                  <p className="text-gray-500">No recent confirmations</p>
                ) : (
                  (selectedFacilityDetails.recent_confirmations || []).slice(0, 5).map((confirmation) => (
                    <div key={confirmation.id} className="activity-item">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <div className="activity-content">
                        <span className="activity-title">{confirmation.picked_up_by}</span>
                        <span className="activity-time">{new Date(confirmation.pickup_timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card className="activity-card">
            <CardHeader>
              <CardTitle>Recent Deliveries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="activity-list">
                {(selectedFacilityDetails.recent_deliveries || []).length === 0 ? (
                  <p className="text-gray-500">No recent deliveries</p>
                ) : (
                  (selectedFacilityDetails.recent_deliveries || []).slice(0, 5).map((delivery) => (
                    <div key={delivery.id} className="activity-item">
                      <Truck className="w-4 h-4 text-purple-500" />
                      <div className="activity-content">
                        <span className="activity-title">Driver: {delivery.driver_name}</span>
                        <span className="activity-time">{new Date(delivery.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
  // PWA install prompt handling
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallApp = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installed');
      setIsInstallable(false);
    }
    
    setDeferredPrompt(null);
  };

  // Load available staff when modal opens
  useEffect(() => {
    if (showCustomTaskCreation && (!availableStaff || availableStaff.length === 0)) {
      loadAvailableStaff();
    }
  }, [showCustomTaskCreation]);

  // Load hotels when template creation modal opens
  useEffect(() => {
    if (showTemplateCreation && (!hotels || hotels.length === 0)) {
      loadHotels();
    }
  }, [showTemplateCreation]);


  const getTotalBags = () => {
    return deliveryLines.reduce((total, line) => ({
      regular: total.regular + line.bag_counts.regular,
      bulk: total.bulk + line.bag_counts.bulk,
      specialty: total.specialty + line.bag_counts.specialty
    }), { regular: 0, bulk: 0, specialty: 0 });
  };

  // Common layout wrapper for consistent dimensions across all screens
  const renderScreenLayout = (content, screenClass = '') => (
    <div className={`screen-container ${screenClass}`}>
      <div className="screen-content">
        {content}
      </div>
    </div>
  );

  // Render functions
  const renderWelcome = () => renderScreenLayout(
    <div className="welcome-header text-center">
      <div className="logo-section">
        <div className="company-logo mb-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_manual-kiosk/artifacts/cavnmvm1_Sunlight%20Hospitality%20PNG.png"
            alt="Sunlight Hospitality Logo"
            className="w-80 h-auto mx-auto mb-2"
          />
          <div className="brand-text text-center mb-3">
            <h1 className="welcome-title text-5xl" style={{ fontWeight: '300', color: '#6B7280 !important', background: 'none !important', lineHeight: '1.3', paddingBottom: '0.5rem' }}>Laundry</h1>
          </div>
        </div>
      </div>

      <div className="action-buttons space-y-4 mb-4 max-w-4xl mx-auto">
        <Button
          size="xl"
          className="action-btn primary-action w-full h-20 text-xl"
          onClick={() => {
            setUserType('driver');
            setCurrentStep('auth');
          }}
        >
          <Truck className="w-10 h-10 mr-4" />
          {tr('laundryStaff')}
        </Button>
        
        <Button
          size="xl"
          className="action-btn primary-action w-full h-20 text-xl"
          onClick={() => {
            setUserType('hotel_staff');
            setCurrentStep('hotel-auth');
          }}
        >
          <Building2 className="w-10 h-10 mr-4" />
          {tr('facilityStaff')}
        </Button>
      </div>

      {/* Language Toggle - Smaller buttons below */}
      <div className="language-toggle-bottom">
        <div className="flex justify-center space-x-3 mb-4">
          <Button
            variant={language === 'en' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLanguage('en')}
            className="language-btn-small"
          >
            <Globe className="w-3 h-3 mr-1" />
            English
          </Button>
          <Button
            variant={language === 'es' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLanguage('es')}
            className="language-btn-small"
          >
            <Globe className="w-3 h-3 mr-1" />
            Español
          </Button>
        </div>
        
        {/* Download App Button */}
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // For PWA installation
              if (window.deferredPrompt) {
                window.deferredPrompt.prompt();
                window.deferredPrompt.userChoice.then((choiceResult) => {
                  if (choiceResult.outcome === 'accepted') {
                    console.log('User accepted the PWA install prompt');
                  }
                  window.deferredPrompt = null;
                });
              } else {
                // Fallback - show install instructions or app store links
                alert('To install this app:\n\niOS: Tap Share button → "Add to Home Screen"\nAndroid: Tap menu (⋮) → "Add to Home screen" or "Install app"');
              }
            }}
            className="download-app-btn flex items-center space-x-2 px-4 py-2 bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium rounded-full transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Download App</span>
          </Button>
        </div>
      </div>
    </div>,
    'welcome-screen'
  );

  const renderAuth = () => renderScreenLayout(
    <div className="auth-content-full">
      <div className="auth-header text-center mb-8">
        <div className="w-24 h-24 bg-blue-600 rounded-full mx-auto mb-6 flex items-center justify-center">
          <Truck className="w-12 h-12 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          {userType === 'manager' ? 'Manager Authentication' : tr('laundryAuth')}
        </h1>
        <p className="text-lg text-gray-600">Enter your PIN to access the system</p>
      </div>
      
      <div className="auth-form-large max-w-2xl mx-auto">
        <Card className="auth-card-large shadow-xl border-0">
          <CardContent className="p-12">
            <div className="space-y-8">
              <div className="space-y-4">
                <Label htmlFor="pin" className="text-xl font-medium text-gray-600 block text-center">
                  {tr('enterPin')}
                </Label>
                <Input
                  id="pin"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="text-center text-4xl tracking-wider h-20 text-gray-800 font-bold"
                  maxLength={4}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      authenticate(userType === 'hotel_staff', userType === 'manager');
                    }
                  }}
                />
              </div>
              
              {error && (
                <Alert className="border-red-200 bg-red-50 p-6">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                  <AlertDescription className="text-red-800 text-lg ml-3">
                    {error}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-4">
                <Button
                  onClick={() => authenticate(userType === 'hotel_staff', userType === 'manager')}
                  disabled={loading || pin.length !== 4}
                  className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xl"
                >
                  {loading ? tr('loading') : tr('authenticate')}
                </Button>
                
                <Button
                  onClick={() => setCurrentStep('welcome')}
                  variant="outline"
                  className="w-full h-12 text-lg"
                >
                  {tr('back')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderHotelAuth = () => renderScreenLayout(
    <div className="auth-content-full">
      <div className="auth-header text-center mb-8">
        <div className="w-24 h-24 bg-green-600 rounded-full mx-auto mb-6 flex items-center justify-center">
          <Building2 className="w-12 h-12 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">{tr('facilityAuth')}</h1>
        <p className="text-lg text-gray-600">Enter your facility PIN to access the system</p>
      </div>
      
      <div className="auth-form-large max-w-2xl mx-auto">
        <Card className="auth-card-large shadow-xl border-0">
          <CardContent className="p-12">
            <div className="space-y-8">
              <div className="space-y-4">
                <Label htmlFor="hotel-pin" className="text-xl font-medium text-gray-600 block text-center">
                  {tr('enterPin')}
                </Label>
                <Input
                  id="hotel-pin"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="text-center text-4xl tracking-wider h-20 text-gray-800 font-bold"
                  maxLength={4}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      authenticate(true);
                    }
                  }}
                />
              </div>
              
              {error && (
                <Alert className="border-red-200 bg-red-50 p-6">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                  <AlertDescription className="text-red-800 text-lg ml-3">
                    {error}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-4">
                <Button
                  onClick={() => authenticate(true)}
                  disabled={loading || pin.length !== 4}
                  className="w-full h-16 bg-green-600 hover:bg-green-700 text-white font-medium text-xl"
                >
                  {loading ? tr('loading') : tr('authenticate')}
                </Button>
                
                <Button
                  onClick={() => setCurrentStep('welcome')}
                  variant="outline"
                  className="w-full h-12 text-lg"
                >
                  {tr('back')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderHotelSelect = () => (
    <div className="laundry-staff-dashboard" style={{maxWidth: '100vw', width: '100%', overflowX: 'hidden', boxSizing: 'border-box', padding: '0.5rem'}}>
      <div className="dashboard-header mb-2 py-1 max-w-full" style={{minHeight: '50px'}}>
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_manual-kiosk/artifacts/cavnmvm1_Sunlight%20Hospitality%20PNG.png"
              alt="Sunlight Hospitality Logo"
              className="w-5 h-auto mr-2 sm:w-6"
              style={{maxHeight: '24px'}}
            />
            <div className="flex flex-col justify-center">
              <h1 className="text-sm sm:text-lg font-semibold text-gray-800 leading-tight">{tr('driverDashboard')}</h1>
              <p className="text-xs text-gray-600 leading-tight">{tr('welcomeBack')}, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Language Toggle */}
            <div className="flex gap-1">
              <Button
                variant={language === 'en' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('en')}
                className="text-xs px-1.5 py-0.5 h-7 sm:px-2 sm:py-1 sm:h-8"
              >
                <Globe className="w-3 h-3 mr-0.5 sm:mr-1" />
                EN
              </Button>
              <Button
                variant={language === 'es' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('es')}
                className="text-xs px-1.5 py-0.5 h-7 sm:px-2 sm:py-1 sm:h-8"
              >
                <Globe className="w-3 h-3 mr-0.5 sm:mr-1" />
                ES
              </Button>
            </div>
            
            <Button
              onClick={() => setCurrentStep('welcome')}
              variant="outline"
              size="sm"
              className="text-xs px-2 py-0.5 h-7 sm:px-3 sm:py-1 sm:h-8"
            >
              {tr('back')}
            </Button>
          </div>
        </div>
      </div>

      <div className="dashboard-grid grid grid-cols-1 gap-4 max-w-full">
        
        {/* Card 1: Customer Database Search - Full Width on Mobile */}
        <Card className="customer-search-card w-full" style={{maxHeight: '280px'}}>
          <CardHeader className="pb-1" style={{padding: '12px 16px 4px 16px'}}>
            <CardTitle className="flex items-center text-xs font-semibold" style={{fontSize: '12px', margin: '0'}}>
              <Search className="w-3 h-3 mr-1 text-blue-600" />
              {tr('customerSearch')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-1" style={{padding: '4px 16px 12px 16px'}}>
            <div className="space-y-1">
              <div className="search-input-group relative">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder={tr('typeCustomerName')}
                    value={customerSearchQuery}
                    onChange={(e) => handleLiveSearch(e.target.value)}
                    className="w-full"
                    style={{height: '32px', fontSize: '16px', padding: '6px 12px'}}
                  />
                  
                  {/* Search Results - positioned relative to input only */}
                  {customerSearchResults.length > 0 && (
                    <div className="search-results absolute bg-white border rounded-lg shadow-lg z-50" style={{top: 'calc(100% + 4px)', left: 0, right: 0}}>
                      <div className="p-3">
                        <h5 className="font-semibold mb-2">
                          Found {customerSearchResults.length} customers
                        </h5>
                        <div className="customers-list max-h-64 overflow-y-auto space-y-2">
                          {customerSearchResults.map((customer) => (
                          <div key={customer.id} className="customer-item p-3 border rounded-lg hover:bg-blue-50">
                            <div className="customer-mobile-layout">
                              <div className="customer-info mb-3">
                                <h6 className="font-medium text-sm">{customer.name}</h6>
                                {customer.phone && (
                                  <p className="text-xs text-gray-600">
                                    <Phone className="w-3 h-3 inline mr-1" />
                                    {customer.phone}
                                  </p>
                                )}
                                {customer.address && (
                                  <p className="text-xs text-gray-600 truncate">
                                    <MapPin className="w-3 h-3 inline mr-1" />
                                    {customer.address}
                                  </p>
                                )}
                              </div>
                              <Button
                                onClick={() => createCustomerDeliveryStop(customer)}
                                disabled={loading}
                                className="w-full bg-green-600 hover:bg-green-700 text-white text-xs py-1"
                                style={{height: '24px', fontSize: '10px'}}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Delivery Stop
                              </Button>
                            </div>
                          </div>
                          ))}
                        </div>
                        <div className="text-xs text-gray-500 mt-2 text-center">
                          Showing all {customerSearchResults.length} results (scroll to see more)
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <Button
                  onClick={() => searchCustomers(customerSearchQuery)}
                  disabled={loading || !customerSearchQuery.trim()}
                  className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
                
                <Button
                  onClick={() => {
                    setShowAddCustomerModal(true);
                  }}
                  disabled={loading}
                  className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white"
                  style={{display: 'block !important'}}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Customer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: My Tasks - Full Width on Mobile */}
        <Card className="my-tasks-card w-full" style={{minHeight: 'calc(100vh - 200px)'}}>
          <CardHeader>
            <CardTitle className="flex items-center text-xl">
              <Package className="w-6 h-6 mr-2 text-green-600" />
              {tr('myTasks')} ({groupedDeliveryStops.length} {tr('locations')})
            </CardTitle>
            <p className="text-sm text-gray-600">{driverDeliveryStops.length} {tr('totalOrdersGrouped')}</p>
          </CardHeader>
          <CardContent>
            {driverDeliveryStops.length === 0 ? (
              <div className="no-tasks text-center py-6">
                <Truck className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 text-sm">{tr('noDeliveryStops')}</p>
                <p className="text-xs text-gray-400">{tr('searchCustomersToCreate')}</p>
              </div>
            ) : (
              <div className="tasks-list overflow-y-auto space-y-3" style={{height: 'calc(100vh - 250px)', minHeight: '400px'}}>
                {groupedDeliveryStops.map((task) => (
                  <Card key={task.taskId} className={`task-group border-l-4 ${task.hasManagerTasks ? 'border-blue-500 bg-blue-50' : 'border-green-500'}`}>
                    <CardContent className="p-3">
                      {/* Task Header */}
                      <div 
                        className="task-header cursor-pointer"
                        onClick={() => toggleTaskExpansion(task.taskId)}
                      >
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
                          <div className="task-info flex-1">
                            <h6 className={`font-semibold text-sm flex flex-wrap items-center ${task.hasManagerTasks ? 'text-blue-700' : 'text-green-700'}`}>
                              <Truck className={`w-4 h-4 mr-2 ${task.hasManagerTasks ? 'text-blue-600' : 'text-green-600'}`} />
                              <span className="truncate">{task.taskName}</span>
                              {task.hasManagerTasks && (
                                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full whitespace-nowrap">{tr('managerTask')}</span>
                              )}
                              {/* Check if any orders in this task are consolidated */}
                              {task.orders.some(order => order.is_consolidated_task) && (
                                <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full whitespace-nowrap">Consolidated</span>
                              )}
                            </h6>
                            <p className="text-xs text-gray-600 truncate">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {tr('location')}: {task.location}
                            </p>
                            <div className="text-xs text-gray-600 flex flex-wrap items-center gap-1">
                              <div className="flex items-center">
                                <Package className="w-3 h-3 mr-1" />
                                <span>{tr('amount')}: {task.totalOrders} {tr('orders')}</span>
                              </div>
                              {/* Show consolidated stops info */}
                              {task.orders.some(order => order.is_consolidated_task) && (
                                <span className="text-xs text-purple-600">
                                  (+{task.orders.filter(order => order.is_consolidated_task).reduce((total, order) => total + (order.consolidation_info?.total_stops || 0), 0)} driver stops)
                                </span>
                              )}
                              {task.pendingCount > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {task.pendingCount} {tr('pending')}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="task-controls flex-shrink-0 mt-2 sm:mt-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1 w-8 h-8"
                            >
                              {expandedTasks.has(task.taskId) ? '↑' : '↓'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Sub-tasks */}
                      {expandedTasks.has(task.taskId) && (
                        <div className="sub-tasks mt-3 border-t pt-3">
                          <h6 className="text-xs font-medium text-gray-700 mb-2">{tr('individualOrders')}</h6>
                          <div className="space-y-2">
                            {task.orders.map((order) => (
                              <div key={order.id} className="sub-task-item p-2 bg-gray-50 rounded border">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="order-info flex-1">
                                    <p className="font-medium text-xs">{order.customer_name || order.guest_name}</p>
                                    <p className="text-xs text-gray-600">
                                      {tr('room')}: {order.room_number} • {order.order_type}
                                    </p>
                                    <Badge 
                                      variant={order.delivery_status === 'pending_pickup' ? 'secondary' : 'default'}
                                      className="text-xs mt-1"
                                    >
                                      {order.delivery_status === 'pending_pickup' ? tr('ready') : tr('delivered')}
                                    </Badge>
                                    {/* Consolidated Task Indicator */}
                                    {order.is_consolidated_task && (
                                      <Badge variant="outline" className="text-xs mt-1 ml-2 border-purple-300 text-purple-700">
                                        Consolidated ({order.consolidation_info?.total_stops || 0} stops)
                                      </Badge>
                                    )}
                                  </div>
                                  {order.delivery_status === 'pending_pickup' && !order.is_manager_task && (
                                    <Button
                                      onClick={() => setSelectedDeliveryStop(order)}
                                      size="sm"
                                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1"
                                    >
                                      <Camera className="w-3 h-3 mr-1" />
                                      Deliver
                                    </Button>
                                  )}
                                </div>

                                {/* Consolidated Task Display */}
                                {order.is_consolidated_task && (
                                  <div className="consolidated-task-display mt-3">
                                    {/* Manager Requirements Section */}
                                    {order.checklist_items && order.checklist_items.length > 0 && (
                                      <div className="manager-requirements mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                                        <h6 className="font-medium text-blue-800 mb-2 flex items-center">
                                          <Shield className="w-4 h-4 mr-1" />
                                          Manager Requirements
                                        </h6>
                                        <div className="checklist-items space-y-2">
                                          {order.checklist_items.map((item) => (
                                            <div key={item.id} className="checklist-item flex items-center space-x-3 p-2 bg-white rounded border">
                                              <input
                                                type="checkbox"
                                                checked={item.completed || false}
                                                onChange={() => {
                                                  handleTaskItemClick(order, item, order.required_photos || 0);
                                                }}
                                                className="w-4 h-4"
                                              />
                                              <div className="flex-1">
                                                <span className={`text-sm ${item.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                                  {item.text}
                                                </span>
                                                {item.completed && item.completed_at && (
                                                  <p className="text-xs text-gray-400 mt-1">
                                                    <Clock className="w-3 h-3 inline mr-1" />
                                                    {tr('completedAt')} {formatCompletionTime(item.completed_at)}
                                                  </p>
                                                )}
                                              </div>
                                              {(!item.completed) && (order.required_photos > 0) && (
                                                <div className="flex items-center text-blue-600 text-xs">
                                                  <Camera className="w-3 h-3 mr-1" />
                                                  <span>{order.required_photos} {order.required_photos > 1 ? tr('photos') : tr('photo')}</span>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                        <div className="checklist-progress mt-3">
                                          <div className="progress-bar bg-gray-200 rounded h-2">
                                            <div 
                                              className="progress-fill bg-blue-500 h-2 rounded"
                                              style={{
                                                width: `${order.checklist_items.length > 0 ? (order.checklist_items.filter(item => item.completed).length / order.checklist_items.length) * 100 : 0}%`
                                              }}
                                            />
                                          </div>
                                          <p className="text-sm text-gray-600 mt-1">
                                            Requirements: {order.checklist_items.filter(item => item.completed || false).length}/{order.checklist_items.length} completed
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Driver-Added Delivery Stops Section */}
                                    {order.driver_delivery_stops && order.driver_delivery_stops.length > 0 && (
                                      <div className="driver-stops mb-4 p-3 bg-green-50 rounded border border-green-200">
                                        <h6 className="font-medium text-green-800 mb-2 flex items-center">
                                          <Truck className="w-4 h-4 mr-1" />
                                          Driver-Added Stops ({order.driver_delivery_stops.length})
                                        </h6>
                                        <div className="stops-list space-y-2">
                                          {order.driver_delivery_stops.map((stop, index) => (
                                            <div key={stop.id || index} className="delivery-stop p-2 bg-white rounded border">
                                              <div className="flex justify-between items-start">
                                                <div className="stop-info flex-1">
                                                  <p className="font-medium text-xs text-gray-800">{stop.guest_name}</p>
                                                  <p className="text-xs text-gray-600">{tr('room')}: {stop.room_number}</p>
                                                  <p className="text-xs text-gray-500">{stop.items_description}</p>
                                                  <Badge 
                                                    variant={stop.status === 'pending' ? 'secondary' : 'default'}
                                                    className="text-xs mt-1"
                                                  >
                                                    {stop.status === 'pending' ? 'Pending Delivery' : 'Delivered'}
                                                  </Badge>
                                                </div>
                                                {stop.status === 'pending' && (
                                                  <Button
                                                    onClick={() => {
                                                      // Create a temporary delivery stop object for the delivery process
                                                      const tempStop = {
                                                        ...stop,
                                                        delivery_status: 'pending_pickup',
                                                        is_consolidated_stop: true,
                                                        parent_task_id: order.id
                                                      };
                                                      setSelectedDeliveryStop(tempStop);
                                                    }}
                                                    size="sm"
                                                    className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1"
                                                  >
                                                    <Camera className="w-3 h-3 mr-1" />
                                                    Deliver
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Overall Task Completion */}
                                    {order.checklist_items && order.checklist_items.every(item => item.completed) && order.status !== 'submitted' && (
                                      <div className="submit-task mt-3">
                                        <Button
                                          onClick={() => submitTaskForApproval(order.id)}
                                          className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                                          disabled={loading}
                                        >
                                          <FileSignature className="w-4 h-4 mr-2" />
                                          Submit Consolidated Task for Approval
                                        </Button>
                                      </div>
                                    )}
                                    
                                    {order.status === 'submitted' && (
                                      <div className="submitted-status mt-3">
                                        <Badge variant="default" className="w-full justify-center bg-purple-100 text-purple-800">
                                          <CheckCircle className="w-3 h-3 mr-1" />
                                          Consolidated Task Submitted - Awaiting Manager Approval
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Regular Manager Task Checklist (non-consolidated) */}
                                {order.is_manager_task && !order.is_consolidated_task && order.checklist_items && order.checklist_items.length > 0 && (
                                  <div className="task-checklist mt-3">
                                    <h6 className="font-medium text-gray-700 mb-2">{tr('taskChecklist')}</h6>
                                    <div className="checklist-items space-y-2">
                                      {order.checklist_items.map((item) => (
                                        <div key={item.id} className="checklist-item flex items-center space-x-3 p-2 bg-white rounded border">
                                          <input
                                            type="checkbox"
                                            checked={item.completed || false}
                                            onChange={() => {
                                              handleTaskItemClick(order, item, order.required_photos || 0);
                                            }}
                                            className="w-4 h-4"
                                          />
                                          <span className={`text-sm flex-1 ${item.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                            {item.text}
                                          </span>
                                          {(!item.completed) && (
                                            <div className="flex items-center space-x-1 text-xs">
                                              {(order.required_photos > 0) ? (
                                                <div className="flex items-center text-blue-600">
                                                  <Camera className="w-3 h-3 mr-1" />
                                                  <span>{order.required_photos} {order.required_photos > 1 ? tr('photos') : tr('photo')}</span>
                                                </div>
                                              ) : (
                                                <div className="text-gray-500">
                                                  <span>No photos required</span>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                    <div className="checklist-progress mt-3">
                                      <div className="progress-bar bg-gray-200 rounded h-2">
                                        <div 
                                          className="progress-fill bg-green-500 h-2 rounded"
                                          style={{
                                            width: `${order.checklist_items.length > 0 ? (order.checklist_items.filter(item => item.completed).length / order.checklist_items.length) * 100 : 0}%`
                                          }}
                                        />
                                      </div>
                                      <p className="text-sm text-gray-600 mt-1">
                                        {tr('progress')}: {order.checklist_items.filter(item => item.completed || false).length}/{order.checklist_items.length} {tr('completed')}
                                      </p>
                                    </div>
                                    
                                    {/* Submit Task Button */}
                                    {order.checklist_items.every(item => item.completed) && order.status !== 'submitted' && (
                                      <div className="submit-task mt-3">
                                        <Button
                                          onClick={() => submitTaskForApproval(order.id)}
                                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                                          disabled={loading}
                                        >
                                          <FileSignature className="w-4 h-4 mr-2" />
                                          Submit for Manager Approval
                                        </Button>
                                      </div>
                                    )}
                                    
                                    {order.status === 'submitted' && (
                                      <div className="submitted-status mt-3">
                                        <Badge variant="default" className="w-full justify-center">
                                          <CheckCircle className="w-3 h-3 mr-1" />
                                          Submitted - Awaiting Manager Approval
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            <div className="task-actions mt-4">
              <Button
                onClick={() => loadDriverDeliveryStops()}
                variant="outline"
                size="sm"
                className="w-full"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {tr('refreshTasks')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Community Facilities */}
        <Card className="facilities-card">
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <Building2 className="w-5 h-5 mr-2 text-purple-600" />
              {tr('communityFacilities')}
            </CardTitle>
            <p className="text-sm text-gray-600">{tr('selectForTraditionalDelivery')}</p>
          </CardHeader>
          <CardContent>
            <div className="facilities-list max-h-80 overflow-y-auto space-y-2">
              {hotels.map(hotel => (
                <Card
                  key={hotel.id}
                  className="facility-item cursor-pointer hover:bg-purple-50 border-l-4 border-purple-500"
                  onClick={() => startDelivery(hotel)}
                >
                  <CardContent className="p-3">
                    <div className="facility-info">
                      <h6 className="font-semibold text-sm">{hotel.name}</h6>
                      <p className="text-xs text-gray-600 truncate">{hotel.address}</p>
                      <div className="flex items-center mt-1">
                        <MapPin className="w-3 h-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-500">PIN: {hotel.pin}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderRecipients = () => (
    <div className="recipients-screen">
      <Card className="recipients-card">
        <CardHeader>
          <CardTitle className="text-center text-3xl">
            <Users className="w-8 h-8 mx-auto mb-4 text-purple-600" />
            {tr('addRecipients')}
          </CardTitle>
          <div className="hotel-info">
            <Badge variant="outline" className="hotel-badge">
              <Building2 className="w-4 h-4 mr-2" />
              {selectedHotel?.name}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="recipient-form">
            <div className="form-group">
              <Label className="text-xl">{tr('selectRecipient')}</Label>
              <div className="recipient-grid">
                {recipients.map(recipient => (
                  <Card
                    key={recipient.id}
                    className={`recipient-item ${selectedRecipient?.id === recipient.id ? 'selected' : ''}`}
                    onClick={() => setSelectedRecipient(recipient)}
                  >
                    <CardContent className="recipient-content">
                      <User className="w-5 h-5 text-blue-500 mb-2" />
                      <h4 className="recipient-name">{recipient.full_name}</h4>
                      {recipient.room_number && (
                        <p className="recipient-room">Room {recipient.room_number}</p>
                      )}
                      {recipient.department && (
                        <p className="recipient-dept">{recipient.department}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {selectedRecipient && (
              <>
                <div className="form-group">
                  <Label className="text-xl">{tr('bagCounts')}</Label>
                  <div className="bag-counters">
                    <div className="counter-group">
                      <Label>{tr('regular')}</Label>
                      <div className="counter">
                        <Button
                          onClick={() => setBagCounts(prev => ({ ...prev, regular: Math.max(0, prev.regular - 1) }))}
                          variant="outline"
                          size="lg"
                        >
                          -
                        </Button>
                        <span className="counter-value">{bagCounts.regular}</span>
                        <Button
                          onClick={() => setBagCounts(prev => ({ ...prev, regular: prev.regular + 1 }))}
                          variant="outline"
                          size="lg"
                        >
                          +
                        </Button>
                      </div>
                    </div>
                    
                    <div className="counter-group">
                      <Label>{tr('bulk')}</Label>
                      <div className="counter">
                        <Button
                          onClick={() => setBagCounts(prev => ({ ...prev, bulk: Math.max(0, prev.bulk - 1) }))}
                          variant="outline"
                          size="lg"
                        >
                          -
                        </Button>
                        <span className="counter-value">{bagCounts.bulk}</span>
                        <Button
                          onClick={() => setBagCounts(prev => ({ ...prev, bulk: prev.bulk + 1 }))}
                          variant="outline"
                          size="lg"
                        >
                          +
                        </Button>
                      </div>
                    </div>
                    
                    <div className="counter-group">
                      <Label>{tr('specialty')}</Label>
                      <div className="counter">
                        <Button
                          onClick={() => setBagCounts(prev => ({ ...prev, specialty: Math.max(0, prev.specialty - 1) }))}
                          variant="outline"
                          size="lg"
                        >
                          -
                        </Button>
                        <span className="counter-value">{bagCounts.specialty}</span>
                        <Button
                          onClick={() => setBagCounts(prev => ({ ...prev, specialty: prev.specialty + 1 }))}
                          variant="outline"
                          size="lg"
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="notes" className="text-xl">{tr('notes')}</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="notes-input"
                    placeholder="Optional delivery notes..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <Label className="text-xl">{tr('addPhotos')}</Label>
                  <div className="photo-upload">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="photo-input"
                      id="photo-upload"
                    />
                    <Button
                      onClick={() => document.getElementById('photo-upload').click()}
                      variant="outline"
                      size="lg"
                      className="photo-btn"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      {tr('addPhotos')}
                    </Button>
                    {photos.length > 0 && (
                      <div className="photo-preview">
                        {photos.map((photo, index) => (
                          <img
                            key={index}
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            className="photo-thumbnail"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="recipient-actions">
                  <Button
                    onClick={() => openBarcodeScanner('driver', 'Scan Delivery Barcodes', 'Scan barcode on each bag for this delivery')}
                    variant="outline"
                    size="lg"
                    className="scan-btn"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Scan Barcodes
                  </Button>
                  
                  {/* CleanCloud button removed */}
                  
                  <Button
                    onClick={addRecipientToDelivery}
                    disabled={loading || !selectedRecipient}
                    size="lg"
                    className="add-recipient-btn"
                  >
                    {loading ? tr('loading') : tr('addRecipient')}
                  </Button>
                </div>
                
                {/* Scanned Barcodes Display */}
                {scannedBarcodes.filter(scan => scan.type === 'driver').length > 0 && (
                  <div className="scanned-barcodes mt-4">
                    <h4 className="text-lg font-semibold mb-3">Scanned Barcodes ({scannedBarcodes.filter(scan => scan.type === 'driver').length})</h4>
                    <div className="barcode-list">
                      {scannedBarcodes.filter(scan => scan.type === 'driver').map((scan, index) => (
                        <div key={index} className="barcode-item">
                          <Badge variant="outline" className="barcode-badge">
                            <Camera className="w-3 h-3 mr-1" />
                            {scan.barcode}
                          </Badge>
                          <span className="scan-time text-xs text-gray-500">
                            {new Date(scan.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {deliveryLines.length > 0 && (
        <Card className="delivery-summary">
          <CardHeader>
            <CardTitle className="text-2xl">Current Delivery</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="delivery-lines">
              {deliveryLines.map((line, index) => (
                <div key={line.id} className="delivery-line">
                  <div className="line-info">
                    <h4>{line.recipient_name}</h4>
                    {line.room_number && <p>Room {line.room_number}</p>}
                    {line.department && <p>{line.department}</p>}
                  </div>
                  <div className="line-bags">
                    <Badge variant="outline">
                      {line.bag_counts.regular + line.bag_counts.bulk + line.bag_counts.specialty} bags
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            
            <Separator className="my-4" />
            
            <div className="action-buttons">
              <Button
                onClick={() => openBarcodeScanner('driver', 'Scan Delivery Barcodes', 'Scan barcode on each bag for this delivery')}
                variant="outline"
                size="lg"
                className="scan-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                Scan Barcodes
              </Button>
              
              <Button
                onClick={() => setCurrentStep('review')}
                size="lg"
                className="review-btn"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                {tr('reviewDelivery')}
              </Button>
            </div>
            
            {/* Scanned Barcodes Display for Recipients Step */}
            {scannedBarcodes.filter(scan => scan.type === 'driver').length > 0 && (
              <div className="scanned-barcodes mt-4">
                <h4 className="text-lg font-semibold mb-3">Scanned Barcodes ({scannedBarcodes.filter(scan => scan.type === 'driver').length})</h4>
                <div className="barcode-list">
                  {scannedBarcodes.filter(scan => scan.type === 'driver').map((scan, index) => (
                    <div key={index} className="barcode-item">
                      <Badge variant="outline" className="barcode-badge">
                        <Camera className="w-3 h-3 mr-1" />
                        {scan.barcode}
                      </Badge>
                      <span className="scan-time text-xs text-gray-500">
                        {new Date(scan.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderReview = () => {
    const totalBags = getTotalBags();
    
    return (
      <Card className="review-card">
        <CardHeader>
          <CardTitle className="text-center text-3xl">
            <CheckCircle className="w-8 h-8 mx-auto mb-4 text-green-600" />
            {tr('reviewDelivery')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="review-content">
            <div className="delivery-info">
              <h3 className="text-2xl mb-4">{selectedHotel?.name}</h3>
              
              <div className="total-summary">
                <h4 className="text-xl mb-2">{tr('totalBags')}</h4>
                <div className="bag-totals">
                  <Badge variant="secondary" className="bag-total">
                    {tr('regular')}: {totalBags.regular}
                  </Badge>
                  <Badge variant="secondary" className="bag-total">
                    {tr('bulk')}: {totalBags.bulk}
                  </Badge>
                  <Badge variant="secondary" className="bag-total">
                    {tr('specialty')}: {totalBags.specialty}
                  </Badge>
                </div>
              </div>
              
              <Separator className="my-6" />
              
              <div className="recipients-summary">
                <h4 className="text-xl mb-4">Recipients ({deliveryLines.length})</h4>
                {deliveryLines.map((line, index) => (
                  <div key={line.id} className="recipient-summary">
                    <div className="recipient-info">
                      <h5>{line.recipient_name}</h5>
                      {line.room_number && <p>Room {line.room_number}</p>}
                      {line.department && <p>{line.department}</p>}
                    </div>
                    <div className="recipient-bags">
                      <div className="bag-details">
                        <span>R: {line.bag_counts.regular}</span>
                        <span>B: {line.bag_counts.bulk}</span>
                        <span>S: {line.bag_counts.specialty}</span>
                      </div>
                      {line.photos.length > 0 && (
                        <Badge variant="outline">
                          <Camera className="w-4 h-4 mr-1" />
                          {line.photos.length}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="review-actions">
              <Button
                onClick={() => setCurrentStep('recipients')}
                variant="outline"
                size="lg"
                className="back-btn"
              >
                {tr('back')}
              </Button>
              
              <Button
                onClick={() => openBarcodeScanner('driver', 'Scan Delivery Barcodes', 'Scan barcode on each bag being delivered')}
                variant="outline"
                size="lg"
                className="scan-btn"
              >
                <Camera className="w-5 h-5 mr-2" />
                Scan Barcodes
              </Button>
              
              <Button
                onClick={() => setCurrentStep('signature')}
                size="lg"
                className="continue-btn"
              >
                <FileSignature className="w-5 h-5 mr-2" />
                {tr('signature')}
              </Button>
            </div>
            
            {/* Scanned Barcodes Display */}
            {scannedBarcodes.filter(scan => scan.type === 'driver').length > 0 && (
              <div className="scanned-barcodes mt-6">
                <h4 className="text-lg font-semibold mb-3">Scanned Barcodes ({scannedBarcodes.filter(scan => scan.type === 'driver').length})</h4>
                <div className="barcode-list">
                  {scannedBarcodes.filter(scan => scan.type === 'driver').map((scan, index) => (
                    <div key={index} className="barcode-item">
                      <Badge variant="outline" className="barcode-badge">
                        <Camera className="w-3 h-3 mr-1" />
                        {scan.barcode}
                      </Badge>
                      <span className="scan-time text-xs text-gray-500">
                        {new Date(scan.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderSignature = () => (
    <Card className="signature-card">
      <CardHeader>
        <CardTitle className="text-center text-3xl">
          <FileSignature className="w-8 h-8 mx-auto mb-4 text-blue-600" />
          {tr('signature')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="signature-content">
          <div className="receiver-info">
            <div className="form-group">
              <Label htmlFor="receiverName" className="text-xl">{tr('receiverName')} *</Label>
              <Input
                id="receiverName"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                className="receiver-input"
                placeholder="Enter receiver's full name"
              />
            </div>
            
            <div className="form-group">
              <Label htmlFor="receiverTitle" className="text-xl">{tr('receiverTitle')}</Label>
              <Input
                id="receiverTitle"
                value={receiverTitle}
                onChange={(e) => setReceiverTitle(e.target.value)}
                className="receiver-input"
                placeholder="Title/Position (optional)"
              />
            </div>
          </div>
          
          <div className="signature-section">
            <Label className="text-xl">{tr('signHere')}</Label>
            <div className="signature-container">
              <canvas
                ref={signatureCanvasRef}
                width={600}
                height={300}
                className="signature-canvas"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={(e) => {
                  e.preventDefault();
                  startDrawing(e);
                }}
                onTouchMove={(e) => {
                  e.preventDefault();
                  draw(e);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  stopDrawing();
                }}
              />
              <div className="signature-overlay">
                {!signatureData && (
                  <p className="signature-placeholder">
                    {tr('signHere')}
                  </p>
                )}
              </div>
            </div>
            
            <Button
              onClick={clearSignature}
              variant="outline"
              size="lg"
              className="clear-signature-btn"
            >
              {tr('clearSignature')}
            </Button>
          </div>
          
          <div className="signature-actions">
            <Button
              onClick={() => setCurrentStep('review')}
              variant="outline"
              size="lg"
              className="back-btn"
            >
              {tr('back')}
            </Button>
            <Button
              onClick={completeDelivery}
              disabled={loading || !signatureData || !receiverName}
              size="lg"
              className="complete-btn"
            >
              {loading ? tr('loading') : tr('completeDelivery')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderHotelDashboard = () => (
    <div className="facility-dashboard w-full">
      {/* Dashboard Header with Back Button */}
      <div className="dashboard-header mb-4 px-4 sm:px-0 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_manual-kiosk/artifacts/cavnmvm1_Sunlight%20Hospitality%20PNG.png"
              alt="Sunlight Hospitality Logo"
              className="w-10 h-auto mr-3 sm:w-12 sm:mr-4"
            />
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-800">{selectedHotel?.name}</h1>
              <p className="text-sm sm:text-base text-gray-600">{tr('facilityStaff')}: {user?.name}</p>
            </div>
          </div>
          <div className="header-actions flex gap-2 sm:gap-3">
            {/* Language Toggle */}
            <div className="flex space-x-1">
              <Button
                variant={language === 'en' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('en')}
                className="text-xs px-2 py-1 h-8"
              >
                <Globe className="w-3 h-3 mr-1" />
                EN
              </Button>
              <Button
                variant={language === 'es' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('es')}
                className="text-xs px-2 py-1 h-8"
              >
                <Globe className="w-3 h-3 mr-1" />
                ES
              </Button>
            </div>
            
            <Button
              onClick={() => loadPickupOrders(selectedHotel?.id)}
              variant="outline"
              size="sm"
              disabled={loading}
              className="flex-1 sm:flex-none"
            >
              <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
              <span className="sm:hidden">↻</span>
            </Button>
            <Button
              onClick={resetApp}
              variant="outline"
              size="sm"
              className="text-red-700 border-red-200 hover:bg-red-50 flex-1 sm:flex-none"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      <Card className="dashboard-card mobile-full-width w-full">
        <CardHeader className="px-4 py-4 sm:p-6">
          <CardTitle className="text-center text-3xl">
            <Building2 className="w-8 h-8 mx-auto mb-4 text-purple-600" />
            {tr('hotelDashboard')}
          </CardTitle>
          <div className="hotel-info">
            <Badge variant="outline" className="hotel-badge">
              <Building2 className="w-4 h-4 mr-2" />
              {selectedHotel?.name}
            </Badge>
            <Badge variant="outline" className="staff-badge">
              <User className="w-4 h-4 mr-2" />
              {user?.name}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tab Navigation */}
          <div className="tab-navigation">
            <Button
              onClick={() => setActiveTab('pickup')}
              variant={activeTab === 'pickup' ? 'default' : 'outline'}
              size="lg"
              className="tab-btn"
            >
              <Package className="w-5 h-5 mr-2" />
              {tr('pickup')}
            </Button>
            <Button
              onClick={() => {
                setActiveTab('gallery');
                if (selectedHotel?.id) {
                  loadFacilityDeliveryPhotos(selectedHotel.id);
                }
              }}
              variant={activeTab === 'gallery' ? 'default' : 'outline'}
              size="lg"
              className="tab-btn"
              data-testid="gallery-tab-btn"
            >
              <Camera className="w-5 h-5 mr-2" />
              {language === 'es' ? 'Galería' : 'Gallery'}
            </Button>
            <Button
              onClick={() => setActiveTab('message')}
              variant={activeTab === 'message' ? 'default' : 'outline'}
              size="lg"
              className="tab-btn"
            >
              <Mail className="w-5 h-5 mr-2" />
              {tr('message')}
            </Button>
            <Button
              onClick={() => setActiveTab('place-order')}
              variant={activeTab === 'place-order' ? 'default' : 'outline'}
              size="lg"
              className="tab-btn"
            >
              <FileSignature className="w-5 h-5 mr-2" />
              {tr('placeOrder')}
            </Button>
          </div>
          
          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'pickup' && renderPickupTab()}
            {activeTab === 'gallery' && renderGalleryTab()}
            {activeTab === 'message' && renderMessageTab()}
            {activeTab === 'place-order' && renderPlaceOrderTab()}
          </div>
          
          <div className="dashboard-footer">
            <Button
              onClick={resetApp}
              variant="outline"
              size="lg"
              className="back-btn"
            >
              {tr('back')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Order Delivery Photo Gallery Card - Organized by Date */}
      <Card className="delivery-photo-gallery-card mobile-full-width w-full mt-4 sm:mt-6" data-testid="delivery-photo-gallery-card">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-6 h-6 text-purple-600" />
              {language === 'es' ? 'Galería de Fotos de Entregas' : 'Order Delivery Photo Gallery'}
            </CardTitle>
            <Button
              onClick={() => loadFacilityDeliveryPhotos(selectedHotel?.id)}
              variant="outline"
              size="sm"
              disabled={galleryLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${galleryLoading ? 'animate-spin' : ''}`} />
              {language === 'es' ? 'Actualizar' : 'Refresh'}
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'es' 
              ? `Fotos de entregas para clientes de ${selectedHotel?.name}`
              : `Delivery photos for ${selectedHotel?.name} customers`
            }
          </p>
        </CardHeader>
        <CardContent>
          {galleryLoading ? (
            <div className="flex justify-center items-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-gray-600">
                {language === 'es' ? 'Cargando fotos...' : 'Loading photos...'}
              </span>
            </div>
          ) : facilityDeliveryPhotos.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">
                {language === 'es' ? 'No hay fotos de entregas todavía' : 'No delivery photos yet'}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {language === 'es' 
                  ? 'Las fotos aparecerán cuando los conductores confirmen entregas'
                  : 'Photos will appear when drivers confirm deliveries'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <Badge variant="outline" className="text-sm">
                  <Camera className="w-4 h-4 mr-1" />
                  {facilityDeliveryPhotos.length} {language === 'es' ? 'fotos' : 'photos'}
                </Badge>
              </div>
              
              {/* Photos Organized by Date */}
              <div className="space-y-3">
                {(() => {
                  // Group photos by date
                  const photosByDate = facilityDeliveryPhotos.reduce((acc, photo) => {
                    const dateKey = photo.delivered_at 
                      ? new Date(photo.delivered_at).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })
                      : language === 'es' ? 'Fecha desconocida' : 'Unknown Date';
                    if (!acc[dateKey]) {
                      acc[dateKey] = [];
                    }
                    acc[dateKey].push(photo);
                    return acc;
                  }, {});
                  
                  // Sort dates (most recent first)
                  const sortedDates = Object.keys(photosByDate).sort((a, b) => {
                    if (a === 'Unknown Date' || a === 'Fecha desconocida') return 1;
                    if (b === 'Unknown Date' || b === 'Fecha desconocida') return -1;
                    return new Date(b) - new Date(a);
                  });
                  
                  return sortedDates.map((dateKey) => {
                    const photos = photosByDate[dateKey];
                    const isExpanded = expandedGalleryDates.has(dateKey);
                    
                    return (
                      <div key={dateKey} className="border rounded-lg overflow-hidden">
                        {/* Date Header - Clickable */}
                        <button
                          onClick={() => {
                            const newExpanded = new Set(expandedGalleryDates);
                            if (isExpanded) {
                              newExpanded.delete(dateKey);
                            } else {
                              newExpanded.add(dateKey);
                            }
                            setExpandedGalleryDates(newExpanded);
                          }}
                          className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                          data-testid={`date-header-${dateKey}`}
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-purple-600" />
                            <span className="font-medium text-gray-800">{dateKey}</span>
                            <Badge variant="secondary" className="text-xs">
                              {photos.length} {photos.length === 1 
                                ? (language === 'es' ? 'foto' : 'photo') 
                                : (language === 'es' ? 'fotos' : 'photos')
                              }
                            </Badge>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {/* Photos Grid - Shown when expanded */}
                        {isExpanded && (
                          <div className="p-3 bg-white">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                              {photos.map((photoItem, index) => (
                                <div 
                                  key={photoItem.id || index}
                                  className="photo-gallery-item cursor-pointer rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                                  onClick={() => setSelectedGalleryPhoto(photoItem)}
                                  data-testid={`gallery-card-photo-${dateKey}-${index}`}
                                >
                                  <div className="aspect-square relative">
                                    <img
                                      src={photoItem.photo}
                                      alt={`Delivery for ${photoItem.customer_name}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <div className="p-2 bg-white">
                                    <p className="text-xs font-medium text-gray-800 truncate">
                                      {photoItem.customer_name}
                                    </p>
                                    {photoItem.delivered_by && (
                                      <p className="text-xs text-gray-400 truncate">
                                        {language === 'es' ? 'Por' : 'By'}: {photoItem.delivered_by}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Photo Lightbox Modal - Shared with gallery tab */}
      {selectedGalleryPhoto && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedGalleryPhoto(null)}
          data-testid="photo-lightbox-modal"
        >
          <div 
            className="bg-white rounded-lg max-w-3xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedGalleryPhoto.photo}
                alt={`Delivery for ${selectedGalleryPhoto.customer_name}`}
                className="max-w-full max-h-[70vh] object-contain"
              />
              <Button
                onClick={() => setSelectedGalleryPhoto(null)}
                variant="outline"
                size="sm"
                className="absolute top-2 right-2 bg-white/90"
              >
                ✕
              </Button>
            </div>
            <div className="p-4 border-t">
              <h4 className="font-bold text-lg">{selectedGalleryPhoto.customer_name}</h4>
              <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-gray-600">
                {selectedGalleryPhoto.room_number && (
                  <div>
                    <span className="font-medium">{language === 'es' ? 'Habitación:' : 'Room:'}</span> {selectedGalleryPhoto.room_number}
                  </div>
                )}
                {selectedGalleryPhoto.delivered_by && (
                  <div>
                    <span className="font-medium">{language === 'es' ? 'Entregado por:' : 'Delivered by:'}</span> {selectedGalleryPhoto.delivered_by}
                  </div>
                )}
                {selectedGalleryPhoto.delivered_at && (
                  <div>
                    <span className="font-medium">{language === 'es' ? 'Fecha:' : 'Date:'}</span> {new Date(selectedGalleryPhoto.delivered_at).toLocaleString()}
                  </div>
                )}
                {selectedGalleryPhoto.order_type && (
                  <div>
                    <span className="font-medium">{language === 'es' ? 'Tipo:' : 'Type:'}</span> {selectedGalleryPhoto.order_type}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPickupTab = () => {
    const orders = filteredPickupOrders || [];
    
    // Separate orders by status
    const awaitingPickupOrders = orders.filter(o => o.display_status === 'awaiting_pickup');
    const inTransitOrders = orders.filter(o => o.display_status === 'in_transit');
    const deliveredOrders = orders.filter(o => o.display_status === 'delivered');
    
    const renderOrderCard = (order) => (
      <div key={order.id} className="bg-white rounded-lg border p-3 mb-3 hover:shadow-sm transition-shadow">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 mb-2">
          <div>
            <h4 className="font-semibold text-gray-800">{order.guest_name}</h4>
            <div className="flex flex-wrap gap-1 mt-1">
              <Badge variant="secondary" className="text-xs">
                Room {order.room_number || 'N/A'}
              </Badge>
              <Badge 
                variant="outline"
                className={`text-xs ${
                  order.display_status === 'in_transit' 
                    ? 'border-orange-500 text-orange-700 bg-orange-50' 
                    : order.display_status === 'awaiting_pickup'
                    ? 'border-blue-500 text-blue-700 bg-blue-50'
                    : 'border-green-500 text-green-700 bg-green-50'
                }`}
              >
                {order.status_message || 'Ready'}
              </Badge>
              <span className="text-xs text-gray-500 flex items-center sm:hidden">
                <Clock className="w-3 h-3 mr-1" />
                {order.ready_date ? new Date(order.ready_date).toLocaleDateString() : 'Today'}
              </span>
            </div>
          </div>
          <div className="hidden sm:block text-right text-xs text-gray-500">
            <Clock className="w-3 h-3 inline mr-1" />
            {order.ready_date ? new Date(order.ready_date).toLocaleDateString() : 'Today'}
          </div>
        </div>
        
        <div className="text-sm text-gray-600 mb-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span><Package className="w-3 h-3 inline mr-1" />Bags: {
              (order.bag_counts?.regular || 0) + 
              (order.bag_counts?.bulk || 0) + 
              (order.bag_counts?.specialty || 0) || 1
            }</span>
            {order.items_description && order.items_description !== 'null' && (
              <span className="truncate text-xs">{order.items_description}</span>
            )}
          </div>
        </div>
        
        {/* Delivery photos preview */}
        {order.delivery_photos && order.delivery_photos.length > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-500">Photos:</span>
            {order.delivery_photos.slice(0, 2).map((photo, idx) => (
              <img key={idx} src={photo} alt="" className="w-8 h-8 rounded object-cover" />
            ))}
            {order.delivery_photos.length > 2 && (
              <span className="text-xs text-gray-400">+{order.delivery_photos.length - 2}</span>
            )}
          </div>
        )}
        
        <div>
          {order.display_status === 'in_transit' ? (
            <Button disabled size="sm" variant="outline" className="opacity-50 w-full">
              <Clock className="w-3 h-3 mr-1" />
              Awaiting Delivery
            </Button>
          ) : order.display_status === 'awaiting_pickup' ? (
            <Button disabled size="sm" variant="outline" className="opacity-50 w-full border-blue-300 text-blue-600">
              <Clock className="w-3 h-3 mr-1" />
              Awaiting Driver
            </Button>
          ) : (
            <Button
              onClick={() => {
                setSelectedOrderForPickup(order);
                setCurrentStep('confirm-pickup');
              }}
              size="sm"
              className="bg-green-600 hover:bg-green-700 w-full"
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              {tr('confirmPickup')}
            </Button>
          )}
        </div>
      </div>
    );
    
    return (
      <div className="pickup-tab">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder={language === 'es' ? "Buscar por nombre, habitación..." : "Search by name, room..."}
              value={facilitySearchQuery}
              onChange={(e) => handleFacilitySearch(e.target.value)}
              className="flex-1"
            />
            {facilitySearchQuery && (
              <Button
                onClick={() => {
                  setFacilitySearchQuery('');
                  setFilteredPickupOrders(pickupOrders);
                }}
                variant="outline"
                size="sm"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
        
        {orders.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
            <p className="text-gray-500">{language === 'es' ? 'No hay órdenes listas' : 'No orders ready for pickup'}</p>
          </div>
        ) : (
          <div className="space-y-4 w-full">
            {/* Awaiting Pickup by Driver Section */}
            {awaitingPickupOrders.length > 0 && (
              <div className="border rounded-lg overflow-hidden w-full shadow-sm">
                <button
                  onClick={() => setExpandedPickupSection(prev => prev === 'awaiting' ? null : 'awaiting')}
                  className="w-full flex items-center justify-between p-4 sm:p-5 bg-blue-50 hover:bg-blue-100 transition-colors"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                    <span className="font-semibold text-blue-800 text-sm sm:text-base">
                      {language === 'es' ? 'Esperando Recogida por Conductor' : 'Awaiting Pickup by Driver'}
                    </span>
                    <Badge className="bg-blue-600 text-white text-sm sm:text-base px-2 sm:px-3">
                      {awaitingPickupOrders.length}
                    </Badge>
                  </div>
                  <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 text-blue-600 transition-transform ${expandedPickupSection === 'awaiting' ? 'rotate-180' : ''}`} />
                </button>
                
                {expandedPickupSection === 'awaiting' && (
                  <div className="p-4 sm:p-5 bg-white max-h-96 overflow-y-auto">
                    {awaitingPickupOrders.map(renderOrderCard)}
                  </div>
                )}
              </div>
            )}
            
            {/* In Transit Section */}
            {inTransitOrders.length > 0 && (
              <div className="border rounded-lg overflow-hidden w-full shadow-sm">
                <button
                  onClick={() => setExpandedPickupSection(prev => prev === 'transit' ? null : 'transit')}
                  className="w-full flex items-center justify-between p-4 sm:p-5 bg-orange-50 hover:bg-orange-100 transition-colors"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                    <span className="font-semibold text-orange-800 text-sm sm:text-base">
                      {language === 'es' ? 'En Tránsito a Instalación' : 'In Transit to Facility'}
                    </span>
                    <Badge className="bg-orange-500 text-white text-sm sm:text-base px-2 sm:px-3">
                      {inTransitOrders.length}
                    </Badge>
                  </div>
                  <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 text-orange-600 transition-transform ${expandedPickupSection === 'transit' ? 'rotate-180' : ''}`} />
                </button>
                
                {expandedPickupSection === 'transit' && (
                  <div className="p-4 sm:p-5 bg-white max-h-96 overflow-y-auto">
                    {inTransitOrders.map(renderOrderCard)}
                  </div>
                )}
              </div>
            )}
            
            {/* Delivered Section */}
            {deliveredOrders.length > 0 && (
              <div className="border rounded-lg overflow-hidden w-full shadow-sm">
                <button
                  onClick={() => setExpandedPickupSection(prev => prev === 'delivered' ? null : 'delivered')}
                  className="w-full flex items-center justify-between p-4 sm:p-5 bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                    <span className="font-semibold text-green-800 text-sm sm:text-base">
                      {language === 'es' ? 'Entregado - Listo para Recoger por Participante' : 'Delivered - Ready for Participant Pickup'}
                    </span>
                    <Badge className="bg-green-600 text-white text-sm sm:text-base px-2 sm:px-3">
                      {deliveredOrders.length}
                    </Badge>
                  </div>
                  <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 text-green-600 transition-transform ${expandedPickupSection === 'delivered' ? 'rotate-180' : ''}`} />
                </button>
                
                {expandedPickupSection === 'delivered' && (
                  <div className="p-4 sm:p-5 bg-white max-h-96 overflow-y-auto">
                    {deliveredOrders.map(renderOrderCard)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render the delivery photo gallery tab for facility dashboard
  const renderGalleryTab = () => {
    return (
      <div className="gallery-tab" data-testid="gallery-tab-content">
        <div className="gallery-header flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold">
            {language === 'es' ? 'Fotos de Entregas' : 'Delivery Photos'}
          </h3>
          <Button
            onClick={() => loadFacilityDeliveryPhotos(selectedHotel?.id)}
            variant="outline"
            size="sm"
            disabled={galleryLoading}
            data-testid="refresh-gallery-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${galleryLoading ? 'animate-spin' : ''}`} />
            {language === 'es' ? 'Actualizar' : 'Refresh'}
          </Button>
        </div>
        
        <p className="text-gray-600 mb-4">
          {language === 'es' 
            ? `Fotos de entregas para clientes de ${selectedHotel?.name}`
            : `Delivery photos for customers at ${selectedHotel?.name}`
          }
        </p>

        {galleryLoading ? (
          <div className="flex justify-center items-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
            <span className="ml-3 text-gray-600">
              {language === 'es' ? 'Cargando fotos...' : 'Loading photos...'}
            </span>
          </div>
        ) : facilityDeliveryPhotos.length === 0 ? (
          <div className="no-photos text-center py-12">
            <Camera className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-xl text-gray-500">
              {language === 'es' ? 'No hay fotos de entregas todavía' : 'No delivery photos yet'}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {language === 'es' 
                ? 'Las fotos aparecerán aquí cuando los conductores confirmen las entregas'
                : 'Photos will appear here when drivers confirm deliveries'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Photo count badge */}
            <div className="mb-4">
              <Badge variant="outline" className="text-sm">
                <Camera className="w-4 h-4 mr-2" />
                {facilityDeliveryPhotos.length} {language === 'es' ? 'fotos' : 'photos'}
              </Badge>
            </div>
            
            {/* Photo Grid */}
            <div className="photo-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {facilityDeliveryPhotos.map((photoItem, index) => (
                <div 
                  key={photoItem.id || index}
                  className="photo-card bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => setSelectedGalleryPhoto(photoItem)}
                  data-testid={`gallery-photo-${index}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={photoItem.photo}
                      alt={`Delivery for ${photoItem.customer_name}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="p-2">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {photoItem.customer_name}
                    </p>
                    {photoItem.room_number && (
                      <p className="text-xs text-gray-500">
                        {language === 'es' ? 'Hab.' : 'Room'} {photoItem.room_number}
                      </p>
                    )}
                    {photoItem.delivered_at && (
                      <p className="text-xs text-gray-400">
                        {new Date(photoItem.delivered_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Photo Lightbox Modal */}
        {selectedGalleryPhoto && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedGalleryPhoto(null)}
            data-testid="photo-lightbox"
          >
            <div 
              className="bg-white rounded-lg max-w-3xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={selectedGalleryPhoto.photo}
                  alt={`Delivery for ${selectedGalleryPhoto.customer_name}`}
                  className="max-w-full max-h-[70vh] object-contain"
                />
                <Button
                  onClick={() => setSelectedGalleryPhoto(null)}
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2 bg-white/90"
                >
                  ✕
                </Button>
              </div>
              <div className="p-4 border-t">
                <h4 className="font-bold text-lg">{selectedGalleryPhoto.customer_name}</h4>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-gray-600">
                  {selectedGalleryPhoto.room_number && (
                    <div>
                      <span className="font-medium">{language === 'es' ? 'Habitación:' : 'Room:'}</span> {selectedGalleryPhoto.room_number}
                    </div>
                  )}
                  {selectedGalleryPhoto.delivered_by && (
                    <div>
                      <span className="font-medium">{language === 'es' ? 'Entregado por:' : 'Delivered by:'}</span> {selectedGalleryPhoto.delivered_by}
                    </div>
                  )}
                  {selectedGalleryPhoto.delivered_at && (
                    <div>
                      <span className="font-medium">{language === 'es' ? 'Fecha:' : 'Date:'}</span> {new Date(selectedGalleryPhoto.delivered_at).toLocaleString()}
                    </div>
                  )}
                  {selectedGalleryPhoto.order_type && (
                    <div>
                      <span className="font-medium">{language === 'es' ? 'Tipo:' : 'Type:'}</span> {selectedGalleryPhoto.order_type}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMessageTab = () => (
    <div className="message-tab">
      <h3 className="text-2xl mb-4">{tr('sendMessage')}</h3>
      
      <div className="message-form">
        <div className="form-group">
          <Label htmlFor="messageSubject" className="text-xl">{tr('messageSubject')} *</Label>
          <Input
            id="messageSubject"
            value={messageForm.subject}
            onChange={(e) => setMessageForm(prev => ({ ...prev, subject: e.target.value }))}
            className="message-input"
            placeholder="Enter message subject"
          />
        </div>
        
        <div className="form-group">
          <Label htmlFor="messagePriority" className="text-xl">{tr('priority')}</Label>
          <select
            id="messagePriority"
            value={messageForm.priority}
            onChange={(e) => setMessageForm(prev => ({ ...prev, priority: e.target.value }))}
            className="priority-select"
          >
            <option value="normal">{tr('normal')}</option>
            <option value="urgent">{tr('urgent')}</option>
          </select>
        </div>
        
        <div className="form-group">
          <Label htmlFor="messageContent" className="text-xl">{tr('messageContent')} *</Label>
          <Textarea
            id="messageContent"
            value={messageForm.message}
            onChange={(e) => setMessageForm(prev => ({ ...prev, message: e.target.value }))}
            className="message-textarea"
            placeholder="Enter your message to management..."
            rows={6}
          />
        </div>
        
        <Button
          onClick={sendMessage}
          disabled={loading || !messageForm.subject || !messageForm.message}
          size="lg"
          className="send-message-btn"
        >
          <Mail className="w-5 h-5 mr-2" />
          {loading ? tr('loading') : tr('sendMessage')}
        </Button>
      </div>
    </div>
  );

  const renderPlaceOrderTab = () => (
    <div className="place-order-tab">
      <h3 className="text-2xl mb-4">{tr('placeOrder')}</h3>
      
      <div className="order-form">
        <div className="form-row">
          <div className="form-group">
            <Label htmlFor="firstName" className="text-xl">{tr('firstName')} *</Label>
            <Input
              id="firstName"
              value={newOrderForm.firstName}
              onChange={(e) => setNewOrderForm(prev => ({ ...prev, firstName: e.target.value }))}
              className="order-input"
              placeholder="Enter first name"
            />
          </div>
          
          <div className="form-group">
            <Label htmlFor="lastName" className="text-xl">{tr('lastName')} *</Label>
            <Input
              id="lastName"
              value={newOrderForm.lastName}
              onChange={(e) => setNewOrderForm(prev => ({ ...prev, lastName: e.target.value }))}
              className="order-input"
              placeholder="Enter last name"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <Label htmlFor="roomNumber" className="text-xl">{tr('roomNumber')} *</Label>
            <Input
              id="roomNumber"
              value={newOrderForm.roomNumber}
              onChange={(e) => setNewOrderForm(prev => ({ ...prev, roomNumber: e.target.value }))}
              className="order-input"
              placeholder="Enter room number"
            />
          </div>
          
          <div className="form-group">
            <Label htmlFor="bedNumber" className="text-xl">{tr('bedNumber')}</Label>
            <Input
              id="bedNumber"
              value={newOrderForm.bedNumber}
              onChange={(e) => setNewOrderForm(prev => ({ ...prev, bedNumber: e.target.value }))}
              className="order-input"
              placeholder="Enter bed number (optional)"
            />
          </div>
        </div>
        
        <div className="form-group">
          <Label htmlFor="orderType" className="text-xl">{tr('orderType')} *</Label>
          <select
            id="orderType"
            value={newOrderForm.orderType}
            onChange={(e) => setNewOrderForm(prev => ({ ...prev, orderType: e.target.value }))}
            className="order-type-select"
          >
            <option value="clothes">{tr('clothes')}</option>
            <option value="linen">{tr('linen')}</option>
          </select>
        </div>
        
        <div className="form-group">
          <Label htmlFor="orderInstructions" className="text-xl">{tr('specialInstructions')}</Label>
          <Textarea
            id="orderInstructions"
            value={newOrderForm.specialInstructions}
            onChange={(e) => setNewOrderForm(prev => ({ ...prev, specialInstructions: e.target.value }))}
            className="order-textarea"
            placeholder="Any special instructions for this order..."
            rows={4}
          />
        </div>
        
        <Button
          onClick={placeNewOrder}
          disabled={loading || !newOrderForm.firstName || !newOrderForm.lastName || !newOrderForm.roomNumber}
          size="lg"
          className="submit-order-btn"
        >
          <FileSignature className="w-5 h-5 mr-2" />
          {loading ? tr('loading') : tr('submitOrder')}
        </Button>
      </div>
    </div>
  );

  const renderConfirmPickup = () => (
    <Card className="confirm-delivery-card">
      <CardHeader>
        <CardTitle className="text-center text-3xl">
          <CheckCircle className="w-8 h-8 mx-auto mb-4 text-green-600" />
          {tr('guestPickup')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="confirm-content">
          {selectedOrderForPickup && (
            <>
              <div className="delivery-summary">
                <h3 className="text-2xl mb-4">{selectedOrderForPickup.guest_name} - Room {selectedOrderForPickup.room_number}</h3>
                
                {/* Display Delivery Photos from Driver */}
                {(selectedOrderForPickup.delivery_photos && selectedOrderForPickup.delivery_photos.length > 0) || 
                 (selectedOrderForPickup.proof_photos && selectedOrderForPickup.proof_photos.length > 0) ? (
                  <div className="delivery-photos-section mb-6">
                    <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Camera className="w-5 h-5 text-purple-600" />
                      {language === 'es' ? 'Fotos de Entrega' : 'Delivery Photos'}
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {(selectedOrderForPickup.delivery_photos || selectedOrderForPickup.proof_photos || []).map((photo, idx) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border shadow-sm">
                          <img 
                            src={photo} 
                            alt={`Delivery photo ${idx + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(photo, '_blank')}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-photos-notice mb-6 p-4 bg-gray-50 rounded-lg border text-center">
                    <Camera className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500 text-sm">
                      {language === 'es' ? 'No hay fotos de entrega disponibles' : 'No delivery photos available'}
                    </p>
                  </div>
                )}
                
                {/* Display GPS Location from Delivery */}
                {selectedOrderForPickup.delivery_gps ? (
                  <div className="pickup-location-big mb-4">
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <MapPin className="w-8 h-8 text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-blue-600 font-medium mb-1">
                          {language === 'es' ? 'Coordenadas de Entrega' : 'Delivery Location'}
                        </p>
                        <p className="text-lg font-semibold text-blue-800">
                          {selectedOrderForPickup.delivery_gps.latitude?.toFixed(6)}, {selectedOrderForPickup.delivery_gps.longitude?.toFixed(6)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : selectedOrderForPickup.storage_location ? (
                  <div className="pickup-location-big mb-4">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border">
                      <MapPin className="w-8 h-8 text-red-500 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-500 font-medium mb-1">
                          {language === 'es' ? 'Ubicación de Almacenamiento' : 'Storage Location'}
                        </p>
                        <p className="text-lg font-semibold">{selectedOrderForPickup.storage_location}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="pickup-location-big mb-4">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border">
                      <MapPin className="w-8 h-8 text-gray-400 flex-shrink-0" />
                      <p className="text-gray-500">
                        {language === 'es' ? 'Ubicación no especificada' : 'Location not specified'}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="summary-info">
                  <div className="info-card">
                    <h4 className="text-xl mb-2">{tr('items')}</h4>
                    <p>{selectedOrderForPickup.items_description}</p>
                  </div>
                  
                  <div className="info-card">
                    <h4 className="text-xl mb-2">{tr('readyDate')}</h4>
                    <p>{new Date(selectedOrderForPickup.ready_date).toLocaleString()}</p>
                  </div>
                  
                  <div className="info-card">
                    <h4 className="text-xl mb-2">{tr('totalBags')}</h4>
                    <div className="bag-summary">
                      <Badge variant="secondary">R: {selectedOrderForPickup.bag_counts?.regular || 0}</Badge>
                      <Badge variant="secondary">B: {selectedOrderForPickup.bag_counts?.bulk || 0}</Badge>
                      <Badge variant="secondary">S: {selectedOrderForPickup.bag_counts?.specialty || 0}</Badge>
                    </div>
                  </div>
                </div>
                
                {selectedOrderForPickup.special_instructions && (
                  <div className="special-instructions">
                    <h4 className="text-xl mb-2">{tr('specialInstructions')}</h4>
                    <p className="instructions-text">{selectedOrderForPickup.special_instructions}</p>
                  </div>
                )}
              </div>
              
              <Separator className="my-6" />
              
              <div className="receiver-info">
                <div className="form-group">
                  <Label htmlFor="guestName" className="text-xl">{tr('guestName')} *</Label>
                  <Input
                    id="guestName"
                    value={receiverName}
                    onChange={(e) => setReceiverName(e.target.value)}
                    className="receiver-input"
                    placeholder="Confirm participant name for pickup"
                  />
                </div>
                
                <div className="form-group">
                  <Label htmlFor="staffName" className="text-xl">Staff Member *</Label>
                  <Input
                    id="staffName"
                    value={receiverTitle}
                    onChange={(e) => setReceiverTitle(e.target.value)}
                    className="receiver-input"
                    placeholder="Enter your name"
                  />
                </div>
              </div>
              
              <div className="image-attachment-section">
                <Label className="text-xl">{tr('attachImage')}</Label>
                <div className="image-upload">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePickupImageUpload}
                    className="image-input"
                    id="pickup-image-upload"
                  />
                  <Button
                    onClick={() => document.getElementById('pickup-image-upload').click()}
                    variant="outline"
                    size="lg"
                    className="image-btn"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    {tr('attachImage')}
                  </Button>
                  <p className="image-help">Attach photos of items being picked up (optional)</p>
                  
                  {pickupImages.length > 0 && (
                    <div className="image-preview">
                      {pickupImages.map((image, index) => (
                        <div key={index} className="image-preview-item">
                          <img
                            src={image}
                            alt={`Pickup photo ${index + 1}`}
                            className="image-thumbnail"
                          />
                          <Button
                            onClick={() => removePickupImage(index)}
                            variant="ghost"
                            size="sm"
                            className="remove-image-btn"
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="signature-section">
                <Label className="text-xl">Participant Signature *</Label>
                <div className="signature-container">
                  <canvas
                    ref={signatureCanvasRef}
                    width={600}
                    height={300}
                    className="signature-canvas"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      startDrawing(e);
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      draw(e);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      stopDrawing();
                    }}
                  />
                  <div className="signature-overlay">
                    {!signatureData && (
                      <p className="signature-placeholder">
                        Participant signature required for pickup
                      </p>
                    )}
                  </div>
                </div>
                
                <Button
                  onClick={clearSignature}
                  variant="outline"
                  size="lg"
                  className="clear-signature-btn"
                >
                  {tr('clearSignature')}
                </Button>
              </div>
              
              <div className="confirm-actions">
                <Button
                  onClick={() => setCurrentStep('facility-dashboard')}
                  variant="outline"
                  size="lg"
                  className="back-btn"
                >
                  {tr('back')}
                </Button>
                <Button
                  onClick={() => confirmGuestPickup(
                    selectedOrderForPickup.id,
                    signatureData,
                    receiverName,
                    receiverTitle
                  )}
                  disabled={loading || !signatureData || !receiverName || !receiverTitle}
                  size="lg"
                  className="confirm-btn"
                >
                  {loading ? tr('loading') : tr('confirmHandout')}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderSuccess = () => (
    <Card className="success-card">
      <CardHeader>
        <CardTitle className="text-center text-3xl">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
          {tr('deliveryComplete')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="success-content">
          <div className="success-info">
            <h3 className="text-2xl mb-4">{selectedHotel?.name}</h3>
            <div className="delivery-stats">
              <div className="stat">
                <Users className="w-6 h-6 text-blue-500" />
                <span>{deliveryLines.length} Recipients</span>
              </div>
              <div className="stat">
                <Package className="w-6 h-6 text-green-500" />
                <span>{getTotalBags().regular + getTotalBags().bulk + getTotalBags().specialty} Total Bags</span>
              </div>
              <div className="stat">
                <Clock className="w-6 h-6 text-purple-500" />
                <span>{new Date().toLocaleTimeString()}</span>
              </div>
            </div>
            
            <div className="receipt-info">
              <p className="text-lg">Delivery ID: {currentDelivery?.id}</p>
              <p>Driver: {user?.name}</p>
              <p>Receiver: {receiverName}</p>
              {receiverTitle && <p>Title: {receiverTitle}</p>}
            </div>
          </div>
          
          <Button
            onClick={resetApp}
            size="lg"
            className="start-new-btn"
          >
            <Truck className="w-5 h-5 mr-2" />
            {tr('startNew')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderManagerAuth = () => (
    <div className="auth-screen min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="auth-card shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-800">Manager Authentication</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="manager-pin" className="text-sm font-medium text-gray-600">
                {tr('enterPin')}
              </Label>
              <Input
                id="manager-pin"
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="text-center text-2xl tracking-wider"
                maxLength={4}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    authenticate(false, true);
                  }
                }}
              />
            </div>
            
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Button
                onClick={() => authenticate(false, true)}
                disabled={loading || pin.length !== 4}
                className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-medium"
              >
                {loading ? tr('loading') : tr('authenticate')}
              </Button>
              
              <Button
                onClick={() => setCurrentStep('welcome')}
                variant="outline"
                className="w-full"
              >
                {tr('back')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderManagerDashboard = () => {
    // Show facility details if selected
    if (selectedFacilityDetails) {
      return renderFacilityDetails();
    }

    // Show main dashboard
    return (
      <div className="manager-dashboard">
        <div className="dashboard-header flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="header-info flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_manual-kiosk/artifacts/cavnmvm1_Sunlight%20Hospitality%20PNG.png"
              alt="Sunlight Hospitality Logo"
              className="w-8 h-auto mr-2 sm:w-12 sm:mr-4"
            />
            <div>
              <h1 className="text-xl sm:text-3xl font-bold">{tr('operationsDashboard')}</h1>
              <p className="text-xs sm:text-base text-gray-600">{tr('sunlightHospitalityLaundry')}</p>
            </div>
          </div>
          
          <div className="header-actions flex flex-wrap items-center gap-1 sm:gap-2">
            {/* Language Toggle */}
            <div className="flex gap-1 mr-1 sm:mr-3">
              <Button
                variant={language === 'en' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('en')}
                className="text-xs px-1.5 py-0.5 h-7 sm:px-2 sm:py-1 sm:h-8"
              >
                <Globe className="w-3 h-3 mr-0.5 sm:mr-1" />
                EN
              </Button>
              <Button
                variant={language === 'es' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLanguage('es')}
                className="text-xs px-1.5 py-0.5 h-7 sm:px-2 sm:py-1 sm:h-8"
              >
                <Globe className="w-3 h-3 mr-0.5 sm:mr-1" />
                ES
              </Button>
            </div>
            
            <Button
              onClick={refreshManagerDashboard}
              variant="outline"
              size="sm"
              disabled={isRefreshing}
              className="refresh-btn text-xs px-2 py-0.5 h-7 sm:px-3 sm:h-8 mr-1 sm:mr-3"
            >
              <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh Now</span>
              <span className="sm:hidden">Refresh</span>
            </Button>
            <Button
              onClick={() => setCurrentStep('welcome')}
              variant="outline"
              size="sm"
              className="text-xs px-2 py-0.5 h-7 sm:px-3 sm:h-8"
            >
              Logout
            </Button>
          </div>
        </div>

        {managerDashboardData && (
          <div className="dashboard-content">
            {/* Statistics Overview */}
            <div className="stats-grid grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4">
              <Card className="stat-card">
                <CardContent className="stat-content p-3 sm:p-4">
                  <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold">{managerDashboardData.statistics?.total_facilities || 0}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Facilities</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="stat-card cursor-pointer hover:bg-orange-50 hover:border-orange-200 transition-colors"
                onClick={openPendingOrdersModal}
              >
                <CardContent className="stat-content p-3 sm:p-4">
                  <Package className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold">{managerDashboardData.statistics?.total_pending_orders || 0}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Pending</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="stat-card">
                <CardContent className="stat-content p-3 sm:p-4">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold">{managerDashboardData.statistics?.total_completed_pickups || 0}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Completed</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="stat-card">
                <CardContent className="stat-content p-3 sm:p-4">
                  <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold">{managerDashboardData.statistics?.total_deliveries || 0}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Deliveries</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tab Navigation - Desktop Enhanced */}
            <div className="tab-navigation mb-6">
              <div className="flex flex-wrap gap-2 p-2 bg-gray-100 rounded-xl lg:gap-3 lg:p-3">
                <button
                  onClick={() => setManagerActiveTab('overview')}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all lg:px-6 lg:py-3 lg:text-base ${
                    managerActiveTab === 'overview'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <LayoutDashboard className="w-4 h-4 mr-2 lg:w-5 lg:h-5" />
                  Overview
                </button>
                <button
                  onClick={() => setManagerActiveTab('orders')}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all lg:px-6 lg:py-3 lg:text-base ${
                    managerActiveTab === 'orders'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Package className="w-4 h-4 mr-2 lg:w-5 lg:h-5" />
                  Orders
                </button>
                <button
                  onClick={() => setManagerActiveTab('tasks')}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all lg:px-6 lg:py-3 lg:text-base ${
                    managerActiveTab === 'tasks'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <CheckCircle className="w-4 h-4 mr-2 lg:w-5 lg:h-5" />
                  Tasks
                </button>
                <button
                  onClick={() => setManagerActiveTab('customers')}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all lg:px-6 lg:py-3 lg:text-base ${
                    managerActiveTab === 'customers'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Users className="w-4 h-4 mr-2 lg:w-5 lg:h-5" />
                  Customers
                </button>
                <button
                  onClick={() => setManagerActiveTab('facilities')}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all lg:px-6 lg:py-3 lg:text-base ${
                    managerActiveTab === 'facilities'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Building2 className="w-4 h-4 mr-2 lg:w-5 lg:h-5" />
                  Facilities
                </button>
                <button
                  onClick={() => setManagerActiveTab('gallery')}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all lg:px-6 lg:py-3 lg:text-base ${
                    managerActiveTab === 'gallery'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Camera className="w-4 h-4 mr-2 lg:w-5 lg:h-5" />
                  Gallery
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {managerActiveTab === 'overview' && (
              <>
            {/* Customer Database Search - Quick Access */}
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <Search className="w-6 h-6 mr-2 text-green-600" />
                  Quick Customer Search
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="search-section">
                  <div className="search-input-container relative">
                    <input
                      type="text"
                      value={customerSearchQuery}
                      onChange={(e) => setCustomerSearchQuery(e.target.value)}
                      placeholder="Search customers by name, room, or address..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Button
                      onClick={() => searchCustomers(customerSearchQuery)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700"
                      disabled={loading || !customerSearchQuery.trim()}
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>
                  
                  {customerSearchResults.length > 0 && (
                    <div className="search-results mt-4 max-h-64 overflow-y-auto">
                      <p className="text-sm text-gray-600 mb-2">Found {customerSearchResults.length} customers</p>
                      {customerSearchResults.slice(0, 5).map((customer) => (
                        <div key={customer.id} className="customer-result p-3 border rounded-lg mb-2 hover:bg-gray-50 flex justify-between items-center">
                          <div>
                            <p className="font-semibold">{customer.name}</p>
                            <p className="text-sm text-gray-500">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {customer.address?.street} // {customer.address?.unit} // {customer.address?.city} // {customer.address?.state} {customer.address?.zip}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => createDeliveryStopFromCustomer(customer)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Create Order
                          </Button>
                        </div>
                      ))}
                      {customerSearchResults.length > 5 && (
                        <p className="text-sm text-gray-500 text-center mt-2">
                          Scroll to see all {customerSearchResults.length} results
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity Summary */}
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <Clock className="w-6 h-6 mr-2 text-purple-600" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800">Today's Orders</h4>
                    <p className="text-2xl font-bold text-blue-600">{pipelineStats?.total_orders || 0}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h4 className="font-semibold text-orange-800">In Transit</h4>
                    <p className="text-2xl font-bold text-orange-600">{pipelineStats?.in_transit || 0}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-800">Delivered Today</h4>
                    <p className="text-2xl font-bold text-green-600">{pipelineStats?.dropped_off || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
              </>
            )}

            {managerActiveTab === 'tasks' && (
              <>
            {/* Task Management System */}
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <CheckCircle className="w-6 h-6 mr-2 text-blue-600" />
                  Task Management System
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Task Management Actions */}
                <div className="task-actions mb-6">
                  <Button
                    onClick={() => setShowTemplateCreation(true)}
                    className="action-btn bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                  </Button>
                  
                  <Button
                    onClick={() => setShowCustomTaskCreation(true)}
                    className="action-btn bg-green-600 hover:bg-green-700 text-white ml-3"
                  >
                    <FileSignature className="w-4 h-4 mr-2" />
                    Create Task
                  </Button>
                  
                  <Button
                    onClick={() => setShowTaskCreation(true)}
                    variant="outline"
                    className="action-btn ml-3"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Assign from Template
                  </Button>
                  
                  <Button
                    onClick={loadManagerTasks}
                    variant="outline"
                    className="action-btn ml-3"
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    {tr('refreshTasks')}
                  </Button>
                </div>

                {/* Task Templates */}
                <div className="templates-section mb-6">
                  <h4 className="text-lg font-semibold mb-3">Weekly Task Templates ({(taskTemplates || []).length})</h4>
                  {(taskTemplates || []).length === 0 ? (
                    <div className="no-templates text-center py-4">
                      <FileSignature className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-gray-500">No task templates yet</p>
                      <p className="text-xs text-gray-400">Create day-based templates for weekly schedules</p>
                    </div>
                  ) : (
                    <div className="weekly-template-buttons">
                      {/* Horizontal Day Buttons */}
                      <div className="days-row flex flex-wrap gap-2 mb-4">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => {
                          const dayTemplates = (taskTemplates || []).filter(t => t.day_of_week === day);
                          const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                          const dayShort = dayName.substring(0, 3);
                          
                          return (
                            <div key={day} className="day-button-container relative">
                              <div className="dropdown relative">
                                <button
                                  className={`day-button px-4 py-3 rounded-lg border-2 font-semibold text-sm transition-all hover:shadow-md ${
                                    dayTemplates.length > 0 
                                      ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100' 
                                      : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-gray-100'
                                  }`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const dropdown = e.target.closest('.dropdown').querySelector('.dropdown-menu');
                                    const allDropdowns = document.querySelectorAll('.dropdown-menu');
                                    
                                    // Close all other dropdowns
                                    allDropdowns.forEach(menu => {
                                      if (menu !== dropdown) {
                                        menu.classList.add('hidden');
                                      }
                                    });
                                    
                                    // Toggle current dropdown
                                    dropdown.classList.toggle('hidden');
                                  }}
                                >
                                  <div className="text-center">
                                    <div className="font-bold">{dayShort}</div>
                                    <div className="text-xs mt-1">
                                      {dayTemplates.length} template{dayTemplates.length !== 1 ? 's' : ''}
                                    </div>
                                  </div>
                                </button>
                                
                                {/* Dropdown Menu */}
                                <div className="dropdown-menu hidden absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                  <div className="p-3">
                                    <div className="flex items-center justify-between mb-3 pb-2 border-b">
                                      <h5 className="font-semibold text-gray-800">{dayName} Templates</h5>
                                      {dayTemplates.length > 0 && (
                                        <Button
                                          onClick={() => {
                                            try {
                                              console.log('Assign All clicked for day:', dayName);
                                              console.log('Templates to assign:', dayTemplates);
                                              console.log('Available drivers:', availableDrivers);
                                              
                                              // Open driver selection for all templates for this day
                                              const assignmentData = { 
                                                id: 'bulk', 
                                                templates: dayTemplates,
                                                day: dayName 
                                              };
                                              
                                              console.log('Setting assignment data:', assignmentData);
                                              setSelectedTemplateForAssignment(assignmentData);
                                              
                                              console.log('Opening driver selection modal...');
                                              setShowDriverSelection(true);
                                              
                                              // Close dropdown
                                              document.querySelectorAll('.dropdown-menu').forEach(menu => menu.classList.add('hidden'));
                                              
                                              console.log('Assign All setup complete');
                                            } catch (error) {
                                              console.error('Error in Assign All:', error);
                                            }
                                          }}
                                          size="sm"
                                          className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1"
                                        >
                                          Assign All
                                        </Button>
                                      )}
                                    </div>
                                    
                                    {dayTemplates.length === 0 ? (
                                      <div className="text-center py-4">
                                        <p className="text-sm text-gray-500">No templates for {dayName}</p>
                                        <p className="text-xs text-gray-400 mt-1">Create a template using the button above</p>
                                      </div>
                                    ) : (
                                      <div className="templates-list space-y-2 max-h-60 overflow-y-auto">
                                        {dayTemplates.map((template) => (
                                          <div key={template.id} className="template-card bg-gray-50 rounded-md p-3 border">
                                            <div className="flex justify-between items-start mb-2">
                                              <div className="flex-1">
                                                <h6 className="font-medium text-sm text-gray-800">{template.title}</h6>
                                                <p className="text-xs text-gray-600 mt-1">{template.description}</p>
                                              </div>
                                              <div className="flex space-x-1">
                                                <Button
                                                  onClick={() => {
                                                    // Open driver selection for this template
                                                    setSelectedTemplateForAssignment(template);
                                                    setShowDriverSelection(true);
                                                    // Close dropdown
                                                    document.querySelectorAll('.dropdown-menu').forEach(menu => menu.classList.add('hidden'));
                                                  }}
                                                  size="sm"
                                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1"
                                                >
                                                  Assign
                                                </Button>
                                                <Button
                                                  onClick={() => {
                                                    try {
                                                      console.log('Edit template clicked:', template);
                                                      
                                                      // Set template for editing with proper data structure
                                                      // Convert global checklist items back to location-specific for UI
                                                      const processedLocations = (template.locations || []).map(location => ({
                                                        ...location,
                                                        checklist_items: [{ text: '', requiresPhoto: false }] // Start with empty checklist for each location
                                                      }));
                                                      
                                                      // If no locations, create a default one
                                                      if (processedLocations.length === 0) {
                                                        processedLocations.push({
                                                          hotel_id: '',
                                                          hotel_name: '',
                                                          task_type: 'both',
                                                          scheduled_time: '',
                                                          notes: '',
                                                          checklist_items: [{ text: '', requiresPhoto: false }]
                                                        });
                                                      }
                                                      
                                                      // Parse global checklist items and distribute to correct locations
                                                      if (template.checklist_items && template.checklist_items.length > 0) {
                                                        template.checklist_items.forEach(item => {
                                                          // Check if item has hotel prefix (e.g., "Saint Andrews Community Housing: Pick Up Clothes")
                                                          const colonIndex = item.indexOf(': ');
                                                          if (colonIndex > 0) {
                                                            const hotelPrefix = item.substring(0, colonIndex);
                                                            const taskText = item.substring(colonIndex + 2);
                                                            
                                                            // Find the matching location by hotel name
                                                            const matchingLocation = processedLocations.find(loc => {
                                                              const locationHotelName = hotels.find(h => h.id === loc.hotel_id)?.name;
                                                              return locationHotelName === hotelPrefix;
                                                            });
                                                            
                                                            if (matchingLocation) {
                                                              // Initialize checklist_items if it's just the default empty array
                                                              if (matchingLocation.checklist_items.length === 1 && matchingLocation.checklist_items[0] === '') {
                                                                matchingLocation.checklist_items = [];
                                                              }
                                                              matchingLocation.checklist_items.push(taskText);
                                                            }
                                                          } else {
                                                            // Item without prefix - add to first location as fallback
                                                            if (processedLocations.length > 0) {
                                                              if (processedLocations[0].checklist_items.length === 1 && processedLocations[0].checklist_items[0] === '') {
                                                                processedLocations[0].checklist_items = [];
                                                              }
                                                              processedLocations[0].checklist_items.push(item);
                                                            }
                                                          }
                                                        });
                                                        
                                                        // Ensure each location has at least one empty item for adding new tasks
                                                        processedLocations.forEach(location => {
                                                          if (location.checklist_items.length === 0) {
                                                            location.checklist_items = [''];
                                                          }
                                                        });
                                                      }
                                                      
                                                      const editTemplate = {
                                                        ...template,
                                                        id: template.id,
                                                        locations: processedLocations,
                                                        // Remove old global checklist_items
                                                        // Ensure required fields have defaults
                                                        day_of_week: template.day_of_week || 'monday',
                                                        assignable_to_roles: template.assignable_to_roles || ['driver'],
                                                        priority: template.priority || 'normal',
                                                        estimated_duration: template.estimated_duration || 30,
                                                        required_photos: template.required_photos || 1
                                                      };
                                                      
                                                      console.log('🔧 Setting template for editing:', editTemplate);
                                                      console.log('🔧 Processed locations with checklists:', 
                                                        processedLocations.map(loc => ({
                                                          hotel_name: hotels.find(h => h.id === loc.hotel_id)?.name,
                                                          checklist_items: loc.checklist_items
                                                        }))
                                                      );
                                                      setNewTemplate(editTemplate);
                                                      
                                                      console.log('Opening template creation modal...');
                                                      setShowTemplateCreation(true);
                                                      
                                                      // Close dropdown
                                                      document.querySelectorAll('.dropdown-menu').forEach(menu => menu.classList.add('hidden'));
                                                      
                                                      console.log('Template edit setup complete');
                                                    } catch (error) {
                                                      console.error('Error in template edit:', error);
                                                    }
                                                  }}
                                                  size="sm"
                                                  variant="outline"
                                                  className="text-xs px-2 py-1 border-gray-300"
                                                >
                                                  <Edit className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                  onClick={async () => {
                                                    if (confirm(`Delete template "${template.title}"?`)) {
                                                      try {
                                                        const response = await api.delete(`/api/task-templates/${template.id}`);
                                                        if (response.data.success) {
                                                          setSuccess('Template deleted successfully!');
                                                          await loadTaskTemplates(selectedHotel?.id || user?.facility_id);
                                                          setTimeout(() => setSuccess(''), 3000);
                                                        }
                                                      } catch (error) {
                                                        setError('Failed to delete template');
                                                        setTimeout(() => setError(''), 3000);
                                                      }
                                                    }
                                                    // Close dropdown
                                                    document.querySelectorAll('.dropdown-menu').forEach(menu => menu.classList.add('hidden'));
                                                  }}
                                                  size="sm"
                                                  variant="outline"
                                                  className="text-xs px-2 py-1 text-red-600 border-red-200 hover:bg-red-50"
                                                >
                                                  <Trash2 className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            </div>
                                            
                                            {/* Location badges */}
                                            {template.locations && template.locations.length > 0 && (
                                              <div className="locations-preview mb-2">
                                                <div className="flex flex-wrap gap-1">
                                                  {template.locations.map((location, idx) => (
                                                    <Badge key={idx} variant="outline" className="text-xs bg-white">
                                                      {location.hotel_name || `Hotel ${idx + 1}`}
                                                      {location.scheduled_time && (
                                                        <span className="ml-1 text-blue-600">@ {location.scheduled_time}</span>
                                                      )}
                                                      <span className="ml-1 text-gray-500">
                                                        ({location.task_type === 'both' ? 'P&D' : 
                                                          location.task_type === 'pickup' ? 'P' : 'D'})
                                                      </span>
                                                    </Badge>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            
                                            {/* Template stats */}
                                            <div className="template-stats flex justify-between items-center text-xs">
                                              <div className="flex space-x-2">
                                                <Badge variant="outline" className="text-xs">
                                                  {template.checklist_items?.length || 0} steps
                                                </Badge>
                                                <Badge variant="secondary" className="text-xs">
                                                  {template.priority}
                                                </Badge>
                                                {template.estimated_duration && (
                                                  <Badge variant="outline" className="text-xs">
                                                    {template.estimated_duration}min
                                                  </Badge>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Summary stats */}
                      <div className="template-summary mt-4 p-3 bg-blue-50 rounded-lg">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-blue-700 font-medium">Weekly Template Summary</span>
                          <div className="flex space-x-4 text-blue-600">
                            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => {
                              const dayTemplates = (taskTemplates || []).filter(t => t.day_of_week === day);
                              const dayShort = day.substring(0, 3).toUpperCase();
                              return (
                                <span key={day} className="text-xs">
                                  {dayShort}: {dayTemplates.length}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* GPS Map Visualization - REMOVED FOR PERFORMANCE 
            <Card className="gps-map-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <MapPin className="w-6 h-6 mr-2 text-green-600" />
                  Real-Time Driver Tracking
                </CardTitle>
                <p className="text-sm text-gray-600">Live GPS tracking of drivers and delivery locations</p>
              </CardHeader>
              <CardContent>
                <div className="map-content">
                  <div className="map-controls mb-4">
                    <div className="flex justify-between items-center">
                      <div className="map-info">
                        <span className="text-sm text-gray-600">
                          GPS Tracking Active
                        </span>
                      </div>
                      <div className="map-actions flex space-x-2">
                        <Button
                          onClick={loadPipelineStats}
                          variant="outline"
                          size="sm"
                          disabled={loading}
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="map-container">
                    <DriverMap
                      driverLocations={driverLocations}
                      facilities={managerDashboardData?.facilities || []}
                      routeHistory={routeHistory}
                      selectedDriver={selectedDriver}
                      onDriverSelect={(driver) => {
                        setSelectedDriver(driver);
                        loadRouteHistory(driver.driver_id);
                      }}
                      height="400px"
                    />
                  </div>
                  
                  <div className="driver-status-section mt-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-lg font-semibold">Active Drivers</h4>
                      <span className="text-sm text-gray-600">
                        {(driverLocations || []).length} driver{(driverLocations || []).length !== 1 ? 's' : ''} online
                      </span>
                    </div>
                    <div className="driver-status-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {(driverLocations || []).map((driver) => (
                        <div 
                          key={driver.id}
                          className={`driver-status-card bg-white border rounded-lg p-3 cursor-pointer transition-all ${
                            selectedDriver?.id === driver.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => {
                            setSelectedDriver(driver);
                            loadRouteHistory(driver.driver_id);
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`driver-status-indicator w-3 h-3 rounded-full ${
                              driver.status === 'active' ? 'bg-green-500' : 
                              driver.status === 'delivering' ? 'bg-blue-500' : 'bg-yellow-500'
                            }`}></div>
                            <div className="flex-1">
                              <h5 className="font-medium text-sm">{driver.driver_name}</h5>
                              <p className="text-xs text-gray-500 capitalize">{driver.status}</p>
                            </div>
                          </div>
                          <div className="driver-details mt-2 text-xs text-gray-600">
                            <p>Last Update: {new Date(driver.last_updated).toLocaleTimeString()}</p>
                            {driver.speed > 0 && (
                              <p>Speed: {driver.speed.toFixed(1)} km/h</p>
                            )}
                            {driver.current_task_id && (
                              <p>Task: {driver.current_task_id.slice(-6)}</p>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {(driverLocations || []).length === 0 && (
                        <div className="col-span-full text-center py-8">
                          <Truck className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p className="text-gray-500">No drivers online</p>
                          <p className="text-xs text-gray-400">Driver locations will appear when drivers are active</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            */}

            {/* Customer Database Search */}
            {/* All Tasks - Unified View */}
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <CheckCircle className="w-6 h-6 mr-2 text-blue-600" />
                  All Tasks ({(managerTasks || []).length + (driverCreatedTasks || []).length})
                </CardTitle>
                <p className="text-sm text-gray-600">Manager-created and driver-created tasks</p>
              </CardHeader>
              <CardContent>
                {/* Overall Progress Bar for All Tasks */}
                {((managerTasks || []).length + (driverCreatedTasks || []).length) > 0 && (
                  <div className="overall-progress mb-6">
                    <div className="progress-header mb-2">
                      <h5 className="font-medium text-gray-700">Overall Progress</h5>
                      <span className="text-sm text-gray-500">
                        {[...(managerTasks || []).filter(task => task.status === 'completed' || task.status === 'approved'), 
                          ...(driverCreatedTasks || []).filter(task => task.delivery_status === 'delivered')].length} of {(managerTasks || []).length + (driverCreatedTasks || []).length} tasks completed
                      </span>
                    </div>
                    <div className="progress-bar bg-gray-200 rounded h-4">
                      <div 
                        className="progress-fill bg-green-500 h-4 rounded"
                        style={{
                          width: `${((managerTasks || []).length + (driverCreatedTasks || []).length) > 0 ? 
                            ([...(managerTasks || []).filter(task => task.status === 'completed' || task.status === 'approved'), 
                              ...(driverCreatedTasks || []).filter(task => task.delivery_status === 'delivered')].length / 
                             ((managerTasks || []).length + (driverCreatedTasks || []).length)) * 100 : 
                            0}%`
                        }}
                      />
                    </div>
                    <p className="text-lg text-green-600 font-medium mt-2">
                      {((managerTasks || []).length + (driverCreatedTasks || []).length) > 0 ? 
                        `${Math.round(([...(managerTasks || []).filter(task => task.status === 'completed' || task.status === 'approved'), 
                          ...(driverCreatedTasks || []).filter(task => task.delivery_status === 'delivered')].length / 
                         ((managerTasks || []).length + (driverCreatedTasks || []).length)) * 100)}% complete` :
                        'No tasks available'
                      }
                    </p>
                  </div>
                )}

                {/* Manager Tasks Section */}
                {(managerTasks || []).length > 0 && (
                  <div className="manager-tasks-subsection mb-6">
                    <h5 className="font-semibold text-gray-700 mb-3">Manager-Created Tasks ({(managerTasks || []).length})</h5>
                    <div className="manager-tasks-list max-h-60 overflow-y-auto space-y-2">
                      {(managerTasks || []).map((task) => (
                        <Card key={task.id} className="manager-task-item border-l-4 border-green-500">
                          <CardContent className="p-3">
                            <div className="flex justify-between items-start">
                              <div className="task-details flex-1">
                                <h6 className="font-semibold text-sm text-green-700">{task.title}</h6>
                                <p className="text-xs text-gray-600 mb-1">{task.description}</p>
                                <div className="task-meta">
                                  <Badge variant="outline" className="text-xs mr-1">
                                    Status: {task.status}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs">
                                    Priority: {task.priority}
                                  </Badge>
                                </div>
                              </div>
                              <div className="task-actions flex gap-1">
                                <Button
                                  onClick={() => {
                                    setSelectedTask(task);
                                    setShowTaskDetailsModal(true);
                                  }}
                                  size="sm"
                                  variant="outline"
                                  className="text-xs"
                                >
                                  View
                                </Button>
                                <Button
                                  onClick={() => openTaskEditModal(task)}
                                  size="sm"
                                  variant="outline"
                                  className="text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  onClick={() => openTaskDeleteModal(task)}
                                  size="sm"
                                  variant="outline"
                                  className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Driver Tasks Section */}
                {(() => {
                  return (driverCreatedTasks || []).length > 0;
                })() && (
                  <div className="driver-tasks-subsection">
                    <h5 className="font-semibold text-gray-700 mb-3">Driver-Created Tasks ({(driverCreatedTasks || []).length})</h5>
                    <div className="driver-tasks-list max-h-60 overflow-y-auto space-y-2">
                      {(driverCreatedTasks || []).map((task, index) => (
                        <Card key={task.id} className="driver-task-item border-l-4 border-blue-500">
                          <CardContent className="p-3">
                            <div className="flex justify-between items-start">
                              <div className="task-details flex-1">
                                <h6 className="font-semibold text-sm text-blue-700">
                                  {task.customer_name || task.guest_name}
                                </h6>
                                <p className="text-xs text-gray-600">
                                  Room: {task.room_number} • Status: {task.delivery_status}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Created: {new Date(task.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="task-actions flex items-center gap-1">
                                <Badge 
                                  variant={task.delivery_status === 'delivered' ? 'default' : 'secondary'}
                                  className="text-xs mr-2"
                                >
                                  {task.delivery_status === 'delivered' ? 'Complete' : 'Pending'}
                                </Badge>
                                <button
                                  onClick={() => openDriverTaskEditModal(task)}
                                  className="p-1 text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
                                  title="Edit task"
                                >
                                  <Edit className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => openDriverTaskDeleteModal(task)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded border border-red-200"
                                  title="Delete task"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Tasks State */}
                {((managerTasks || []).length === 0 && (driverCreatedTasks || []).length === 0) && (
                  <div className="no-tasks text-center py-8">
                    <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 text-lg">No Tasks</p>
                    <p className="text-gray-400">Create tasks or wait for drivers to create delivery stops</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Completed Tasks with Images Section */}
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
                  Completed Tasks with Images
                </CardTitle>
                <p className="text-sm text-gray-600">View completed tasks and their attached proof images</p>
              </CardHeader>
              <CardContent>
                {/* Completed Manager Tasks */}
                {(managerTasks || []).filter(task => task.status === 'completed' || task.status === 'approved').length > 0 ? (
                  <div className="completed-manager-tasks mb-6">
                    <h5 className="font-semibold text-gray-700 mb-3 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                      Completed Manager Tasks ({(managerTasks || []).filter(task => task.status === 'completed' || task.status === 'approved').length})
                    </h5>
                    <div className="completed-tasks-grid grid gap-4">
                      {(managerTasks || []).filter(task => task.status === 'completed' || task.status === 'approved').map((task) => (
                        <Card key={task.id} className="completed-task-card border-l-4 border-green-500 hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="task-header mb-3">
                              <h6 className="font-semibold text-green-700 text-lg">{task.title}</h6>
                              <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                              <div className="task-meta flex flex-wrap gap-2 mb-3">
                                <Badge className="bg-green-100 text-green-800">✓ Completed</Badge>
                                <Badge variant="outline">Priority: {task.priority}</Badge>
                                {task.completed_at && (
                                  <Badge variant="outline">
                                    Completed: {new Date(task.completed_at).toLocaleDateString()}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            {/* Task Images */}
                            {task.checklist_items && task.checklist_items.length > 0 ? (
                              <div className="task-images">
                                <h6 className="font-medium text-gray-700 mb-2 flex items-center">
                                  <Camera className="w-4 h-4 mr-1" />
                                  {tr('proofImages')}
                                </h6>
                                <div className="checklist-items space-y-3">
                                  {task.checklist_items.map((item, index) => (
                                    item.proof_photos && item.proof_photos.length > 0 ? (
                                      <div key={index} className="checklist-item-images bg-gray-50 p-3 rounded-lg">
                                        <div className="mb-2">
                                          <p className="text-sm font-medium text-gray-700">
                                            ✓ {item.text}
                                          </p>
                                          {item.completed_at && (
                                            <p className="text-xs text-gray-500 mt-1">
                                              <Clock className="w-3 h-3 inline mr-1" />
                                              {tr('completedAt')} {formatCompletionTime(item.completed_at)}
                                            </p>
                                          )}
                                        </div>
                                        <div className="images-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                          {item.proof_photos.map((photo, photoIndex) => (
                                            <div key={photoIndex} className="image-container relative group">
                                              <img
                                                src={photo}
                                                alt={`Proof ${photoIndex + 1}`}
                                                className="w-full h-20 object-cover rounded cursor-pointer border hover:border-blue-500 transition-colors"
                                                onClick={() => {
                                                  const modal = document.createElement('div');
                                                  modal.className = 'fixed inset-0 z-[9999] bg-black bg-opacity-75 flex items-center justify-center p-4';
                                                  modal.innerHTML = `
                                                    <div class="relative max-w-4xl max-h-full">
                                                      <img src="${photo}" class="max-w-full max-h-full object-contain rounded" />
                                                      <button class="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors" onclick="this.parentElement.parentElement.remove()">×</button>
                                                      <div class="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white p-2 rounded">${item.text} - Image ${photoIndex + 1}</div>
                                                    </div>
                                                  `;
                                                  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
                                                  document.body.appendChild(modal);
                                                }}
                                              />
                                              <button
                                                className="absolute top-1 right-1 bg-blue-600 hover:bg-blue-700 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const link = document.createElement('a');
                                                  link.href = photo;
                                                  link.download = `${task.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${item.text.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${photoIndex + 1}.jpg`;
                                                  document.body.appendChild(link);
                                                  link.click();
                                                  document.body.removeChild(link);
                                                }}
                                                title="Download image"
                                              >
                                                <Download className="w-3 h-3" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ) : null
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="no-images text-center py-4 text-gray-500">
                                <Camera className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm">No proof images attached</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Completed Driver Tasks */}
                {(driverCreatedTasks || []).filter(task => task.delivery_status === 'delivered').length > 0 ? (
                  <div className="completed-driver-tasks mb-6">
                    <h5 className="font-semibold text-gray-700 mb-3 flex items-center">
                      <Truck className="w-5 h-5 mr-2 text-blue-600" />
                      Completed Driver Tasks ({(driverCreatedTasks || []).filter(task => task.delivery_status === 'delivered').length})
                    </h5>
                    <div className="completed-tasks-grid grid gap-4">
                      {(driverCreatedTasks || []).filter(task => task.delivery_status === 'delivered').map((task) => (
                        <Card key={task.id} className="completed-task-card border-l-4 border-blue-500 hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="task-header mb-3">
                              <h6 className="font-semibold text-blue-700 text-lg">
                                {task.customer_name || task.guest_name}
                              </h6>
                              <p className="text-sm text-gray-600">
                                Room: {task.room_number} • {task.items_description}
                              </p>
                              <div className="task-meta flex flex-wrap gap-2 mb-3">
                                <Badge className="bg-blue-100 text-blue-800">✓ Delivered</Badge>
                                {task.delivered_at && (
                                  <Badge variant="outline">
                                    Delivered: {new Date(task.delivered_at).toLocaleDateString()}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Delivery Images */}
                            {task.delivery_photos && task.delivery_photos.length > 0 ? (
                              <div className="delivery-images">
                                <h6 className="font-medium text-gray-700 mb-2 flex items-center">
                                  <Camera className="w-4 h-4 mr-1" />
                                  Delivery Photos ({task.delivery_photos.length})
                                </h6>
                                <div className="images-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                  {task.delivery_photos.map((photo, photoIndex) => (
                                    <div key={photoIndex} className="image-container relative group">
                                      <img
                                        src={photo}
                                        alt={`Delivery photo ${photoIndex + 1}`}
                                        className="w-full h-20 object-cover rounded cursor-pointer border hover:border-blue-500 transition-colors"
                                        onClick={() => {
                                          const modal = document.createElement('div');
                                          modal.className = 'fixed inset-0 z-[9999] bg-black bg-opacity-75 flex items-center justify-center p-4';
                                          modal.innerHTML = `
                                            <div class="relative max-w-4xl max-h-full">
                                              <img src="${photo}" class="max-w-full max-h-full object-contain rounded" />
                                              <button class="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors" onclick="this.parentElement.parentElement.remove()">×</button>
                                              <div class="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white p-2 rounded">Delivery Photo ${photoIndex + 1} - ${task.customer_name || task.guest_name}</div>
                                            </div>
                                          `;
                                          modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
                                          document.body.appendChild(modal);
                                        }}
                                      />
                                      <button
                                        className="absolute top-1 right-1 bg-blue-600 hover:bg-blue-700 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const link = document.createElement('a');
                                          link.href = photo;
                                          link.download = `delivery_${(task.customer_name || task.guest_name).replace(/[^a-z0-9]/gi, '_').toLowerCase()}_photo_${photoIndex + 1}.jpg`;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        }}
                                        title="Download image"
                                      >
                                        <Download className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="no-images text-center py-4 text-gray-500">
                                <Camera className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm">No delivery photos attached</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* No Completed Tasks State */}
                {((managerTasks || []).filter(task => task.status === 'completed' || task.status === 'approved').length === 0 && 
                  (driverCreatedTasks || []).filter(task => task.delivery_status === 'delivered').length === 0) && (
                  <div className="no-completed-tasks text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 text-lg">No Completed Tasks</p>
                    <p className="text-gray-400">Completed tasks with images will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
              </>
            )}

            {managerActiveTab === 'customers' && (
              <>
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <Search className="w-6 h-6 mr-2 text-blue-600" />
                  Customer Database Search
                </CardTitle>
                <p className="text-sm text-gray-600">Search customers to create delivery orders</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="search-input-group">
                    <Input
                      type="text"
                      placeholder="Type customer name, phone, or address..."
                      value={customerSearchQuery}
                      onChange={(e) => handleLiveSearch(e.target.value)}
                      className="w-full"
                    />
                    <Button
                      onClick={() => searchCustomers(customerSearchQuery)}
                      disabled={loading || !customerSearchQuery.trim()}
                      className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                    
                    <Button
                      onClick={() => setShowAddCustomerModal(true)}
                      disabled={loading}
                      className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add New Customer
                    </Button>
                  </div>
                  
                  {/* Search Results */}
                  {customerSearchResults.length > 0 && (
                    <div className="search-results">
                      <h5 className="font-semibold mb-2">
                        Found {customerSearchResults.length} customers
                      </h5>
                      <div className="customers-list max-h-80 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                        {customerSearchResults.map((customer) => (
                          <div key={customer.id} className="customer-item p-3 border rounded-lg hover:bg-blue-50 bg-white">
                            <div className="flex justify-between items-start">
                              <div className="customer-info flex-1">
                                <h6 className="font-medium text-sm">{customer.name}</h6>
                                {customer.phone && (
                                  <p className="text-xs text-gray-600">
                                    <Phone className="w-3 h-3 inline mr-1" />
                                    {customer.phone}
                                  </p>
                                )}
                                {customer.address && (
                                  <p className="text-xs text-gray-600 truncate">
                                    <MapPin className="w-3 h-3 inline mr-1" />
                                    {customer.address}
                                  </p>
                                )}
                              </div>
                              <Button
                                onClick={async () => {
                                  await createCustomerOrder(customer.id, {
                                    room_number: customer.room_number || 'N/A',
                                    order_type: 'laundry',
                                    items_description: 'Manager created order',
                                    special_instructions: 'Order created by Manager',
                                    priority: 'normal',
                                    customer_name: customer.name // Pass customer name for better success message
                                  });
                                }}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1"
                                disabled={loading}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Create Order
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {customerSearchResults.length > 10 && (
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Scroll to see all {customerSearchResults.length} results
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
              </>
            )}

            {managerActiveTab === 'orders' && (
              <>
            {/* Delivery Pipeline Statistics */}
            <Card className="pipeline-stats-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <Truck className="w-6 h-6 mr-2 text-purple-600" />
                  Delivery Pipeline Overview
                </CardTitle>
                <p className="text-sm text-gray-600">Track orders from driver delivery to customer pickup</p>
              </CardHeader>
              <CardContent>
                {pipelineStats ? (
                  <div className="pipeline-overview">
                    <div className="pipeline-stats grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="stat-item bg-blue-50 p-4 rounded-lg">
                        <h4 className="text-2xl font-bold text-blue-700">
                          {pipelineStats.orders_in_transit || 0}
                        </h4>
                        <p className="text-sm text-gray-600">Orders in Transit</p>
                        <p className="text-xs text-gray-500">Being delivered by drivers</p>
                      </div>
                      <div className="stat-item bg-orange-50 p-4 rounded-lg">
                        <h4 className="text-2xl font-bold text-orange-700">
                          {pipelineStats.orders_ready_for_pickup || 0}
                        </h4>
                        <p className="text-sm text-gray-600">Ready for Pickup</p>
                        <p className="text-xs text-gray-500">Delivered, awaiting collection</p>
                      </div>
                      <div className="stat-item bg-green-50 p-4 rounded-lg">
                        <h4 className="text-2xl font-bold text-green-700">
                          {pipelineStats.orders_completed_today || 0}
                        </h4>
                        <p className="text-sm text-gray-600">Completed Today</p>
                        <p className="text-xs text-gray-500">Fully processed orders</p>
                      </div>
                    </div>
                    
                    {/* Pipeline Flow Visualization */}
                    <div className="pipeline-flow bg-gray-50 p-4 rounded-lg">
                      <h5 className="font-semibold mb-3">Pipeline Flow</h5>
                      <div className="flow-steps grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="flow-step text-center">
                          <div className="step-icon bg-blue-100 w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center">
                            <Truck className="w-6 h-6 text-blue-600" />
                          </div>
                          <h6 className="font-medium text-sm">Driver Delivery</h6>
                          <p className="text-xs text-gray-600">{pipelineStats.orders_in_transit || 0} active</p>
                        </div>
                        <div className="flow-step text-center">
                          <div className="step-icon bg-orange-100 w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center">
                            <Package className="w-6 h-6 text-orange-600" />
                          </div>
                          <h6 className="font-medium text-sm">Ready for Pickup</h6>
                          <p className="text-xs text-gray-600">{pipelineStats.orders_ready_for_pickup || 0} waiting</p>
                        </div>
                        <div className="flow-step text-center">
                          <div className="step-icon bg-purple-100 w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center">
                            <User className="w-6 h-6 text-purple-600" />
                          </div>
                          <h6 className="font-medium text-sm">Customer Pickup</h6>
                          <p className="text-xs text-gray-600">In progress</p>
                        </div>
                        <div className="flow-step text-center">
                          <div className="step-icon bg-green-100 w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          </div>
                          <h6 className="font-medium text-sm">Completed</h6>
                          <p className="text-xs text-gray-600">{pipelineStats.orders_completed_today || 0} today</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="loading-state text-center py-8">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 text-gray-400 animate-spin" />
                    <p className="text-gray-500">Loading pipeline data...</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Order Submissions & Inspection */}
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <FileSignature className="w-6 h-6 mr-2 text-purple-600" />
                  Order Submissions & Inspection
                </CardTitle>
                <p className="text-sm text-gray-600">Review completed deliveries with photo documentation and client signatures</p>
              </CardHeader>
              <CardContent>
                <div className="submission-stats grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="stat-item bg-purple-50 p-4 rounded-lg">
                    <h4 className="text-2xl font-bold text-purple-700">
                      {submissionStats?.ordersReadyForInspection || 0}
                    </h4>
                    <p className="text-sm text-gray-600">Orders Ready for Inspection</p>
                  </div>
                  <div className="stat-item bg-green-50 p-4 rounded-lg">
                    <h4 className="text-2xl font-bold text-green-700">
                      {submissionStats?.ordersCompletedToday || 0}
                    </h4>
                    <p className="text-sm text-gray-600">Orders Completed Today</p>
                  </div>
                  <div className="stat-item bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-2xl font-bold text-blue-700">
                      {submissionStats?.ordersInProgress || 0}
                    </h4>
                    <p className="text-sm text-gray-600">Orders In Progress</p>
                  </div>
                </div>

                {/* Order History Controls */}
                <div className="order-history-controls bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                    <div className="flex-1">
                      <Label htmlFor="order-search-main">Search Orders</Label>
                      <Input
                        id="order-search-main"
                        placeholder="Search by order ID, customer name, or facility"
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <div>
                        <Label htmlFor="sort-by">Sort By</Label>
                        <select id="sort-by" className="p-2 border rounded-md mt-1">
                          <option value="date">Date (Newest First)</option>
                          <option value="date-asc">Date (Oldest First)</option>
                          <option value="name">Customer Name (A-Z)</option>
                          <option value="name-desc">Customer Name (Z-A)</option>
                          <option value="facility">Facility (A-Z)</option>
                          <option value="facility-desc">Facility (Z-A)</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="filter-status">Status</Label>
                        <select id="filter-status" className="p-2 border rounded-md mt-1">
                          <option value="all">All Orders</option>
                          <option value="delivered">Delivered</option>
                          <option value="signed">Client Signed</option>
                          <option value="pending">Pending Signature</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order History List */}
                <div className="order-history-list">
                  <h4 className="text-lg font-semibold mb-4">Recent Order History</h4>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    
                    {/* Empty State - No Orders Yet */}
                    <div className="order-history-empty bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h5 className="text-lg font-medium text-gray-600 mb-2">No Order History Yet</h5>
                      <p className="text-gray-500 mb-4">
                        Order history will appear here once drivers complete deliveries with photo documentation.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        <div className="text-sm text-gray-400">
                          <span className="font-medium">Complete workflow:</span> Driver delivers → Photos taken → Client signs → Review here
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center mt-4">
                  <button
                    onClick={async () => {
                      setShowSubmissionReview(true);
                      await loadSubmissionStats();
                      await loadAvailableDrivers(); // Ensure drivers are up to date
                    }}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded flex items-center"
                  >
                    <FileSignature className="w-4 h-4 mr-2" />
                    Open Advanced Review
                  </button>
                </div>
              </CardContent>
            </Card>
              </>
            )}

            {managerActiveTab === 'gallery' && (
              <>
            {/* Order Delivery Photo Gallery */}
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <Camera className="w-6 h-6 mr-2 text-green-600" />
                  Order Delivery Photo Gallery
                </CardTitle>
                <p className="text-sm text-gray-600">Browse delivery photos and client signature proof</p>
              </CardHeader>
              <CardContent>
                <div className="gallery-stats grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="stat-item bg-gray-50 p-4 rounded-lg text-center">
                    <h4 className="text-2xl font-bold text-gray-700">
                      {submissionStats?.totalOrders || 0}
                    </h4>
                    <p className="text-xs text-gray-600">Total Orders</p>
                  </div>
                  <div className="stat-item bg-green-50 p-4 rounded-lg text-center">
                    <h4 className="text-2xl font-bold text-green-700">
                      {submissionStats?.ordersWithPhotos || 0}
                    </h4>
                    <p className="text-xs text-gray-600">Orders with Photo Proof</p>
                  </div>
                  <div className="stat-item bg-blue-50 p-4 rounded-lg text-center">
                    <h4 className="text-2xl font-bold text-blue-700">
                      {submissionStats?.totalDeliveryPhotos || 0}
                    </h4>
                    <p className="text-xs text-gray-600">Delivery Photos</p>
                  </div>
                  <div className="stat-item bg-yellow-50 p-4 rounded-lg text-center">
                    <h4 className="text-2xl font-bold text-yellow-700">
                      {submissionStats?.clientSignatures || 0}
                    </h4>
                    <p className="text-xs text-gray-600">Client Signatures</p>
                  </div>
                </div>
                
                <div className="gallery-actions flex flex-col sm:flex-row gap-4 items-center">
                  <Button
                    onClick={() => setShowPhotoGallery(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 text-sm sm:text-base"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Browse Delivery Photos</span>
                    <span className="sm:hidden">Photos</span>
                  </Button>
                  
                  <div className="quick-access flex flex-wrap items-center gap-2">
                    <span className="text-xs sm:text-sm text-gray-600 w-full sm:w-auto">Quick Access:</span>
                    <Button
                      onClick={() => {
                        setShowPhotoGallery(true);
                        // Could set a filter for recent deliveries
                      }}
                      variant="outline"
                      size="sm"
                      className="text-xs px-2 py-1 h-7"
                    >
                      Today's
                    </Button>
                    <Button
                      onClick={() => {
                        setShowPhotoGallery(true);
                        // Could set a filter for pending signatures
                      }}
                      variant="outline"
                      size="sm"
                      className="text-xs px-2 py-1 h-7"
                    >
                      Pending
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
              </>
            )}

            {/* Order Management is in the Orders tab - move it there */}
            {managerActiveTab === 'orders' && (
              <>
            {/* Order Management System - Optimized with Pagination */}
            <div className="order-management-section mb-8">
              <OrderManagement 
                key={orderManagementRefreshKey}
                api={api}
                user={user}
                onEditOrder={openOrderEditModal}
                onDeleteOrder={openOrderDeleteModal}
              />
            </div>
              </>
            )}

            {managerActiveTab === 'facilities' && (
              <>
            {/* PIN Management System */}
            <Card className="pin-management-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <Key className="w-6 h-6 mr-2 text-indigo-600" />
                  PIN Management System
                </CardTitle>
                <p className="text-sm text-gray-600">Manage access codes for drivers, facility staff, and managers</p>
              </CardHeader>
              <CardContent>
                <div className="pin-management-content">
                  {/* Load PIN Management Data Button */}
                  <div className="pin-management-header mb-4">
                    <Button
                      onClick={loadPinManagementData}
                      variant="outline"
                      size="sm"
                      disabled={isLoadingPinData}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingPinData ? 'animate-spin' : ''}`} />
                      {isLoadingPinData ? 'Loading...' : 'Load PIN Data'}
                    </Button>
                  </div>

                  {pinManagementData && (
                    <div className="pin-sections space-y-6">
                      {/* Manager PINs */}
                      <div className="managers-section">
                        <h4 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <Shield className="w-5 h-5 mr-2 text-red-600" />
                          Manager Access Codes ({pinManagementData.managers?.length || 0})
                        </h4>
                        <div className="pin-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {(pinManagementData.managers || []).map((manager) => (
                            <Card key={manager.id} className="pin-item border-l-4 border-red-500">
                              <CardContent className="p-4">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <h6 className="font-semibold text-sm text-red-700">{manager.name}</h6>
                                    <p className="text-xs text-gray-600">PIN: ****</p>
                                  </div>
                                  <Button
                                    onClick={() => openPinEditModal('manager', manager)}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs"
                                  >
                                    <Edit className="w-3 h-3 mr-1" />
                                    Edit
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>

                      {/* Driver PINs */}
                      <div className="drivers-section">
                        <div className="drivers-header flex justify-between items-center mb-3">
                          <h4 className="text-lg font-semibold text-gray-700 flex items-center">
                            <Truck className="w-5 h-5 mr-2 text-blue-600" />
                            Driver Access Codes ({pinManagementData.drivers?.length || 0})
                          </h4>
                          <Button
                            onClick={() => setShowAddUserModal(true)}
                            variant="outline"
                            size="sm"
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Driver
                          </Button>
                        </div>
                        <div className="pin-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {(pinManagementData.drivers || []).map((driver) => (
                            <Card key={driver.id} className="pin-item border-l-4 border-blue-500">
                              <CardContent className="p-4">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h6 className="font-semibold text-sm text-blue-700">{driver.name}</h6>
                                    <p className="text-xs text-gray-600">PIN: ****</p>
                                    {driver.phone && (
                                      <p className="text-xs text-gray-500">Phone: {driver.phone}</p>
                                    )}
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <Button
                                      onClick={(e) => {
                                        console.log('🔴 Driver Edit button clicked for:', driver.name);
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openPinEditModal('driver', driver);
                                        console.log('🔴 After openPinEditModal call');
                                      }}
                                      variant="outline"
                                      size="sm"
                                      className="text-xs"
                                    >
                                      <Edit className="w-3 h-3 mr-1" />
                                      Edit
                                    </Button>
                                    <Button
                                      onClick={(e) => {
                                        console.log('🔴 Driver Delete button clicked for:', driver.name);
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openDeleteConfirmModal(driver);
                                        console.log('🔴 After openDeleteConfirmModal call');
                                      }}
                                      variant="outline"
                                      size="sm"
                                      className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-3 h-3 mr-1" />
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>

                      {/* Facility PINs */}
                      <div className="facilities-section">
                        <h4 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                          <Building2 className="w-5 h-5 mr-2 text-green-600" />
                          Facility Access Codes ({pinManagementData.facilities?.length || 0})
                        </h4>
                        <div className="pin-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {(pinManagementData.facilities || []).map((facility) => (
                            <Card key={facility.id} className="pin-item border-l-4 border-green-500">
                              <CardContent className="p-4">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <h6 className="font-semibold text-sm text-green-700">{facility.name}</h6>
                                    <p className="text-xs text-gray-600">PIN: ****</p>
                                  </div>
                                  <Button
                                    onClick={() => openPinEditModal('facility', facility)}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs"
                                  >
                                    <Edit className="w-3 h-3 mr-1" />
                                    Edit
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {!pinManagementData && (
                    <div className="no-pin-data text-center py-8">
                      <Key className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500 text-lg">No PIN Data Loaded</p>
                      <p className="text-gray-400">Click "Load PIN Data" to view and manage access codes</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Community Facilities */}
            <Card className="facilities-card mb-8 shadow-sm">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center text-lg">
                      <Building2 className="w-5 h-5 mr-2 text-purple-600" />
                      {tr('communityFacilities')} ({(managerDashboardData?.facilities || []).length})
                    </CardTitle>
                    <p className="text-sm text-gray-600">Community housing facilities</p>
                  </div>
                  <Button
                    onClick={() => setShowAddFacilityModal(true)}
                    variant="outline"
                    size="sm"
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Facility
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="facilities-grid grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {(managerDashboardData?.facilities || []).map((facility) => (
                    <Card
                      key={facility.id}
                      className="facility-item hover:bg-purple-50 border border-gray-200 hover:border-purple-300 relative"
                    >
                      <CardContent className="p-2">
                        <div className="facility-actions absolute top-1 right-1">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteFacilityModal(facility);
                            }}
                            variant="outline"
                            size="sm"
                            className="text-xs text-red-600 border-red-200 hover:bg-red-50 p-1"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div 
                          className="facility-info text-center cursor-pointer"
                          onClick={() => viewFacilityDetails(facility)}
                        >
                          <Building2 className="w-6 h-6 mx-auto mb-1 text-purple-600" />
                          <span className="text-xs font-medium">{facility.name}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
              </>
            )}

            {/* Customer Database Management - Customers Tab */}
            {managerActiveTab === 'customers' && (
              <>
            <Card className="section-card mb-8 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-bold text-gray-800">
                  <FileText className="w-6 h-6 mr-2 text-green-600" />
                  Customer Database Management
                </CardTitle>
                <p className="text-sm text-gray-600">Import additional customers or update existing customer data</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Import Status */}
                  {customerImportStatus && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h6 className="font-medium text-sm mb-2">Current Database Status</h6>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Total Customers:</span>
                          <span className="font-medium ml-1">{customerImportStatus.statistics?.total_customers || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">With Orders:</span>
                          <span className="font-medium ml-1">{customerImportStatus.statistics?.customers_with_orders || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Recent Imports:</span>
                          <span className="font-medium ml-1">{customerImportStatus.statistics?.recent_imports || 0}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* CSV Upload Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Upload CSV button clicked - opening modal');
                        setShowUploadModal(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload New CSV
                    </Button>
                    
                    <Button
                      onClick={importCustomerDatabase}
                      disabled={loading}
                      variant="outline"
                      className="border-green-600 text-green-600 hover:bg-green-50"
                    >
                      <Database className="w-4 h-4 mr-2" />
                      Import Original Database
                    </Button>
                    
                    <Button
                      onClick={cleanupDuplicateCustomers}
                      disabled={loading}
                      variant="outline"
                      className="border-orange-600 text-orange-600 hover:bg-orange-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Cleanup Duplicates
                    </Button>
                    
                    <Button
                      onClick={loadUploadHistory}
                      variant="outline"
                      className="border-gray-600 text-gray-600 hover:bg-gray-50"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Upload History
                    </Button>
                    
                    <Button
                      onClick={() => setShowDeleteDatabaseModal(true)}
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-50"
                      data-testid="open-delete-database-modal-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Entire Database
                    </Button>
                  </div>
                  
                  {/* Upload History */}
                  {uploadHistory.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h6 className="font-medium text-sm mb-2">Recent Uploads</h6>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {uploadHistory.slice(0, 5).map((upload, index) => (
                          <div key={index} className="text-sm flex justify-between items-center">
                            <div>
                              <span className="font-medium">{upload.original_filename}</span>
                              <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                upload.status === 'completed' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {upload.status}
                              </span>
                            </div>
                            <span className="text-gray-500">
                              {new Date(upload.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
              </>
            )}

          </div>
        )}
      </div>
    );
  };

  return (
    <div className="App">
      <div className="app-container" style={{maxWidth: '100vw', width: '100%', overflowX: 'hidden', boxSizing: 'border-box'}}>
        {/* Success Alert */}
        {success && (
          <Alert className="max-w-md mx-auto mb-4 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              {success}
            </AlertDescription>
            <Button
              onClick={() => setSuccess('')}
              variant="ghost"
              size="sm"
              className="ml-auto"
            >
              ×
            </Button>
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert className="max-w-md mx-auto mb-4 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
            <Button
              onClick={() => setError('')}
              variant="ghost"
              size="sm"
              className="ml-auto"
            >
              ×
            </Button>
          </Alert>
        )}

        {/* Barcode Scanner Modal */}
        <BarcodeScanner
          isOpen={showBarcodeScanner}
          onClose={closeBarcodeScanner}
          onBarcodeScanned={handleBarcodeScanned}
          title={barcodeScanContext?.title || "Scan Barcode"}
          subtitle={barcodeScanContext?.subtitle || "Point camera at barcode or QR code"}
        />

        <main className="main-content" style={{maxWidth: '100vw', width: '100%', overflowX: 'hidden', boxSizing: 'border-box', padding: '0'}}>
          {currentStep === 'welcome' && renderWelcome()}
          {currentStep === 'auth' && renderAuth()}
          {currentStep === 'hotel-auth' && renderHotelAuth()}
          {currentStep === 'hotel-select' && renderHotelSelect()}
          {currentStep === 'recipients' && renderRecipients()}
          {currentStep === 'review' && renderReview()}
          {currentStep === 'signature' && renderSignature()}
          {currentStep === 'success' && renderSuccess()}
          {currentStep === 'facility-dashboard' && renderHotelDashboard()}
          {currentStep === 'manager-dashboard' && renderManagerDashboard()}
          {currentStep === 'confirm-pickup' && renderConfirmPickup()}
        </main>

        {/* Custom Task Creation Modal - Step-by-step rebuild */}
        {showCustomTaskCreation ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div 
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b pb-4 mb-6">
                <div className="flex items-center">
                  <Plus className="w-6 h-6 mr-2 text-green-600" />
                  <h2 className="text-xl font-bold">{tr('createCustomTask')}</h2>
                </div>
                <p className="text-gray-600 mt-2">{tr('createTaskDescription')}</p>
              </div>

              {/* Form Content */}
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{tr('taskTitle')} *</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={newCustomTask.title}
                      onChange={(e) => setNewCustomTask(prev => ({...prev, title: e.target.value}))}
                      placeholder={tr('placeholderTaskTitle')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{tr('taskType')}</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={newCustomTask.task_type}
                      onChange={(e) => setNewCustomTask(prev => ({...prev, task_type: e.target.value}))}
                    >
                      <option value="delivery">{tr('delivery')}</option>
                      <option value="pickup">{tr('pickup')}</option>
                      <option value="quality_check">{tr('qualityCheck')}</option>
                      <option value="maintenance">{tr('maintenance')}</option>
                      <option value="emergency">{tr('emergency')}</option>
                      <option value="special_request">{tr('specialRequest')}</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('description')} *</label>
                  <textarea
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    value={newCustomTask.description}
                    onChange={(e) => setNewCustomTask(prev => ({...prev, description: e.target.value}))}
                    placeholder={tr('placeholderDescription')}
                  />
                </div>

                {/* Assignment, Hotel and Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{tr('assignToStaff')} *</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={newCustomTask.assigned_to}
                      onChange={(e) => setNewCustomTask(prev => ({...prev, assigned_to: e.target.value}))}
                    >
                      <option value="">{tr('placeholderChooseStaff')}</option>
                      {(availableStaff || []).map(staff => (
                        <option key={staff.id} value={staff.id}>
                          {staff.name} ({staff.role})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{tr('priority')}</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={newCustomTask.priority}
                      onChange={(e) => setNewCustomTask(prev => ({...prev, priority: e.target.value}))}
                    >
                      <option value="normal">{tr('normal')}</option>
                      <option value="high">{tr('high')}</option>
                      <option value="urgent">{tr('urgent')}</option>
                    </select>
                  </div>
                </div>

                {/* Hotel Assignment (Optional) */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {tr('assignToHotel')}
                    <span className="text-gray-400 text-xs ml-1">{tr('optional')}</span>
                  </label>
                  <select
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newCustomTask.hotel_id || ''}
                    onChange={(e) => setNewCustomTask(prev => ({...prev, hotel_id: e.target.value}))}
                  >
                    <option value="">{tr('placeholderSelectHotel')}</option>
                    {(hotels || []).map(hotel => (
                      <option key={hotel.id} value={hotel.id}>
                        {hotel.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-600 mt-1">
                    {tr('hotelAssociationNote')}
                  </p>
                </div>

                {/* Photos Required */}
                <div>
                  <label className="block text-sm font-medium mb-1">{tr('photosRequired')}</label>
                  <select
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newCustomTask.required_photos}
                    onChange={(e) => setNewCustomTask(prev => ({...prev, required_photos: parseInt(e.target.value)}))}
                  >
                    <option value="0">{tr('noPhotosRequired')}</option>
                    <option value="1">{tr('onePhotoRequired')}</option>
                    <option value="2">{tr('twoPhotosRequired')}</option>
                    <option value="3">{tr('threePhotosRequired')}</option>
                  </select>
                </div>

                {/* Checklist Items */}
                <div>
                  <label className="block text-sm font-medium mb-2">{tr('taskSteps')}</label>
                  <p className="text-sm text-gray-600 mb-4">{tr('taskStepsDescription')}</p>
                  
                  {(newCustomTask.checklist_items || [{ text: '', requiresPhoto: false }]).map((item, index) => (
                    <div key={index} className="flex flex-col space-y-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-600 min-w-[60px]">
                          Step {index + 1}:
                        </span>
                        <input
                          type="text"
                          className="flex-1 p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={typeof item === 'string' ? item : item.text || ''}
                          onChange={(e) => {
                            const newItems = [...(newCustomTask.checklist_items || [{ text: '', requiresPhoto: false }])];
                            if (typeof newItems[index] === 'string') {
                              newItems[index] = { text: e.target.value, requiresPhoto: false };
                            } else {
                              newItems[index] = { ...newItems[index], text: e.target.value };
                            }
                            setNewCustomTask(prev => ({...prev, checklist_items: newItems}));
                          }}
                          placeholder={`Step ${index + 1}: e.g., Check customer ID`}
                        />
                        <button
                          type="button"
                          className="px-3 py-2 text-red-600 hover:text-red-700 border border-red-300 rounded hover:bg-red-50"
                          onClick={() => {
                            if ((newCustomTask.checklist_items || [{ text: '', requiresPhoto: false }]).length > 1) {
                              const newItems = newCustomTask.checklist_items.filter((_, i) => i !== index);
                              setNewCustomTask(prev => ({...prev, checklist_items: newItems}));
                            }
                          }}
                          disabled={(newCustomTask.checklist_items || [{ text: '', requiresPhoto: false }]).length <= 1}
                        >
                          Remove
                        </button>
                      </div>
                      
                      {/* Photo Requirement for Individual Step */}
                      <div className="flex items-center space-x-4 ml-16">
                        <span className="text-sm text-gray-600">Require Photo?</span>
                        <div className="flex items-center space-x-3">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`step-photo-${index}`}
                              checked={typeof item === 'object' ? item.requiresPhoto === true : false}
                              onChange={() => {
                                const newItems = [...(newCustomTask.checklist_items || [{ text: '', requiresPhoto: false }])];
                                if (typeof newItems[index] === 'string') {
                                  newItems[index] = { text: newItems[index], requiresPhoto: true };
                                } else {
                                  newItems[index] = { ...newItems[index], requiresPhoto: true };
                                }
                                setNewCustomTask(prev => ({...prev, checklist_items: newItems}));
                              }}
                              className="mr-1"
                            />
                            <span className="text-green-600 font-medium">Yes</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name={`step-photo-${index}`}
                              checked={typeof item === 'object' ? item.requiresPhoto === false : true}
                              onChange={() => {
                                const newItems = [...(newCustomTask.checklist_items || [{ text: '', requiresPhoto: false }])];
                                if (typeof newItems[index] === 'string') {
                                  newItems[index] = { text: newItems[index], requiresPhoto: false };
                                } else {
                                  newItems[index] = { ...newItems[index], requiresPhoto: false };
                                }
                                setNewCustomTask(prev => ({...prev, checklist_items: newItems}));
                              }}
                              className="mr-1"
                            />
                            <span className="text-gray-600">No</span>
                          </label>
                        </div>
                        {(typeof item === 'object' && item.requiresPhoto) && (
                          <div className="flex items-center text-blue-600">
                            <Camera className="w-3 h-3 mr-1" />
                            <span className="text-xs">Photo Required</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    className="w-full p-2 border-2 border-dashed border-gray-300 rounded text-gray-600 hover:border-gray-400 hover:text-gray-700"
                    onClick={() => {
                      const currentItems = newCustomTask.checklist_items || [{ text: '', requiresPhoto: false }];
                      setNewCustomTask(prev => ({...prev, checklist_items: [...currentItems, { text: '', requiresPhoto: false }]}));
                    }}
                  >
                    <Plus className="w-4 h-4 inline mr-2" />
                    {tr('addStep')}
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowCustomTaskCreation(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  onClick={createCustomTask}
                  disabled={loading || !newCustomTask.title || !newCustomTask.assigned_to}
                >
                  {loading ? 'Creating...' : 'Create Task'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Template Creation Dialog */}
        {/* Template Creation Modal */}
        {showTemplateCreation ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div 
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b pb-4 mb-6">
                <div className="flex items-center">
                  <Plus className="w-6 h-6 mr-2 text-blue-600" />
                  <h2 className="text-xl font-bold">{newTemplate.id ? 'Edit Task Template' : 'Create Task Template'}</h2>
                </div>
                <p className="text-gray-600 mt-2">
                  {newTemplate.id ? 'Edit the existing task template' : 'Create a new task template for weekly scheduling'}
                </p>
              </div>
            
              {/* Form Content */}
              <div className="space-y-4">
                {/* Debug info */}
                {newTemplate.id && (
                  <div className="bg-blue-50 p-2 rounded text-xs text-blue-700">
                    Editing template: {newTemplate.title} (ID: {newTemplate.id})
                  </div>
                )}
                
                {/* Template Title */}
                <div>
                  <label className="text-sm font-medium block mb-1">{tr('templateName')}</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newTemplate.title}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={tr('placeholderTaskTitle')}
                  />
                </div>

                {/* Template Description */}
                <div>
                  <label className="text-sm font-medium block mb-1">{tr('templateDescription')}</label>
                  <textarea
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this template is used for..."
                  />
                </div>

                {/* Day of Week */}
                <div>
                  <label className="text-sm font-medium block mb-1">{tr('dayOfWeek')}</label>
                  <select
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newTemplate.day_of_week}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, day_of_week: e.target.value }))}
                  >
                    <option value="monday">{tr('monday')}</option>
                    <option value="tuesday">{tr('tuesday')}</option>
                    <option value="wednesday">{tr('wednesday')}</option>
                    <option value="thursday">{tr('thursday')}</option>
                    <option value="friday">{tr('friday')}</option>
                  </select>
                </div>

                {/* Hotel Locations */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Hotel Locations & Schedule</label>
                  <div className="space-y-3">
                    {newTemplate.locations.map((location, index) => (
                      <div key={index} className="border rounded-lg p-3 bg-gray-50">
                        <div className="grid grid-cols-2 gap-3 mb-2">
                          <div>
                            <label className="text-xs text-gray-600 block">Hotel</label>
                            <select
                              className="w-full p-1 border rounded text-sm"
                              value={location.hotel_id}
                              onChange={(e) => {
                                console.log('🏨 Hotel selection changed:', e.target.value);
                                const selectedHotel = hotels.find(h => h.id === e.target.value);
                                console.log('🏨 Selected hotel object:', selectedHotel);
                                updateTemplateLocation(index, 'hotel_id', e.target.value);
                                updateTemplateLocation(index, 'hotel_name', selectedHotel?.name || '');
                                console.log('🏨 Updated template location with hotel_id:', e.target.value);
                              }}
                            >
                              <option value="">
                                {!hotels || hotels.length === 0 ? tr('loadingHotels') : tr('selectHotel')}
                              </option>
                              {hotels && hotels.length > 0 ? (
                                hotels.map(hotel => (
                                  <option key={hotel.id} value={hotel.id}>{hotel.name}</option>
                                ))
                              ) : (
                                <option value="" disabled>No hotels available</option>
                              )}
                            </select>
                            {/* Debug info */}
                            {process.env.NODE_ENV === 'development' && (
                              <div className="text-xs text-gray-500 mt-1">
                                Debug: {hotels ? hotels.length : 0} hotels loaded
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block">Task Type</label>
                            <select
                              className="w-full p-1 border rounded text-sm"
                              value={location.task_type}
                              onChange={(e) => updateTemplateLocation(index, 'task_type', e.target.value)}
                            >
                              <option value="pickup">Pickup</option>
                              <option value="delivery">Delivery</option>
                              <option value="both">Both</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-2">
                          <div>
                            <label className="text-xs text-gray-600 block">Scheduled Time (Optional)</label>
                            <input
                              type="time"
                              className="w-full p-1 border rounded text-sm"
                              value={location.scheduled_time}
                              onChange={(e) => updateTemplateLocation(index, 'scheduled_time', e.target.value)}
                            />
                          </div>
                          <div className="flex items-end">
                            <button
                              type="button"
                              className="px-3 py-1 text-red-600 border border-red-200 rounded hover:bg-red-50 text-sm"
                              onClick={() => removeTemplateLocation(index)}
                              disabled={newTemplate.locations.length <= 1}
                            >
                              <Trash2 className="w-3 h-3 inline mr-1" />
                              Remove
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 block">Notes (Optional)</label>
                          <input
                            type="text"
                            className="w-full p-1 border rounded text-sm"
                            value={location.notes}
                            onChange={(e) => updateTemplateLocation(index, 'notes', e.target.value)}
                            placeholder="Special instructions..."
                          />
                        </div>

                        {/* Checklist Items for this location */}
                        <div className="mt-3">
                          <label className="text-xs text-gray-600 block mb-2">{tr('taskChecklistForHotel')}</label>
                          <div className="space-y-3">
                            {(location.checklist_items || [{ text: '', requiresPhoto: false }]).map((item, itemIndex) => (
                              <div key={itemIndex} className="border rounded p-3 bg-gray-50">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className="w-5 h-5 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-semibold">
                                    {itemIndex + 1}
                                  </span>
                                  <input
                                    type="text"
                                    className="flex-1 p-2 border rounded text-sm"
                                    value={item.text || ''}
                                    onChange={(e) => updateLocationChecklistItem(index, itemIndex, 'text', e.target.value)}
                                    placeholder={`Task ${itemIndex + 1}: e.g., Check lobby cleanliness`}
                                  />
                                  <button
                                    type="button"
                                    className="p-1 text-red-600 border border-red-200 rounded hover:bg-red-50"
                                    onClick={() => removeLocationChecklistItem(index, itemIndex)}
                                    disabled={(location.checklist_items || []).length <= 1}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                                
                                {/* Photo Requirement Toggle */}
                                <div className="flex items-center space-x-3 text-sm mt-2">
                                  <label className="text-gray-600 font-medium">Require Photo?</label>
                                  <div className="flex items-center space-x-3">
                                    <label className="flex items-center">
                                      <input
                                        type="radio"
                                        name={`photo-req-${index}-${itemIndex}`}
                                        checked={item.requiresPhoto === true}
                                        onChange={() => updateLocationChecklistItem(index, itemIndex, 'requiresPhoto', true)}
                                        className="mr-1"
                                      />
                                      <span className="text-green-600 font-medium">Yes</span>
                                    </label>
                                    <label className="flex items-center">
                                      <input
                                        type="radio"
                                        name={`photo-req-${index}-${itemIndex}`}
                                        checked={item.requiresPhoto === false}
                                        onChange={() => updateLocationChecklistItem(index, itemIndex, 'requiresPhoto', false)}
                                        className="mr-1"
                                      />
                                      <span className="text-gray-600">No</span>
                                    </label>
                                  </div>
                                  {item.requiresPhoto && (
                                    <div className="flex items-center text-blue-600">
                                      <Camera className="w-3 h-3 mr-1" />
                                      <span className="text-xs">Photo Required</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          <button
                            type="button"
                            className="w-full mt-2 p-1 border-2 border-dashed border-gray-300 rounded text-gray-600 hover:border-gray-400 hover:text-gray-700 text-xs"
                            onClick={() => addLocationChecklistItem(index)}
                          >
                            <Plus className="w-3 h-3 inline mr-1" />
                            {tr('addTaskForHotel')}
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      className="w-full p-2 border-2 border-dashed border-gray-300 rounded text-gray-600 hover:border-gray-400 hover:text-gray-700"
                      onClick={addTemplateLocation}
                    >
                      <Plus className="w-4 h-4 inline mr-2" />
                      Add Hotel Location
                    </button>
                  </div>
                </div>

                {/* Note about hotel-specific checklists */}
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <p className="text-sm text-blue-800">
                    📋 <strong>Task Lists:</strong> Each hotel location above has its own task checklist. 
                    Add specific tasks that need to be completed at each location.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setShowTemplateCreation(false);
                    setNewTemplate({
                      title: '',
                      description: '',
                      day_of_week: 'monday',
                      locations: [{
                        hotel_id: '',
                        hotel_name: '',
                        task_type: 'both',
                        scheduled_time: '',
                        notes: '',
                        checklist_items: [{ text: '', requiresPhoto: false }]
                      }],
                      assignable_to_roles: ['driver'],
                      priority: 'normal',
                      estimated_duration: 30,
                      required_photos: 1
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  onClick={createTaskTemplate}
                  disabled={(() => {
                    const isLoading = loading;
                    const hasTitle = newTemplate.title && newTemplate.title.trim();
                    const hasHotelId = !newTemplate.locations.some(loc => !loc.hotel_id);
                    
                    // Fixed validation for object-based checklist items
                    const hasValidChecklist = !newTemplate.locations.some(loc => {
                      if (!loc.checklist_items || loc.checklist_items.length === 0) return true;
                      
                      return loc.checklist_items.every(item => {
                        if (!item) return true;
                        
                        // Handle both string and object formats
                        if (typeof item === 'string') {
                          return !item.trim();
                        } else if (typeof item === 'object') {
                          // Check common text properties in checklist item objects
                          const text = item.text || item.title || item.description || item.name || '';
                          return !text || !text.trim();
                        }
                        
                        return true; // Invalid format
                      });
                    });
                    
                    console.log('🔶 Create Template Button Validation:', {
                      loading: isLoading,
                      hasTitle,
                      hasHotelId,
                      hasValidChecklist,
                      title: newTemplate.title,
                      disabled: isLoading || !hasTitle || !hasHotelId || !hasValidChecklist
                    });
                    
                    return isLoading || !hasTitle || !hasHotelId || !hasValidChecklist;
                  })()}
                >
                  {loading ? (newTemplate.id ? 'Updating...' : 'Creating...') : (newTemplate.id ? 'Update Template' : 'Create Template')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Driver Selection Dialog */}
        <Dialog open={showDriverSelection} onOpenChange={setShowDriverSelection}>
          <DialogContent 
            className="driver-selection-dialog max-w-md z-[9999] relative bg-white border shadow-lg"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            aria-describedby="driver-selection-description"
          >
            <DialogHeader className="border-b pb-3">
              <DialogTitle className="flex items-center text-lg font-bold">
                <User className="w-6 h-6 mr-2 text-green-600" />
                Assign Template to Driver
              </DialogTitle>
              <div id="driver-selection-description" className="text-sm text-gray-600 mt-1">
                Choose driver and target date for template assignment
              </div>
            </DialogHeader>
            
            <div className="driver-selection p-4">
              {/* Debug Info */}
              {selectedTemplateForAssignment && (
                <div className="debug-info mb-2 p-2 bg-gray-100 rounded text-xs">
                  <strong>Debug:</strong> {selectedTemplateForAssignment.id === 'bulk' ? 'Bulk Assignment' : 'Single Assignment'}
                  {selectedTemplateForAssignment.id === 'bulk' && (
                    <span> - {selectedTemplateForAssignment.templates?.length || 0} templates</span>
                  )}
                </div>
              )}
              
              {selectedTemplateForAssignment && (
                <div className="assignment-info mb-4 p-3 bg-blue-50 rounded">
                  <h6 className="font-medium text-blue-800">
                    {selectedTemplateForAssignment.id === 'bulk' 
                      ? `Assigning all ${selectedTemplateForAssignment.day} templates`
                      : `Assigning: ${selectedTemplateForAssignment.title}`
                    }
                  </h6>
                  {selectedTemplateForAssignment.id === 'bulk' && (
                    <p className="text-xs text-blue-600 mt-1">
                      {selectedTemplateForAssignment.templates?.length || 0} templates will be assigned
                    </p>
                  )}
                </div>
              )}
              
              {/* Date Selection */}
              <div className="date-selection mb-4">
                <Label className="text-sm font-medium mb-2 block">Target Date:</Label>
                <div className="date-input-container">
                  <Input
                    type="date"
                    value={selectedAssignmentDate}
                    onChange={(e) => setSelectedAssignmentDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]} // Prevent past dates
                    className="w-full"
                  />
                  <div className="date-helpers mt-2 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedAssignmentDate(getLocalDateString())}
                      className="text-xs"
                    >
                      Today
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        setSelectedAssignmentDate(getLocalDateString(tomorrow));
                      }}
                      className="text-xs"
                    >
                      Tomorrow
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const nextWeek = new Date();
                        nextWeek.setDate(nextWeek.getDate() + 7);
                        setSelectedAssignmentDate(getLocalDateString(nextWeek));
                      }}
                      className="text-xs"
                    >
                      Next Week
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {formatDateForDisplay(selectedAssignmentDate)}
                  </p>
                </div>
              </div>
              
              <div className="drivers-list space-y-2">
                <Label className="text-sm font-medium">Available Drivers:</Label>
                {availableDrivers.length === 0 ? (
                  <div className="no-drivers text-center py-4">
                    <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-gray-500">No drivers available</p>
                    <p className="text-xs text-gray-400">Add drivers from the user management section</p>
                  </div>
                ) : (
                  <div className="driver-options space-y-2">
                    {availableDrivers.map((driver) => (
                      <div key={driver.id} className="driver-option">
                        <Button
                          onClick={() => assignTemplateToDriver(driver.id)}
                          className="w-full justify-between bg-gray-50 hover:bg-blue-50 text-gray-800 border hover:border-blue-300 p-4"
                          disabled={loading}
                        >
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-3 text-gray-600" />
                            <div className="text-left">
                              <div className="font-medium">{driver.name || driver.email}</div>
                              <div className="text-xs text-gray-500">Driver • ID: {driver.id.substring(0, 8)}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-600">Assign for:</div>
                            <div className="text-xs font-medium text-blue-600">
                              {(() => {
                                const [year, month, day] = selectedAssignmentDate.split('-');
                                const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                return localDate.toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                });
                              })()}
                            </div>
                          </div>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="form-actions flex justify-end space-x-2 pt-4 border-t mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDriverSelection(false);
                    setSelectedTemplateForAssignment(null);
                    setSelectedAssignmentDate(getLocalDateString()); // Reset to today
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Order Confirmation Dialog */}
        <Dialog open={showOrderConfirmation} onOpenChange={setShowOrderConfirmation}>
          <DialogContent className="order-confirmation-dialog">
            <DialogHeader>
              <DialogTitle className="confirmation-title">
                <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
                {tr('orderConfirmation')}
              </DialogTitle>
            </DialogHeader>
            
            {confirmedOrder && (
              <div className="confirmation-content">
                <div className="success-message">
                  <h3 className="text-2xl font-bold text-green-600 mb-2">
                    {tr('orderCreated')}
                  </h3>
                  <p className="text-gray-600 mb-4">{tr('thankYou')}</p>
                </div>

                <div className="order-summary">
                  <div className="summary-section">
                    <h4 className="section-title">{tr('orderNumber')}</h4>
                    <p className="order-id">#{confirmedOrder.orderId.slice(-8).toUpperCase()}</p>
                  </div>

                  <Separator className="my-4" />

                  <div className="summary-section">
                    <h4 className="section-title">{tr('participantDetails')}</h4>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span className="detail-label">{tr('firstName')}:</span>
                        <span className="detail-value">{confirmedOrder.firstName}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{tr('lastName')}:</span>
                        <span className="detail-value">{confirmedOrder.lastName}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{tr('roomNumber')}:</span>
                        <span className="detail-value">{confirmedOrder.roomNumber}</span>
                      </div>
                      {confirmedOrder.bedNumber && (
                        <div className="detail-item">
                          <span className="detail-label">{tr('bedNumber')}:</span>
                          <span className="detail-value">{confirmedOrder.bedNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <div className="summary-section">
                    <h4 className="section-title">{tr('orderDetails')}</h4>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span className="detail-label">{tr('orderType')}:</span>
                        <span className="detail-value">
                          {confirmedOrder.orderType === 'clothes' ? tr('clothes') : tr('linen')}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Hotel:</span>
                        <span className="detail-value">{confirmedOrder.hotelName}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Staff:</span>
                        <span className="detail-value">{confirmedOrder.staffName}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Created:</span>
                        <span className="detail-value">{confirmedOrder.createdAt}</span>
                      </div>
                    </div>
                    
                    {confirmedOrder.specialInstructions && (
                      <div className="special-instructions-section">
                        <span className="detail-label">{tr('specialInstructions')}:</span>
                        <p className="instructions-text">{confirmedOrder.specialInstructions}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="confirmation-actions flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={printOrderReceipt}
                    size="lg"
                    variant="outline"
                    className="print-receipt-btn"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    {language === 'es' ? 'Imprimir Recibo' : 'Print Receipt'}
                  </Button>
                  <Button
                    onClick={() => setShowOrderConfirmation(false)}
                    size="lg"
                    className="close-confirmation-btn"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    {tr('closeConfirmation')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Duplicate modal removed - using the main Custom Task Creation Dialog above */}
        
        {/* Task Completion Photo Modal - Custom HTML Modal */}
        {showTaskPhotoModal && (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="task-photo-modal p-6">
                <div className="flex items-center mb-6">
                  <Camera className="w-6 h-6 mr-2 text-blue-600" />
                  <h2 className="text-xl font-semibold">Upload Completion Photos</h2>
                  <button
                    onClick={() => {
                      setShowTaskPhotoModal(false);
                      setTaskCompletionPhotos([]);
                      setSelectedTaskForCompletion(null);
                      setSelectedTaskItem(null);
                    }}
                    className="ml-auto text-gray-500 hover:text-gray-700"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
                
                <div className="task-photo-content space-y-6">
                  {selectedTaskForCompletion && selectedTaskItem && (
                    <div className="task-info bg-blue-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-lg mb-2">{selectedTaskForCompletion.title}</h3>
                      <p className="text-gray-600 mb-2">Completing step: <span className="font-medium">{selectedTaskItem.text}</span></p>
                      <p className="text-sm text-blue-600">
                        <Camera className="w-4 h-4 inline mr-1" />
                        {selectedTaskForCompletion.required_photos} {selectedTaskForCompletion.required_photos > 1 ? tr('photos') : tr('photo')} required for completion
                      </p>
                    </div>
                  )}
                  
                  <div className="photo-upload-section">
                    <label className="text-lg font-semibold block mb-2">Upload Photos</label>
                    <p className="text-sm text-gray-600 mb-4">Take photos to document task completion</p>
                    
                    <div className="upload-area border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleTaskPhotoUpload}
                        className="hidden"
                        id="task-photo-upload"
                        capture="environment"
                      />
                      <label htmlFor="task-photo-upload" className="cursor-pointer">
                        <Camera className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium text-gray-600">Take Photos</p>
                        <p className="text-sm text-gray-500">Click to open camera or select files</p>
                      </label>
                    </div>
                    
                    {taskCompletionPhotos.length > 0 && (
                      <div className="photo-preview mt-4">
                        <label className="text-md font-medium block mb-2">Uploaded Photos ({taskCompletionPhotos.length})</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                          {taskCompletionPhotos.map((photo, index) => (
                            <div key={index} className="relative">
                              <img 
                                src={photo} 
                                alt={`Task completion ${index + 1}`} 
                                className="w-full h-32 object-cover rounded-lg border"
                              />
                              <button
                                onClick={() => removeTaskPhoto(index)}
                                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 flex items-center justify-center"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="form-actions flex justify-end space-x-2 pt-4">
                    <button
                      onClick={() => {
                        setShowTaskPhotoModal(false);
                        setTaskCompletionPhotos([]);
                        setSelectedTaskForCompletion(null);
                        setSelectedTaskItem(null);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={completeTaskWithPhotos}
                      disabled={loading || taskCompletionPhotos.length < (selectedTaskForCompletion?.required_photos || 1)}
                      className={`px-4 py-2 rounded-md text-white ${
                        loading || taskCompletionPhotos.length < (selectedTaskForCompletion?.required_photos || 1)
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {loading ? 'Completing...' : 'Complete Task Step'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* CSV Upload Modal - Custom Implementation */}
        {showUploadModal && (
          <div 
            className="fixed inset-0 z-[10000] bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={(e) => {
              console.log('Backdrop clicked:', e.target === e.currentTarget);
              // Only close if clicking the backdrop, not the modal content
              if (e.target === e.currentTarget) {
                console.log('Closing modal - backdrop click');
                setShowUploadModal(false);
                setCsvUploadFile(null);
                setUploadProgress(0);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                border: '2px solid #000',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-blue-600" />
                  Upload Customer CSV
                </h2>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setCsvUploadFile(null);
                    setUploadProgress(0);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl font-bold w-8 h-8 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              {/* Description */}
              <p className="text-gray-600 mb-6">
                Upload a CSV file to import customer data. Existing customers can be updated if enabled.
              </p>
              
              {/* File Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileSelect(e)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
                {csvUploadFile && (
                  <p className="text-sm text-gray-600 mt-2">
                    Selected: {csvUploadFile.name}
                  </p>
                )}
              </div>
              
              {/* Update Existing Option */}
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  id="update-existing-custom"
                  checked={updateExistingCustomers}
                  onChange={(e) => setUpdateExistingCustomers(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="update-existing-custom" className="text-sm">
                  Update existing customers if found
                </label>
              </div>
              
              {/* Upload Progress */}
              {uploadProgress > 0 && (
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span>Upload Progress</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setCsvUploadFile(null);
                    setUploadProgress(0);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={uploadCustomerCSV}
                  disabled={!csvUploadFile || loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Uploading...' : 'Upload CSV'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete All Customers Modal - Password Protected */}
        {showDeleteDatabaseModal && (
          <div 
            className="fixed inset-0 z-[10000] bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteDatabaseModal(false);
                setDeleteDatabasePassword('');
                setDeleteDatabaseError('');
              }
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                border: '2px solid #dc2626',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center text-red-600">
                  <AlertCircle className="w-6 h-6 mr-2" />
                  Delete Entire Database
                </h2>
                <button
                  onClick={() => {
                    setShowDeleteDatabaseModal(false);
                    setDeleteDatabasePassword('');
                    setDeleteDatabaseError('');
                  }}
                  className="text-gray-500 hover:text-gray-700 text-xl font-bold w-8 h-8 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              {/* Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 font-medium">⚠️ DANGER: This action cannot be undone!</p>
                <p className="text-red-600 text-sm mt-2">
                  This will permanently delete ALL data from the ENTIRE database including:
                  customers, orders, deliveries, messages, and all other records.
                  Make sure you have a backup before proceeding.
                </p>
              </div>
              
              {/* Password Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Password to Confirm
                </label>
                <input
                  type="password"
                  value={deleteDatabasePassword}
                  onChange={(e) => {
                    setDeleteDatabasePassword(e.target.value);
                    setDeleteDatabaseError('');
                  }}
                  placeholder="Enter deletion password"
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                {deleteDatabaseError && (
                  <p className="text-red-600 text-sm mt-2">{deleteDatabaseError}</p>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowDeleteDatabaseModal(false);
                    setDeleteDatabasePassword('');
                    setDeleteDatabaseError('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={deleteEntireDatabase}
                  disabled={loading || !deleteDatabasePassword}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-database-btn"
                >
                  {loading ? 'Deleting...' : 'Delete Entire Database'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Pickup Success Confirmation Modal */}
        {showPickupSuccessModal && pickupSuccessDetails && (
          <Dialog open={true} onOpenChange={() => setShowPickupSuccessModal(false)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-center">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
                  Successfully Delivered!
                </DialogTitle>
              </DialogHeader>
              <div className="success-content text-center space-y-4">
                <div className="delivery-confirmation">
                  <h3 className="text-lg font-bold text-green-800">
                    Order Completed ✅
                  </h3>
                  <p className="text-gray-600">
                    Laundry has been successfully delivered to customer
                  </p>
                </div>
                
                <div className="success-details bg-green-50 p-4 rounded">
                  <div className="detail-row">
                    <strong>Customer:</strong> {pickupSuccessDetails.customerName}
                  </div>
                  <div className="detail-row">
                    <strong>Facility Staff:</strong> {pickupSuccessDetails.staffName}
                  </div>
                  <div className="detail-row">
                    <strong>Facility:</strong> {pickupSuccessDetails.facilityName}
                  </div>
                  <div className="detail-row">
                    <strong>Completed:</strong> {pickupSuccessDetails.timestamp}
                  </div>
                  {pickupSuccessDetails.orderDetails && (
                    <div className="detail-row">
                      <strong>Room:</strong> {pickupSuccessDetails.orderDetails.room_number}
                    </div>
                  )}
                </div>
                
                <div className="success-actions">
                  <Button
                    onClick={() => {
                      setShowPickupSuccessModal(false);
                      setPickupSuccessDetails(null);
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    size="lg"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        
        {/* Driver Delivery Photo Modal - Custom HTML Modal */}
        {selectedDeliveryStop && (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delivery-photo-modal p-6">
                <div className="flex items-center mb-6">
                  <Camera className="w-6 h-6 mr-2 text-blue-600" />
                  <h2 className="text-xl font-semibold">Delivery Photo Upload</h2>
                  <button
                    onClick={() => {
                      setSelectedDeliveryStop(null);
                      setDeliveryPhotos([]);
                    }}
                    className="ml-auto text-gray-500 hover:text-gray-700"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
                
                <div className="delivery-photo-content space-y-6">
                  <div className="delivery-info bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg mb-2">Delivering Order</h3>
                    <div className="order-details">
                      <p className="text-gray-700 mb-1">
                        <strong>Customer:</strong> {selectedDeliveryStop.customer_name}
                      </p>
                      <p className="text-gray-700 mb-1">
                        <strong>Location:</strong> {selectedDeliveryStop.facility_name}
                      </p>
                      <p className="text-gray-700">
                        <strong>Order ID:</strong> {selectedDeliveryStop.id}
                      </p>
                    </div>
                  </div>
                  
                  <div className="photo-upload-section">
                    <label className="text-lg font-semibold block mb-2">Upload Delivery Photos</label>
                    <p className="text-sm text-gray-600 mb-4">Take photos to document the delivery location and condition</p>
                    
                    <div className="upload-area border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleDeliveryPhotoUpload}
                        className="hidden"
                        id="delivery-photo-upload"
                        capture="environment"
                      />
                      <label htmlFor="delivery-photo-upload" className="cursor-pointer">
                        <Camera className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium text-gray-600">Take Delivery Photos</p>
                        <p className="text-sm text-gray-500">Click to open camera or select files</p>
                      </label>
                    </div>
                    
                    {deliveryPhotos.length > 0 && (
                      <div className="photo-preview mt-4">
                        <label className="text-md font-medium block mb-2">Uploaded Photos ({deliveryPhotos.length})</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
                          {deliveryPhotos.map((photo, index) => (
                            <div key={index} className="relative">
                              <img 
                                src={photo} 
                                alt={`Delivery ${index + 1}`} 
                                className="w-full h-32 object-cover rounded-lg border"
                              />
                              <button
                                onClick={() => removePhoto(index)}
                                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 flex items-center justify-center"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="form-actions flex justify-end space-x-2 pt-4">
                    <button
                      onClick={() => {
                        setSelectedDeliveryStop(null);
                        setDeliveryPhotos([]);
                        setDeliveryGpsCoordinates(null);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          setLoading(true);
                          
                          // Call delivery completion API with photos and GPS
                          const response = await api.post(`/api/orders/${selectedDeliveryStop.id}/complete-delivery`, {
                            delivery_photos: deliveryPhotos,
                            delivery_status: 'delivered',
                            delivered_at: new Date().toISOString(),
                            delivered_by: user?.name || 'Driver',
                            delivery_gps: deliveryGpsCoordinates
                          });
                          
                          if (response.data.success) {
                            setSuccess(`Delivery completed successfully for ${selectedDeliveryStop.customer_name}!`);
                            setTimeout(() => setSuccess(''), 5000);
                            
                            // Reset and refresh
                            setSelectedDeliveryStop(null);
                            setDeliveryPhotos([]);
                            setDeliveryGpsCoordinates(null);
                            
                            // Refresh driver tasks
                            await loadDriverDeliveryStops();
                          } else {
                            setError('Failed to complete delivery');
                            setTimeout(() => setError(''), 5000);
                          }
                        } catch (err) {
                          console.error('Failed to complete delivery:', err);
                          setError('Failed to complete delivery: ' + (err.response?.data?.detail || err.message));
                          setTimeout(() => setError(''), 5000);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || deliveryPhotos.length === 0}
                      className={`px-4 py-2 rounded-md text-white ${
                        loading || deliveryPhotos.length === 0 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {loading ? 'Completing Delivery...' : 'Complete Delivery'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Order Delivery Photo Gallery Modal */}
        {showPhotoGallery ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b pb-4 p-6">
                <div className="flex items-center">
                  <Camera className="w-6 h-6 mr-2 text-green-600" />
                  <h2 className="text-xl font-bold">Order Delivery Photo Gallery</h2>
                </div>
              </div>
            
              <div className="gallery-content p-6 overflow-y-auto space-y-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
                {/* Search and Filter Bar */}
                <div className="gallery-filters bg-gray-50 p-4 rounded-lg">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label htmlFor="order-search" className="block text-sm font-medium mb-1">Search by Order ID or Customer</label>
                      <input
                        id="order-search"
                        placeholder="Enter order ID or customer name"
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="date-filter" className="block text-sm font-medium mb-1">Filter by Delivery Date</label>
                      <input
                        id="date-filter"
                        type="date"
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="status-filter" className="block text-sm font-medium mb-1">Delivery Status</label>
                      <select 
                        id="status-filter" 
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">All Orders</option>
                        <option value="delivered">Delivered with Photos</option>
                        <option value="signed">Client Signed</option>
                        <option value="pending">Pending Signature</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 flex items-center">
                        <Search className="w-4 h-4 mr-2" />
                        Search
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Statistics Summary */}
                {photoGalleryData && (
                  <div className="statistics-summary bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-700 mb-2">Photo Gallery Summary</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <span className="block text-2xl font-bold text-green-600">{photoGalleryData.totalOrders || 0}</span>
                        <span className="text-sm text-gray-600">Total Orders</span>
                      </div>
                      <div>
                        <span className="block text-2xl font-bold text-blue-600">{photoGalleryData.totalPhotos || 0}</span>
                        <span className="text-sm text-gray-600">Total Photos</span>
                      </div>
                      <div>
                        <span className="block text-2xl font-bold text-purple-600">{photoGalleryData.ordersWithPhotos || 0}</span>
                        <span className="text-sm text-gray-600">Orders with Photos</span>
                      </div>
                      <div>
                        <span className="block text-2xl font-bold text-orange-600">{photoGalleryData.clientSignatures || 0}</span>
                        <span className="text-sm text-gray-600">Client Signatures</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Photo Grid */}
                <div className="photo-grid">
                  <h3 className="text-lg font-semibold mb-4">Recent Order Deliveries</h3>
                  
                  {photoGalleryData && photoGalleryData.orders && photoGalleryData.orders.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {photoGalleryData.orders.map((order, index) => (
                        <div key={index} className="delivery-entry bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="order-header mb-3">
                            <h5 className="font-semibold text-gray-800">{order.customerName || order.guest_name}</h5>
                            <p className="text-sm text-gray-600">Order #{order.orderId || order.id}</p>
                            <p className="text-xs text-gray-500">
                              {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString() : 'No date'}
                            </p>
                          </div>
                          
                          {order.photos && order.photos.length > 0 ? (
                            <div className="photos-grid grid grid-cols-2 gap-2 mb-3">
                              {order.photos.slice(0, 4).map((photo, photoIndex) => (
                                <img
                                  key={photoIndex}
                                  src={photo}
                                  alt={`Delivery ${photoIndex + 1}`}
                                  className="w-full h-20 object-cover rounded cursor-pointer border hover:border-green-500 transition-colors"
                                  onClick={() => {
                                    // Create full-size image modal
                                    const modal = document.createElement('div');
                                    modal.className = 'fixed inset-0 z-[10000] bg-black bg-opacity-90 flex items-center justify-center p-4';
                                    modal.innerHTML = `
                                      <div class="relative max-w-4xl max-h-full">
                                        <img src="${photo}" class="max-w-full max-h-full object-contain rounded" />
                                        <button class="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors" onclick="this.parentElement.parentElement.remove()">×</button>
                                        <div class="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white p-2 rounded">
                                          ${order.customerName || 'Customer'} - Photo ${photoIndex + 1}
                                        </div>
                                      </div>
                                    `;
                                    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
                                    document.body.appendChild(modal);
                                  }}
                                />
                              ))}
                            </div>
                          ) : (
                            <div className="no-photos bg-gray-50 border-2 border-dashed border-gray-300 rounded p-4 text-center text-gray-500 mb-3">
                              <Camera className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm">No photos available</p>
                            </div>
                          )}
                          
                          <div className="order-footer">
                            <Badge className={`text-xs ${order.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {order.status || 'Unknown Status'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="delivery-entry bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg h-64 flex flex-col items-center justify-center">
                        <Camera className="w-16 h-16 text-gray-400 mb-4" />
                        <p className="text-gray-500 text-center mb-2 font-medium">No delivery photos yet</p>
                        <p className="text-sm text-gray-400 text-center">
                          Photos will appear here when drivers complete deliveries with photo documentation
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="gallery-actions flex justify-end space-x-2 p-6 border-t bg-gray-50">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowPhotoGallery(false)}
                >
                  Close
                </button>
                <button
                  onClick={loadPhotoGalleryData}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        ) : null}
        
        {/* Order Submission Review Modal */}
        {showSubmissionReview ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b pb-4 p-6">
                <div className="flex items-center">
                  <FileSignature className="w-6 h-6 mr-2 text-purple-600" />
                  <h2 className="text-xl font-bold">Review Order Deliveries & Signatures</h2>
                </div>
              </div>
            
              <div className="submission-content p-6 overflow-y-auto space-y-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
                {/* Filter Options */}
                <div className="filter-bar bg-purple-50 p-4 rounded-lg">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <select className="flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="">All Orders</option>
                      <option value="delivered">Delivered with Photos</option>
                      <option value="signed">Client Signed</option>
                      <option value="pending">Pending Client Signature</option>
                    </select>
                    <select className="flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                      <option value="">All Drivers</option>
                      {(availableDrivers || []).map(driver => (
                        <option key={driver.id} value={driver.id}>
                          {driver.name}
                        </option>
                      ))}
                    </select>
                    <input
                      placeholder="Search by order ID or customer"
                      className="flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                
                {/* Submissions List */}
                <div className="submissions-list space-y-4">
                  <h3 className="text-lg font-semibold">Recent Order Deliveries</h3>
                  
                  {/* Empty state */}
                  <div className="submission-empty bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <FileSignature className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-600 mb-2">No Order Submissions Yet</h4>
                    <p className="text-gray-500 mb-4">
                      Order submissions will appear here once drivers complete deliveries and facility staff collect signatures.
                    </p>
                    <div className="text-sm text-gray-400">
                      <span className="font-medium">Workflow:</span> Driver delivers → Photos taken → Facility staff → Client signs → Review here
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="review-actions flex justify-end space-x-2 p-6 border-t bg-gray-50">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowSubmissionReview(false)}
                >
                  Close
                </button>
                <button
                  onClick={loadSubmissionStats}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* PIN Edit Modal - Custom Implementation */}
        {showPinEditModal && editingPin && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => {
              console.log('🔍 PIN Edit backdrop clicked - closing modal');
              setShowPinEditModal(false);
              setEditingPin(null);
            }}></div>
            
            {/* Modal Content */}
            <div className="relative bg-white rounded-lg border-4 border-gray-800 shadow-2xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center mb-4">
                <Key className="w-5 h-5 mr-2 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Edit {editingPin?.type?.charAt(0).toUpperCase() + editingPin?.type?.slice(1)} PIN
                </h3>
              </div>
              
              <div className="space-y-4">
                <div className="bg-indigo-50 p-4 rounded border border-indigo-200">
                  <p className="text-sm text-indigo-700">
                    <strong>User:</strong> {editingPin.name}
                  </p>
                  <p className="text-sm text-indigo-700">
                    <strong>Current PIN:</strong> ****
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New PIN (4-6 digits)
                  </label>
                  <input
                    type="password"
                    placeholder="Enter new PIN"
                    value={editingPin.newPin}
                    onChange={(e) => setEditingPin(prev => ({...prev, newPin: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    maxLength="6"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a 4-6 digit PIN
                  </p>
                </div>
                
                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => {
                      console.log('🔍 PIN Edit Cancel button clicked');
                      setShowPinEditModal(false);
                      setEditingPin(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      console.log('🔍 PIN Edit Save button clicked');
                      updatePin(editingPin.type, editingPin.id, editingPin.newPin);
                    }}
                    disabled={!editingPin.newPin || editingPin.newPin.length < 4}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4 mr-2 inline" />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add User Modal - Custom Implementation */}
        {showAddUserModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => {
              console.log('🟢 Add User modal backdrop clicked - closing modal');
              setShowAddUserModal(false);
              setNewUserData({ name: '', pin: '', phone: '', role: 'driver' });
            }}></div>
            
            {/* Modal Content */}
            <div className="relative bg-white rounded-lg border-4 border-gray-800 shadow-2xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center mb-4">
                <Plus className="w-5 h-5 mr-2 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-800">Add New Driver</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Driver Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter driver name"
                    value={newUserData.name}
                    onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PIN *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter 4-6 digit PIN"
                    value={newUserData.pin}
                    onChange={(e) => setNewUserData({...newUserData, pin: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength="6"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter a 4-6 digit PIN
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Enter phone number"
                    value={newUserData.phone}
                    onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={newUserData.role}
                    onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="driver">Driver</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
                
                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => {
                      console.log('🟢 Add User Cancel button clicked');
                      setShowAddUserModal(false);
                      setNewUserData({ name: '', pin: '', phone: '', role: 'driver' });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      console.log('🟢 Add User Create button clicked');
                      createUser();
                    }}
                    disabled={!newUserData.name || !newUserData.pin || newUserData.pin.length < 4}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4 mr-2 inline" />
                    Create User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal - Custom Implementation */}
        {showDeleteConfirmModal && userToDelete && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => {
              console.log('🔵 Delete modal backdrop clicked - closing modal');
              setShowDeleteConfirmModal(false);
              setUserToDelete(null);
            }}></div>
            
            {/* Modal Content */}
            <div className="relative bg-white rounded-lg border-4 border-gray-800 shadow-2xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center mb-4">
                <Trash2 className="w-5 h-5 mr-2 text-red-600" />
                <h3 className="text-lg font-semibold text-red-600">Delete User</h3>
              </div>
              
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded border border-red-200">
                  <p className="text-sm text-red-700">
                    Are you sure you want to delete this user? This action cannot be undone.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded border">
                  <p className="text-sm text-gray-700">
                    <strong>Name:</strong> {userToDelete.name}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Role:</strong> {userToDelete.role || 'Driver'}
                  </p>
                  {userToDelete.phone && (
                    <p className="text-sm text-gray-700">
                      <strong>Phone:</strong> {userToDelete.phone}
                    </p>
                  )}
                </div>
                
                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => {
                      console.log('🔵 Delete modal Cancel button clicked');
                      setShowDeleteConfirmModal(false);
                      setUserToDelete(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      console.log('🔵 Delete modal Delete User button clicked');
                      deleteUser(userToDelete.id);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2 inline" />
                    Delete User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Task Edit Modal */}
        {showTaskEditModal && editingTask ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div 
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b pb-4 mb-6">
                <div className="flex items-center">
                  <Edit className="w-5 h-5 mr-2 text-blue-600" />
                  <h2 className="text-xl font-bold">Edit Task</h2>
                </div>
              </div>

              {/* Form Content */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Task Title *</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter task title"
                    value={editingTask.title || ''}
                    onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Description *</label>
                  <textarea
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder="Enter task description"
                    value={editingTask.description || ''}
                    onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={editingTask.priority || 'medium'}
                      onChange={(e) => setEditingTask({...editingTask, priority: e.target.value})}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={editingTask.status || 'pending'}
                      onChange={(e) => setEditingTask({...editingTask, status: e.target.value})}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Target Date</label>
                  <input
                    type="date"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={editingTask.target_date || ''}
                    onChange={(e) => setEditingTask({...editingTask, target_date: e.target.value})}
                  />
                </div>

                {/* Subtasks/Checklist Editor */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium">{tr('checklistItems')}</label>
                    <button
                      type="button"
                      className="px-3 py-1 text-sm text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                      onClick={() => {
                        const newSubtasks = [...(editingTask.subtasks || []), {
                          text: '',
                          completed: false,
                          required_photos: 0
                        }];
                        setEditingTask({...editingTask, subtasks: newSubtasks});
                      }}
                    >
                      <Plus className="w-3 h-3 inline mr-1" />
                      {tr('addStep')}
                    </button>
                  </div>
                  
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {(editingTask.subtasks || []).map((subtask, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded border">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={subtask.completed || false}
                            onChange={(e) => {
                              const newSubtasks = [...(editingTask.subtasks || [])];
                              newSubtasks[index] = {...newSubtasks[index], completed: e.target.checked};
                              setEditingTask({...editingTask, subtasks: newSubtasks});
                            }}
                          />
                          <div className="flex-1">
                            <input
                              type="text"
                              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              placeholder="Enter subtask description"
                              value={subtask.text || ''}
                              onChange={(e) => {
                                const newSubtasks = [...(editingTask.subtasks || [])];
                                newSubtasks[index] = {...newSubtasks[index], text: e.target.value};
                                setEditingTask({...editingTask, subtasks: newSubtasks});
                              }}
                            />
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-500">Photos required:</span>
                              <input
                                type="number"
                                className="w-16 p-1 border rounded text-xs"
                                min="0"
                                max="10"
                                value={subtask.required_photos || 0}
                                onChange={(e) => {
                                  const newSubtasks = [...(editingTask.subtasks || [])];
                                  newSubtasks[index] = {...newSubtasks[index], required_photos: parseInt(e.target.value) || 0};
                                  setEditingTask({...editingTask, subtasks: newSubtasks});
                                }}
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            className="p-1 text-red-600 border border-red-200 rounded hover:bg-red-50"
                            onClick={() => {
                              const newSubtasks = (editingTask.subtasks || []).filter((_, i) => i !== index);
                              setEditingTask({...editingTask, subtasks: newSubtasks});
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {(!editingTask.subtasks || editingTask.subtasks.length === 0) && (
                      <div className="text-center py-6 text-gray-500">
                        <p className="text-sm">{tr('noChecklistItems')}</p>
                        <p className="text-xs">{tr('clickAddStep')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-6 border-t mt-6">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setShowTaskEditModal(false);
                    setEditingTask(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  onClick={updateTask}
                  disabled={!editingTask.title || !editingTask.description}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Update Task
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Task Delete Confirmation Modal */}
        {showTaskDeleteModal && taskToDelete ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div 
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b pb-4 mb-4">
                <div className="flex items-center text-red-600">
                  <Trash2 className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-bold">Delete Task</h2>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded border border-red-200">
                  <p className="text-sm text-red-700">
                    Are you sure you want to delete this task? This action cannot be undone.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">
                    <strong>Title:</strong> {taskToDelete.title}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Description:</strong> {taskToDelete.description}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Status:</strong> {taskToDelete.status}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Priority:</strong> {taskToDelete.priority}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-6 mt-4">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setShowTaskDeleteModal(false);
                    setTaskToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                  onClick={() => deleteTask(taskToDelete.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Task
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Order Delete Confirmation Modal */}
        {showOrderDeleteModal && orderToDelete ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div 
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b pb-4 mb-4">
                <div className="flex items-center text-red-600">
                  <Trash2 className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-bold">Delete Order</h2>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded border border-red-200">
                  <p className="text-sm text-red-700">
                    Are you sure you want to delete this order? This action cannot be undone.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">
                    <strong>Customer:</strong> {orderToDelete.customer_name || orderToDelete.guest_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Room:</strong> {orderToDelete.room_number}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Facility:</strong> {orderToDelete.facility_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Order ID:</strong> {orderToDelete.id || orderToDelete.order_id}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Status:</strong> {orderToDelete.delivery_status}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-6 mt-4">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setShowOrderDeleteModal(false);
                    setOrderToDelete(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                  onClick={() => deleteOrder(orderToDelete.id || orderToDelete.order_id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Order
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Driver Task Edit Modal */}
        {showDriverTaskEditModal ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div 
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b pb-4 mb-6">
                <div className="flex items-center">
                  <Edit className="w-5 h-5 mr-2 text-blue-600" />
                  <h2 className="text-xl font-bold">Edit Driver Task</h2>
                </div>
              </div>
            
              {editingDriverTask && (
                <div className="driver-task-edit-form space-y-4">
                  <div className="customer-name-input">
                    <label htmlFor="driver-task-customer" className="block text-sm font-medium mb-1">Customer Name *</label>
                    <input
                      id="driver-task-customer"
                      type="text"
                      placeholder="Enter customer name"
                      value={editingDriverTask.customer_name || editingDriverTask.guest_name || ''}
                      onChange={(e) => setEditingDriverTask({
                        ...editingDriverTask, 
                        customer_name: e.target.value,
                        guest_name: e.target.value
                      })}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="room-input">
                      <label htmlFor="driver-task-room" className="block text-sm font-medium mb-1">Room Number</label>
                      <input
                        id="driver-task-room"
                        type="text"
                        placeholder="Room number"
                        value={editingDriverTask.room_number || ''}
                        onChange={(e) => setEditingDriverTask({...editingDriverTask, room_number: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="bag-count-input">
                      <label htmlFor="driver-task-bags" className="block text-sm font-medium mb-1">Bag Count</label>
                      <input
                        id="driver-task-bags"
                        type="number"
                        placeholder="Number of bags"
                        value={editingDriverTask.bag_count || ''}
                        onChange={(e) => setEditingDriverTask({...editingDriverTask, bag_count: parseInt(e.target.value) || 0})}
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="status-input">
                      <label htmlFor="driver-task-status" className="block text-sm font-medium mb-1">Delivery Status</label>
                      <select
                        id="driver-task-status"
                        value={editingDriverTask.delivery_status || 'pending_pickup'}
                        onChange={(e) => setEditingDriverTask({...editingDriverTask, delivery_status: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="pending_pickup">Pending Pickup</option>
                        <option value="picked_up">Picked Up</option>
                        <option value="in_transit">In Transit</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                    
                    <div className="priority-input">
                      <label htmlFor="driver-task-priority" className="block text-sm font-medium mb-1">Priority</label>
                      <select
                        id="driver-task-priority"
                        value={editingDriverTask.priority || 'normal'}
                        onChange={(e) => setEditingDriverTask({...editingDriverTask, priority: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="notes-input">
                    <label htmlFor="driver-task-notes" className="block text-sm font-medium mb-1">Delivery Notes</label>
                    <textarea
                      id="driver-task-notes"
                      placeholder="Enter delivery notes"
                      value={editingDriverTask.delivery_notes || ''}
                      onChange={(e) => setEditingDriverTask({...editingDriverTask, delivery_notes: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="2"
                    />
                  </div>
                  
                  <div className="special-instructions-input">
                    <label htmlFor="driver-task-instructions" className="block text-sm font-medium mb-1">Special Instructions</label>
                    <textarea
                      id="driver-task-instructions"
                      placeholder="Enter special instructions"
                      value={editingDriverTask.special_instructions || ''}
                      onChange={(e) => setEditingDriverTask({...editingDriverTask, special_instructions: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="2"
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                    <button
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        setShowDriverTaskEditModal(false);
                        setEditingDriverTask(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                      onClick={updateDriverTask}
                      disabled={!editingDriverTask.customer_name && !editingDriverTask.guest_name}
                    >
                      <Save className="w-4 h-4 mr-2 inline" />
                      Update Task
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {/* Driver Task Delete Confirmation Modal */}
        {showDriverTaskDeleteModal ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div 
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b pb-4 mb-6">
                <div className="flex items-center text-red-600">
                  <Trash2 className="w-5 h-5 mr-2" />
                  <h2 className="text-xl font-bold">Delete Driver Task</h2>
                </div>
              </div>
            
              {driverTaskToDelete && (
                <div className="driver-task-delete-content space-y-4">
                  <div className="warning-message bg-red-50 p-4 rounded border border-red-200">
                    <p className="text-sm text-red-700">
                      Are you sure you want to delete this driver-created task? This action cannot be undone.
                    </p>
                  </div>
                  
                  <div className="task-info bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">
                      <strong>Customer:</strong> {driverTaskToDelete.customer_name || driverTaskToDelete.guest_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Room:</strong> {driverTaskToDelete.room_number}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Driver:</strong> {driverTaskToDelete.driver_name || 'Unknown'}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Status:</strong> {driverTaskToDelete.delivery_status}
                    </p>
                    {driverTaskToDelete.bag_count && (
                      <p className="text-sm text-gray-600">
                        <strong>Bags:</strong> {driverTaskToDelete.bag_count}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                    <button
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        setShowDriverTaskDeleteModal(false);
                        setDriverTaskToDelete(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      onClick={() => deleteDriverTask(driverTaskToDelete.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2 inline" />
                      Delete Task
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {/* Pending Orders Management Modal */}
        {showPendingOrdersModal ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b pb-4 p-6">
                <div className="flex items-center mb-2">
                  <Package className="w-6 h-6 mr-2 text-orange-600" />
                  <h2 className="text-xl font-bold">Pending Orders Management</h2>
                </div>
                <p className="text-sm text-gray-600">
                  Manage all pending orders across facilities
                </p>
              </div>
              
              <div className="pending-orders-content p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 180px)' }}>
                {/* Header Actions */}
                <div className="flex justify-between items-center mb-4">
                  <div className="orders-summary">
                    <span className="text-sm font-medium text-gray-700">
                      {pendingOrders.length} pending orders found
                    </span>
                  </div>
                  <button
                    onClick={loadPendingOrders}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                {/* Orders List */}
                <div className="orders-list max-h-96 overflow-y-auto">
                  {pendingOrders.length > 0 ? (
                    <div className="space-y-3">
                      {pendingOrders.map((order) => (
                        <Card key={order.order_id} className="pending-order-item border-l-4 border-orange-500">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="order-details flex-1">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <h6 className="font-semibold text-sm text-orange-700">
                                      {order.customer_name || order.guest_name}
                                    </h6>
                                    <p className="text-xs text-gray-600">
                                      Room: {order.room_number}
                                    </p>
                                    <p className="text-xs font-medium text-blue-600">
                                      📍 Location: {order.facility_name || 'Unknown Facility'}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <Badge 
                                      variant="secondary"
                                      className="text-xs mb-1"
                                    >
                                      {order.delivery_status || 'pending'}
                                    </Badge>
                                    {order.priority && (
                                      <Badge 
                                        variant={order.priority === 'high' || order.priority === 'urgent' ? 'destructive' : 'outline'}
                                        className="text-xs ml-1"
                                      >
                                        {order.priority}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="order-meta text-xs text-gray-500 space-y-1">
                                  <p>Order ID: {order.order_id}</p>
                                  <p>Created: {new Date(order.created_at).toLocaleDateString()} at {new Date(order.created_at).toLocaleTimeString()}</p>
                                  {order.bag_count && (
                                    <p>Bags: {order.bag_count}</p>
                                  )}
                                  {order.special_instructions && (
                                    <p className="text-blue-600">Instructions: {order.special_instructions}</p>
                                  )}
                                  {order.driver_name && (
                                    <p>Assigned Driver: {order.driver_name}</p>
                                  )}
                                </div>
                              </div>
                              <div className="order-actions flex flex-col gap-2 ml-4">
                                <button
                                  onClick={() => {
                                    // For driver-created orders, use driver task edit
                                    if (order.driver_id) {
                                      openDriverTaskEditModal(order);
                                    } else {
                                      // For regular orders, use the new order edit modal
                                      openOrderEditModal(order);
                                    }
                                  }}
                                  className="px-2 py-1 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                                >
                                  <Edit className="w-3 h-3 mr-1 inline" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    setShowPendingOrdersModal(false);
                                    openOrderDeleteModal(order);
                                  }}
                                  className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                                >
                                  <Trash2 className="w-3 h-3 mr-1 inline" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="no-pending-orders text-center py-12">
                      <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No Pending Orders</h3>
                      <p className="text-gray-500">
                        All orders have been completed or delivered
                      </p>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="modal-footer mt-6 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-500">
                      Orders are grouped by facility. Use the location information to find specific orders.
                    </p>
                    <button
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowPendingOrdersModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Order Edit Modal */}
        {showOrderEditModal ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b pb-4 p-6">
                <div className="flex items-center">
                  <Edit className="w-5 h-5 mr-2 text-blue-600" />
                  <h2 className="text-xl font-bold">Edit Order</h2>
                </div>
              </div>
            
              {editingOrder && (
                <div className="order-edit-form p-6 space-y-4">
                  <div className="customer-name-input">
                    <label htmlFor="order-customer" className="block text-sm font-medium mb-1">Customer Name *</label>
                    <input
                      id="order-customer"
                      type="text"
                      placeholder="Enter customer name"
                      value={editingOrder.customer_name || editingOrder.guest_name || ''}
                      onChange={(e) => setEditingOrder({
                        ...editingOrder, 
                        customer_name: e.target.value,
                        guest_name: e.target.value
                      })}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="room-input">
                      <label htmlFor="order-room" className="block text-sm font-medium mb-1">Room Number</label>
                      <input
                        id="order-room"
                        type="text"
                        placeholder="Room number"
                        value={editingOrder.room_number || ''}
                        onChange={(e) => setEditingOrder({...editingOrder, room_number: e.target.value})}
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="bag-count-input">
                      <label htmlFor="order-bags" className="block text-sm font-medium mb-1">Bag Count</label>
                      <input
                        id="order-bags"
                        type="number"
                        placeholder="Number of bags"
                        value={editingOrder.bag_count || ''}
                        onChange={(e) => setEditingOrder({...editingOrder, bag_count: parseInt(e.target.value) || 0})}
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div className="order-type-input">
                    <label htmlFor="order-type" className="block text-sm font-medium mb-1">Order Type</label>
                    <select
                      id="order-type"
                      value={editingOrder.order_type || 'laundry'}
                      onChange={(e) => setEditingOrder({...editingOrder, order_type: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="laundry">Laundry</option>
                      <option value="dry_cleaning">Dry Cleaning</option>
                      <option value="linen">Linen</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="items-description-input">
                    <label htmlFor="order-items" className="block text-sm font-medium mb-1">Items Description</label>
                    <textarea
                      id="order-items"
                      placeholder="Describe the items"
                      value={editingOrder.items_description || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, items_description: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                  
                  <div className="special-instructions-input">
                    <label htmlFor="order-instructions" className="block text-sm font-medium mb-1">Special Instructions</label>
                    <textarea
                      id="order-instructions"
                      placeholder="Any special instructions"
                      value={editingOrder.special_instructions || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, special_instructions: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                    />
                  </div>
                  
                  <div className="priority-input">
                    <label htmlFor="order-priority" className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      id="order-priority"
                      value={editingOrder.priority || 'normal'}
                      onChange={(e) => setEditingOrder({...editingOrder, priority: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                    <button
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                      onClick={() => {
                        setShowOrderEditModal(false);
                        setEditingOrder(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                      onClick={updateOrder}
                      disabled={loading || !editingOrder.customer_name}
                    >
                      <Save className="w-4 h-4 mr-2 inline" />
                      Update Order
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {/* Add Facility Modal */}
        {showAddFacilityModal ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b pb-4 p-6">
                <div className="flex items-center">
                  <Plus className="w-5 h-5 mr-2 text-purple-600" />
                  <h2 className="text-xl font-bold">Add New Facility</h2>
                </div>
              </div>
            
              <div className="add-facility-form p-6 space-y-4">
                <div className="name-input">
                  <label htmlFor="facility-name" className="block text-sm font-medium mb-1">Facility Name *</label>
                  <input
                    id="facility-name"
                    type="text"
                    placeholder="Enter facility name"
                    value={newFacilityData.name}
                    onChange={(e) => setNewFacilityData({...newFacilityData, name: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                <div className="address-input">
                  <label htmlFor="facility-address" className="block text-sm font-medium mb-1">Address</label>
                  <textarea
                    id="facility-address"
                    placeholder="Enter facility address"
                    value={newFacilityData.address}
                    onChange={(e) => setNewFacilityData({...newFacilityData, address: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="2"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="pin-input">
                    <label htmlFor="facility-pin" className="block text-sm font-medium mb-1">PIN *</label>
                    <input
                      id="facility-pin"
                      type="text"
                      placeholder="Enter 4-6 digit PIN"
                      value={newFacilityData.pin}
                      onChange={(e) => setNewFacilityData({...newFacilityData, pin: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                      maxLength="6"
                    />
                  </div>
                  
                  <div className="phone-input">
                    <label htmlFor="facility-phone" className="block text-sm font-medium mb-1">Phone</label>
                    <input
                      id="facility-phone"
                      type="text"
                      placeholder="Enter phone number"
                      value={newFacilityData.phone}
                      onChange={(e) => setNewFacilityData({...newFacilityData, phone: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                
                <div className="contact-input">
                  <label htmlFor="facility-contact" className="block text-sm font-medium mb-1">Contact Person</label>
                  <input
                    id="facility-contact"
                    type="text"
                    placeholder="Enter contact person name"
                    value={newFacilityData.contact_person}
                    onChange={(e) => setNewFacilityData({...newFacilityData, contact_person: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                <div className="modal-actions flex justify-end space-x-3 pt-6 border-t mt-6">
                  <button
                    onClick={() => {
                      setShowAddFacilityModal(false);
                      setNewFacilityData({ name: '', address: '', pin: '', phone: '', contact_person: '' });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createFacility}
                    disabled={!newFacilityData.name || !newFacilityData.pin || newFacilityData.pin.length < 4}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded disabled:bg-gray-400 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Facility
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Delete Facility Confirmation Modal - Custom Implementation */}
        {showDeleteFacilityModal && facilityToDelete && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => {
              console.log('🔵 Delete facility modal backdrop clicked - closing modal');
              setShowDeleteFacilityModal(false);
              setFacilityToDelete(null);
            }}></div>
            
            {/* Modal Content - Fixed sizing and positioning */}
            <div className="relative bg-white rounded-lg border-4 border-gray-800 shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center mb-4">
                <Trash2 className="w-5 h-5 mr-2 text-red-600" />
                <h3 className="text-lg font-semibold text-red-600">Delete Facility</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="bg-red-50 p-4 rounded border border-red-200">
                  <p className="text-sm text-red-700">
                    Are you sure you want to delete this facility? This action will deactivate the facility and cannot be undone.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded border">
                  <p className="text-sm text-gray-700">
                    <strong>Name:</strong> {facilityToDelete.name}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>PIN:</strong> {facilityToDelete.pin}
                  </p>
                </div>
              </div>
              
              {/* Fixed button positioning */}
              <div className="flex justify-between gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    console.log('🔵 Delete facility Cancel button clicked');
                    setShowDeleteFacilityModal(false);
                    setFacilityToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    console.log('🔵 Delete facility Delete button clicked');
                    deleteFacility(facilityToDelete.id);
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2 inline" />
                  Delete Facility
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Task Details Modal */}
        {showTaskDetailsModal && selectedTask ? (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div 
              className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="border-b pb-4 mb-6">
                <div className="flex items-center">
                  <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
                  <h2 className="text-xl font-bold">Task Details</h2>
                </div>
              </div>

              {/* Task Header */}
              <div className="bg-green-50 p-4 rounded-lg mb-6">
                <h3 className="text-xl font-bold text-green-800 mb-2">{String(selectedTask.title || 'Untitled Task')}</h3>
                <p className="text-gray-700 mb-3">{String(selectedTask.description || 'No description available')}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-xs text-gray-500 block">Status</span>
                    <span className={`inline-block mt-1 px-2 py-1 text-xs rounded ${
                      selectedTask.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {String(selectedTask.status || 'pending')}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">Priority</span>
                    <span className={`inline-block mt-1 px-2 py-1 text-xs rounded ${
                      selectedTask.priority === 'high' || selectedTask.priority === 'urgent' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {String(selectedTask.priority || 'normal')}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">Created</span>
                    <p className="text-sm font-medium">
                      {selectedTask.created_at ? new Date(selectedTask.created_at).toLocaleDateString() : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 block">Target Date</span>
                    <p className="text-sm font-medium">
                      {selectedTask.target_date ? new Date(selectedTask.target_date).toLocaleDateString() : 'Not set'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Task Details */}
              <div className="space-y-4 mb-6">
                {selectedTask.assigned_to && (
                  <div>
                    <span className="text-sm font-semibold text-gray-700 block">Assigned To</span>
                    <p className="text-gray-600">{String(selectedTask.assigned_to)}</p>
                  </div>
                )}
                
                {selectedTask.delivery_location && (
                  <div>
                    <span className="text-sm font-semibold text-gray-700 block">Delivery Location</span>
                    <p className="text-gray-600">{String(selectedTask.delivery_location)}</p>
                  </div>
                )}
                
                {selectedTask.pickup_location && (
                  <div>
                    <span className="text-sm font-semibold text-gray-700 block">Pickup Location</span>
                    <p className="text-gray-600">{String(selectedTask.pickup_location)}</p>
                  </div>
                )}
                
                {selectedTask.bag_details && (
                  <div>
                    <span className="text-sm font-semibold text-gray-700 block">Bag Details</span>
                    <p className="text-gray-600">{String(selectedTask.bag_details)}</p>
                  </div>
                )}
              </div>

              {/* Task Subtasks/Checklist */}
              {selectedTask.subtasks && Array.isArray(selectedTask.subtasks) && selectedTask.subtasks.length > 0 && (
                <div className="mb-6">
                  <span className="text-sm font-semibold text-gray-700 mb-3 block">{tr('checklistItems')}</span>
                  <div className="space-y-2">
                    {selectedTask.subtasks.map((subtask, index) => (
                      <div key={index} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          subtask.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                        }`}>
                          {subtask.completed && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className={`flex-1 ${subtask.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                          {String(subtask.text || 'Subtask')}
                        </span>
                        {(subtask.required_photos && subtask.required_photos > 0) && (
                          <span className="text-xs px-2 py-1 bg-gray-100 rounded border">
                            <Camera className="w-3 h-3 inline mr-1" />
                            {subtask.required_photos} {subtask.required_photos > 1 ? tr('photos') : tr('photo')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Task Progress */}
              {selectedTask.subtasks && Array.isArray(selectedTask.subtasks) && selectedTask.subtasks.length > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700">Progress</span>
                    <span className="text-sm text-gray-600">
                      {selectedTask.subtasks.filter(s => s && s.completed).length} of {selectedTask.subtasks.length} completed
                    </span>
                  </div>
                  <div className="bg-gray-200 rounded h-3">
                    <div 
                      className="bg-green-500 h-3 rounded transition-all"
                      style={{
                        width: `${selectedTask.subtasks.length > 0 ? (selectedTask.subtasks.filter(s => s && s.completed).length / selectedTask.subtasks.length) * 100 : 0}%`
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Modal Actions */}
              <div className="flex justify-between pt-4 border-t border-gray-200">
                <button
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setShowTaskDetailsModal(false);
                    setSelectedTask(null);
                  }}
                >
                  Close
                </button>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 text-blue-600 border border-blue-200 rounded hover:bg-blue-50 flex items-center"
                    onClick={() => {
                      setShowTaskDetailsModal(false);
                      openTaskEditModal(selectedTask);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Task
                  </button>
                  <button
                    className="px-4 py-2 text-red-600 border border-red-200 rounded hover:bg-red-50 flex items-center"
                    onClick={() => {
                      setShowTaskDetailsModal(false);
                      openTaskDeleteModal(selectedTask);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Task
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Add New Customer Modal */}
        {showAddCustomerModal && (
          <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Add New Customer</h3>
                  <button
                    onClick={() => {
                      setShowAddCustomerModal(false);
                      setNewCustomerData({
                        name: '',
                        phone: '',
                        email: '',
                        address: '',
                        facility_id: '',
                        notes: ''
                      });
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Customer Name - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name *
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter customer name"
                      value={newCustomerData.name}
                      onChange={(e) => setNewCustomerData({...newCustomerData, name: e.target.value})}
                      className="w-full"
                      required
                    />
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      placeholder="Enter phone number"
                      value={newCustomerData.phone}
                      onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})}
                      className="w-full"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={newCustomerData.email}
                      onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})}
                      className="w-full"
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter address"
                      value={newCustomerData.address}
                      onChange={(e) => setNewCustomerData({...newCustomerData, address: e.target.value})}
                      className="w-full"
                    />
                  </div>

                  {/* Facility Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Facility (Optional)
                    </label>
                    <select
                      value={newCustomerData.facility_id}
                      onChange={(e) => setNewCustomerData({...newCustomerData, facility_id: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select facility (auto-detect from address)</option>
                      {hotels.map((hotel) => (
                        <option key={hotel.id} value={hotel.id}>
                          {hotel.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      placeholder="Any additional notes..."
                      value={newCustomerData.notes}
                      onChange={(e) => setNewCustomerData({...newCustomerData, notes: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={3}
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                  <Button
                    onClick={() => {
                      setShowAddCustomerModal(false);
                      setNewCustomerData({
                        name: '',
                        phone: '',
                        email: '',
                        address: '',
                        facility_id: '',
                        notes: ''
                      });
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={createCustomer}
                    disabled={loading || !newCustomerData.name.trim()}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {loading ? 'Adding...' : 'Add Customer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;